import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from 'react';
import axios from 'axios';
// Google Maps eliminado - usando lista simple
import './styles/metallic-effects.css';
import './styles/premium-theme.css';
import { 
  MapPin, 
  User, 
  Star, 
  Search, 
  DollarSign, 
  Clock, 
  Scissors, 
  Menu,
  LogIn,
  UserPlus,
  Filter,
  Calendar,
  Bell,
  CheckCircle,
  XCircle,
  MapIcon,
  Settings,
  LogOut,
  Plus,
  Upload,
  Heart,
  MessageCircle,
  Phone,
  Mail,
  Users,
  TrendingUp,
  History,
  Edit3,
  Save,
  Navigation,
  Zap,
  Shield,
  Award,
  Gift,
  Target,
  AlertCircle,
  Info,
  Eye,
  Trash2,
  Reply,
  X,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Button } from './components/ui/button.jsx';
import { Input } from './components/ui/input.jsx';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog.jsx';
import { Badge } from './components/ui/badge.jsx';
import { Avatar, AvatarImage, AvatarFallback } from './components/ui/avatar.jsx';
import { Slider } from './components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './components/ui/collapsible.jsx';
import { Calendar as CalendarComponent } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Textarea } from './components/ui/textarea';
import { Alert, AlertDescription } from './components/ui/alert.jsx';
import { LoadingSpinner } from './components/ui/loading-spinner';
import { Progress } from './components/ui/progress';
import { Skeleton } from './components/ui/skeleton';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './components/ui/dropdown-menu';
import { Checkbox } from './components/ui/checkbox.jsx';
import { LocationTracker } from './components/advanced/LocationTracker';
import { BarbershopManagement } from './components/barber/BarbershopManagement';
import { ProfileManagement } from './components/user/ProfileManagement';
import { AppointmentManager } from './components/barber/AppointmentManager';
import { RealTimeChat } from './components/chat/RealTimeChat';
import { ReviewModal } from './components/app/ReviewModal';
import { BookingModal } from './components/app/BookingModal';
import { ErrorBoundary } from './components/app/ErrorBoundary';
const loadClientInterface = () => import('./components/app/ClientInterface');
const ClientInterface = React.lazy(() =>
  loadClientInterface().then((mod) => ({ default: mod.ClientInterface }))
);
import { LoginForm } from './components/app/LoginForm.jsx';
const loadBarberInterface = () => import('./components/app/BarberInterface');
const BarberInterface = React.lazy(() =>
  loadBarberInterface().then((mod) => ({ default: mod.BarberInterface }))
);
const loadAdminInterface = () => import('./components/app/AdminInterface');
const AdminInterface = React.lazy(() =>
  loadAdminInterface().then((mod) => ({ default: mod.AdminInterface }))
);
import { BarbershopCard } from './components/app/BarbershopCard';
import { useToast } from './hooks/useToast.js';
import { useGooglePlaces } from './hooks/useGooglePlaces';
import { getUserAvatarUrl, getBarbershopAvatarUrl } from './lib/avatars';
import { formatClp, formatDateWithTZ, formatTimeWithTZ, formatDateTimeWithTZ, parseAsUTC } from './lib/utils';
import { LanguageProvider, useLanguage } from './hooks/useLanguage';
import { useCalendarDownload } from './hooks/useCalendarDownload';
import { useClientDataLoaders } from './hooks/useClientDataLoaders';
import { useNotificationsStream } from './hooks/useNotificationsStream';
import { useClientPolling } from './hooks/useClientPolling';
import { useBarberPolling } from './hooks/useBarberPolling';
import { useChatPolling } from './hooks/useChatPolling';
import { useInboxData } from './hooks/useInboxData';
import { useAdminLoaders } from './hooks/useAdminLoaders';
import { useQuickCutTimers } from './hooks/useQuickCutTimers';
import { useQuickCutFlow } from './hooks/useQuickCutFlow';
import { useAdminActions } from './hooks/useAdminActions';
import { useQuickCutActions } from './hooks/useQuickCutActions';
import { useAdminExports } from './hooks/useAdminExports';
import { useQuickCutProgress } from './hooks/useQuickCutProgress';
import './App.css';

// --- CAMBIO REALIZADO AQUÍ ---
// Apuntamos directamente a tu backend en Vercel
const BACKEND_URL = "https://backend-eight-ruby-15.vercel.app";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  || (typeof window !== 'undefined' ? window.localStorage?.getItem('google_maps_api_key') : '')
  || '';

axios.defaults.timeout = 15000;
axios.defaults.headers.common['Content-Type'] = 'application/json';


function App() {
  // Suprimir ResizeObserver errors (son benignos)
  React.useEffect(() => {
    const originalError = window.console.error;
    const originalWarn = window.console.warn;
    window.console.error = (...args) => {
      const message = typeof args[0] === 'string' ? args[0] : (args[0]?.message || '');
      if (message.includes('ResizeObserver')) return;
      if (message.includes('DialogContent') && message.includes('DialogTitle')) {
        originalError('Aviso: algunos diálogos no tenían título accesible. Se agregó uno oculto por defecto.');
        return;
      }
      if (message.includes('WebSocket connection') && message.includes('/ws/notifications')) {
        originalError('No se pudo conectar a notificaciones en tiempo real. Se usará la conexión alternativa.');
        return;
      }
      if (message.includes('ApiNotActivatedMapError')) {
        originalError('Google Maps no está activado para esta API key. Revisa la consola de Google y el dominio permitido.');
        return;
      }
      if (message.includes('ProjectDeniedMapError')) {
        window.__gmapsError = 'PROJECT_DENIED';
        window.dispatchEvent(new CustomEvent('gmaps-error', { detail: 'PROJECT_DENIED' }));
        originalError('Google Maps rechazó la API key. Revisa restricciones y billing en Google Cloud.');
        return;
      }
      originalError(...args);
    };
    window.console.warn = (...args) => {
      const message = typeof args[0] === 'string' ? args[0] : (args[0]?.message || '');
      if (message.includes('DialogContent') && message.includes('DialogTitle')) {
        originalWarn('Aviso: algunos diálogos no tenían título accesible. Se agregó uno oculto por defecto.');
        return;
      }
      if (message.includes('Missing `Description`') || message.includes('aria-describedby')) {
        originalWarn('Aviso: algunos diálogos no tenían descripción accesible. Se agregó una por defecto.');
        return;
      }
      if (message.includes('Maps JavaScript API has been loaded directly without loading=async')) {
        originalWarn('Aviso: Google Maps se cargó sin "loading=async". No afecta el funcionamiento.');
        return;
      }
      if (message.includes('Marker is deprecated')) {
        originalWarn('Aviso: Google recomienda usar AdvancedMarkerElement. No afecta el mapa actual.');
        return;
      }
      if (message.includes('places.Autocomplete is not available to new customers')) {
        originalWarn('Aviso: Google recomienda migrar Autocomplete. Seguimos usando la versión actual por compatibilidad.');
        return;
      }
      if (message.includes('Node cannot be found in the current page')) return;
      if (message.includes('Blocked aria-hidden') && message.includes('descendant retained focus')) return;
      originalWarn(...args);
    };
    return () => {
      window.console.error = originalError;
      window.console.warn = originalWarn;
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = idle(() => {
      loadClientInterface();
      loadBarberInterface();
      loadAdminInterface();
    });
    return () => cancel(handle);
  }, []);

  return (
    <LanguageProvider>
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    </LanguageProvider>
  );
}
function AppContent() {
  const { t } = useLanguage();
  
  // Initialize toast system
  const { toast, toasts } = useToast();

  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeTab, setActiveTab] = useState('map');
  const [isMobile, setIsMobile] = useState(false);
  const [map, setMap] = useState(null);
  const [barbershops, setBarbershops] = useState([]);
  const [rankingBarbershops, setRankingBarbershops] = useState([]);
  const [showTopRanking, setShowTopRanking] = useState(false);
  const [selectedBarbershop, setSelectedBarbershop] = useState(null);
  const [selectedBarbershopEta, setSelectedBarbershopEta] = useState(null);
  const [selectedUpcomingBooking, setSelectedUpcomingBooking] = useState(null);
  const [selectedPastBooking, setSelectedPastBooking] = useState(null);
  const [userNotifications, setUserNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showClientReviews, setShowClientReviews] = useState(false);
  const [barbershopReviews, setBarbershopReviews] = useState([]);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [calendarOptions, setCalendarOptions] = useState(null);
  const [confirmDialogData, setConfirmDialogData] = useState({
    title: 'Confirmar acción',
    description: '',
    confirmText: 'Confirmar',
    variant: 'default',
    onConfirm: null,
    onCancel: null
  });
  const [historyFilter, setHistoryFilter] = useState('monthly');
  const [historyStartDate, setHistoryStartDate] = useState('2026-01-01');
  const [historyEndDate, setHistoryEndDate] = useState('2030-12-31');
  const [historyFiltersOpen, setHistoryFiltersOpen] = useState(false);
  const [loadingBarbershops, setLoadingBarbershops] = useState(false);
  const [loadingBarberHistory, setLoadingBarberHistory] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [hasRequestNotification, setHasRequestNotification] = useState(false);
  const [lastPendingCount, setLastPendingCount] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [adminTab, setAdminTab] = useState('barbershops');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminReviews, setAdminReviews] = useState([]);
  const [adminReviewsLoading, setAdminReviewsLoading] = useState(false);
  const [adminBarbershops, setAdminBarbershops] = useState([]);
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [debouncedAdminUserSearch, setDebouncedAdminUserSearch] = useState('');
  const [adminPasswordDrafts, setAdminPasswordDrafts] = useState({});
  const [adminEventType, setAdminEventType] = useState('booking');
  const [adminEventId, setAdminEventId] = useState('');
  const [adminUserTypeFilter, setAdminUserTypeFilter] = useState('all');
  const [adminUserBlockedFilter, setAdminUserBlockedFilter] = useState('all');
  const [adminUserStats, setAdminUserStats] = useState({});
  const [adminBlockDrafts, setAdminBlockDrafts] = useState({});
  const [adminReviewFilters, setAdminReviewFilters] = useState({
    startDate: '',
    endDate: '',
    minRating: '',
    maxRating: '',
    minPrice: '',
    maxPrice: '',
    barberId: 'all',
    barbershopId: 'all',
    flaggedOnly: false
  });
  const [debouncedAdminReviewFilters, setDebouncedAdminReviewFilters] = useState({
    startDate: '',
    endDate: '',
    minRating: '',
    maxRating: '',
    minPrice: '',
    maxPrice: '',
    barberId: 'all',
    barbershopId: 'all',
    flaggedOnly: false
  });
  const [adminReviewNotes, setAdminReviewNotes] = useState({});
  const [adminVisitStats, setAdminVisitStats] = useState(null);
  const [adminVisitEvents, setAdminVisitEvents] = useState([]);
  const [adminVisitsStart, setAdminVisitsStart] = useState('');
  const [adminVisitsEnd, setAdminVisitsEnd] = useState('');
  const [adminMetrics, setAdminMetrics] = useState(null);
  const [adminMetricsStart, setAdminMetricsStart] = useState('');
  const [adminMetricsEnd, setAdminMetricsEnd] = useState('');
  const [adminAuditLogs, setAdminAuditLogs] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState({ users: [], barbershops: [], reviews: [] });
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceBanner, setMaintenanceBanner] = useState({ enabled: false, message: '' });
  const [adminBarbershopSearch, setAdminBarbershopSearch] = useState('');
  const [debouncedAdminBarbershopSearch, setDebouncedAdminBarbershopSearch] = useState('');
  const [adminBarbershopStatus, setAdminBarbershopStatus] = useState('all');
  const [adminBarbershopRatingMin, setAdminBarbershopRatingMin] = useState('');
  const [adminEditBarbershop, setAdminEditBarbershop] = useState(null);
  const [adminEditBarbershopDraft, setAdminEditBarbershopDraft] = useState({
    name: '',
    address: '',
    city: '',
    comuna: '',
    region: '',
    street: '',
    street_number: '',
    postal_code: '',
    lat: null,
    lng: null,
    phone: '',
    description: '',
    available: true,
    services: [],
    profile_image: ''
  });
  const {
    isLoaded: adminPlacesLoaded,
    inputRef: adminAddressRef,
    setupPlaceChangedListener: setupAdminPlaceChangedListener,
    initializeAutocomplete: initializeAdminAutocomplete
  } = useGooglePlaces(GOOGLE_MAPS_API_KEY);
  const [adminSelectedPlace, setAdminSelectedPlace] = useState(null);
  const [adminImageUploading, setAdminImageUploading] = useState(false);
  const [adminMessageBarbershop, setAdminMessageBarbershop] = useState(null);
  const [adminMessageDraft, setAdminMessageDraft] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerInviteBarbershopId, setRegisterInviteBarbershopId] = useState(null);
  const [showInbox, setShowInbox] = useState(false);
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [archivedInboxKeys, setArchivedInboxKeys] = useState([]);
  const [deletedInboxKeys, setDeletedInboxKeys] = useState([]);
  const [inboxActionTarget, setInboxActionTarget] = useState(null);
  const [showInboxActionMenu, setShowInboxActionMenu] = useState(false);
  const inboxPressTimerRef = useRef(null);
  const inboxLongPressTriggeredRef = useRef(false);
  const [userLocation, setUserLocation] = useState({ lat: -33.4489, lng: -70.6693 });
  const [priceLimit, setPriceLimit] = useState([15000]); // Empezar en $15.000 CLP
  const [priceInputFocused, setPriceInputFocused] = useState(false);
  const [priceInput, setPriceInput] = useState(formatClp(15000));
  const [selectedService, setSelectedService] = useState('');
  // Quick cut states for real-time Uber-style matching
  const [quickCutStatus, setQuickCutStatus] = useState('idle'); // 'idle', 'searching', 'matched', 'pending', 'completed'
  const [matchedBarber, setMatchedBarber] = useState(null);
  const [quickCutTimer, setQuickCutTimer] = useState(0);
  const [searchTimeLeft, setSearchTimeLeft] = useState(900); // 15 minutes = 900 seconds
  const [showNoBarbersModal, setShowNoBarbersModal] = useState(false);
  const [quickCutSearchAttempts, setQuickCutSearchAttempts] = useState(0);
  const [quickCutWindowStart, setQuickCutWindowStart] = useState(null);
  const [currentQuickCutRequestId, setCurrentQuickCutRequestId] = useState(null);
  const [countdownTick, setCountdownTick] = useState(0);
  const [mobileStage, setMobileStage] = useState('idle');
  const [mobileRequest, setMobileRequest] = useState(null);
  const [mobileAcceptedUntil, setMobileAcceptedUntil] = useState(null);
  const quickCutTimerRef = useRef(null);
  const quickCutMatchRef = useRef(null);
  const currentQuickCutRequestRef = useRef(null);

  function getQuickCutResponseWindowSeconds(preferred) {
    if (preferred === '30min') return 30 * 60;
    if (preferred === '1hour') return 60 * 60;
    if (preferred === '2hours') return 120 * 60;
    return 15 * 60;
  }

  function formatCalendarDate(dateValue) {
    return dateValue.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
  }

  function buildCalendarDetails(booking) {
    const startDate = new Date(booking?.date || booking?.created_at);
    const durationMinutes = Number(booking?.duration || booking?.service_duration || 30);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
    const barbershopName = booking?.barbershop?.name || booking?.barbershop_name || 'Barbería';
    const location =
      booking?.barbershop?.address ||
      booking?.barbershop_address ||
      barbershopName;
    const title = booking?.service || 'Corte de pelo';
    const description = `Reserva en ${barbershopName}`;
    return {
      title,
      description,
      location,
      startDate,
      endDate
    };
  }

  function buildGoogleCalendarUrl(details) {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: details.title,
      dates: `${formatCalendarDate(details.startDate)}/${formatCalendarDate(details.endDate)}`,
      details: details.description,
      location: details.location
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  function buildGoogleCalendarAppUrl(details) {
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: details.title,
      dates: `${formatCalendarDate(details.startDate)}/${formatCalendarDate(details.endDate)}`,
      details: details.description,
      location: details.location
    });
    return `googlecalendar://calendar/render?${params.toString()}`;
  }

  function buildOutlookCalendarUrl(details) {
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      startdt: details.startDate.toISOString(),
      enddt: details.endDate.toISOString(),
      subject: details.title,
      body: details.description,
      location: details.location
    });
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  }

  function buildOutlookCalendarAppUrl(details) {
    const params = new URLSearchParams({
      subject: details.title,
      body: details.description,
      startdt: details.startDate.toISOString(),
      enddt: details.endDate.toISOString(),
      location: details.location
    });
    return `ms-outlook://compose?${params.toString()}`;
  }

  function buildWebcalUrl(calendarUrl) {
    const url = getCalendarUrlWithToken(calendarUrl);
    if (!url) return null;
    return url.replace(/^https?:\/\//, 'webcal://');
  }

  function openCalendarWithFallback(appUrl, webUrl) {
    if (typeof window === 'undefined') return;
    const fallbackUrl = webUrl || appUrl;
    if (!appUrl) {
      if (fallbackUrl) window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    let resolved = false;
    const handleVisibility = () => {
      if (document.hidden) {
        resolved = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility, { once: true });
    const fallbackTimer = setTimeout(() => {
      if (!resolved && fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
    }, 1200);
    window.location.href = appUrl;
    setTimeout(() => {
      clearTimeout(fallbackTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
    }, 2500);
  }

  function openCalendarOptionsForBooking(booking, calendarUrl) {
    if (!calendarUrl) return;
    const details = buildCalendarDetails(booking);
    setCalendarOptions({
      title: details.title,
      calendarUrl,
      googleUrl: buildGoogleCalendarUrl(details),
      googleAppUrl: buildGoogleCalendarAppUrl(details),
      outlookUrl: buildOutlookCalendarUrl(details),
      outlookAppUrl: buildOutlookCalendarAppUrl(details),
      webcalUrl: buildWebcalUrl(calendarUrl)
    });
    setShowCalendarOptions(true);
  }

  function getBarbershopFromBooking(booking) {
    if (!booking) return null;
    if (booking.barbershop) return booking.barbershop;
    if (booking.barbershop_id) {
      const found = barbershops.find((shop) => shop.id === booking.barbershop_id);
      if (found) return found;
    }
    if (booking.barbershop_name || booking.barbershop_address) {
      return {
        id: booking.barbershop_id || booking.id,
        name: booking.barbershop_name || 'Barbería',
        address: booking.barbershop_address
      };
    }
    return null;
  }

  function openReviewForBooking(booking) {
    if (!booking) return;
    setCompletedQuickCut(booking);
    setReviewData({ rating: 0, comment: '', images: [] });
    setShowReviewModal(true);
  }
  const [maxDistance, setMaxDistance] = useState([5]); // km
  const [serviceLocation, setServiceLocation] = useState('local'); // 'local' or 'home'
  const [preferredTime, setPreferredTime] = useState('asap'); // 'asap', '30min', '1hour', '2hours'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationResendLoading, setVerificationResendLoading] = useState(false);
  const [verificationResendMessage, setVerificationResendMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetPasswordDraft, setResetPasswordDraft] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');

  // Helper para convertir cualquier error a string
  const setErrorSafe = (err) => {
    if (typeof err === 'string') {
      setError(err);
    } else if (Array.isArray(err)) {
      setError(err.map(e => e.msg || e.message || String(e)).join(', '));
    } else if (err && typeof err === 'object') {
      setError(err.detail || err.message || err.msg || JSON.stringify(err));
    } else {
      setError(String(err));
    }
  };
  const [success, setSuccess] = useState('');
  
  // Advanced features states
  const [realTimeTracking, setRealTimeTracking] = useState(false);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [quickCutProgress, setQuickCutProgress] = useState(0);
  const [barberRating, setBarberRating] = useState(null);
  const [isLocationTracking, setIsLocationTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [clientLevel, setClientLevel] = useState('Bronze');
  const [totalSpent, setTotalSpent] = useState(0);
  const [totalCuts, setTotalCuts] = useState(0);
  const [achievements, setAchievements] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  
  // Enterprise features
  const [analyticsData, setAnalyticsData] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [realTimeUpdates, setRealTimeUpdates] = useState(true);
  const [smartRecommendations, setSmartRecommendations] = useState([]);
  const [dynamicPricing, setDynamicPricing] = useState(false);

  // Barber specific states
  const [appointments, setAppointments] = useState([]);
  const [quickCutRequests, setQuickCutRequests] = useState([]);
  const [clientAcceptedQuickCuts, setClientAcceptedQuickCuts] = useState([]); // Quick cuts aceptados pendientes del cliente
  const [barberAcceptedQuickCuts, setBarberAcceptedQuickCuts] = useState([]); // Quick cuts aceptados pendientes del barbero
  const [barberCompletedQuickCuts, setBarberCompletedQuickCuts] = useState([]); // Quick cuts completados del barbero
  const [barberAdminChats, setBarberAdminChats] = useState([]);
  const [clientReviews, setClientReviews] = useState([]); // Todas las reseñas del cliente
  const [editingReview, setEditingReview] = useState(null); // Reseña que se está editando
  const [editReviewData, setEditReviewData] = useState({ rating: 0, comment: '' });
  const [barberBookings, setBarberBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [barbershopData, setBarbershopData] = useState(null);
  const [barberBarbershopsList, setBarberBarbershopsList] = useState([]);
  const [showCreateBarbershop, setShowCreateBarbershop] = useState(false);
  const [selectedClientProfile, setSelectedClientProfile] = useState(null);
  const [clientProfileReviews, setClientProfileReviews] = useState([]);
  const [clientProfileStats, setClientProfileStats] = useState(null);
  const [dismissedReviewItems, setDismissedReviewItems] = useState([]); // IDs de cortes completados que el cliente ha descartado

  // Client specific states
  const [bookingHistory, setBookingHistory] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [recentCancelledBookings, setRecentCancelledBookings] = useState(() => {
    try {
      const raw = localStorage.getItem('recentCancelledBookings');
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const now = Date.now();
      const ttl = 15 * 60 * 1000;
      const cleaned = {};
      Object.entries(parsed).forEach(([key, value]) => {
        if (value && now - value.timestamp < ttl) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    } catch (error) {
      return {};
    }
  });
  const [selectedBookingChat, setSelectedBookingChat] = useState(null);
  const chatLastSeenRef = useRef({});
  const chatOpenedRef = useRef({});
  const chatUnreadRef = useRef({});
  const adminCacheRef = useRef({
    users: new Map(),
    reviews: new Map(),
    barbershops: new Map()
  });
  const visitTrackedRef = useRef(false);
  const recordVisitEvent = useCallback((eventType) => {
    axios.post(`${BACKEND_URL}/api/visit-events`, { event_type: eventType }).catch(() => {});
  }, []);
  const realtimeConnectedRef = useRef(false);

  const applyDefaultAvatar = (userData) => {
    if (!userData) return userData;
    return { ...userData, profile_image: getUserAvatarUrl(userData) };
  };
  const [userSettings, setUserSettings] = useState({
    name: '',
    email: '',
    phone: '',
    age: '',
    address: '',
    hairPreference: '',
    notifications: {
      offers: true,
      reminders: true
    }
  });
  const [showHistory, setShowHistory] = useState(false);
  const [historyView, setHistoryView] = useState('day'); // 'day' | 'week' | 'month' | 'year'
  const [historyExpanded, setHistoryExpanded] = useState(() => new Set()); // group keys expanded
  const [historyColumnsVisible, setHistoryColumnsVisible] = useState(() => ({ fecha: true, lugar: true, barbero: true, servicio: true, cobro: true }));
  const [historialActionItem, setHistorialActionItem] = useState(null); // item seleccionado para Hablar/Reseña/Ayuda
  const [showSettings, setShowSettings] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  const historialGrouped = useMemo(() => {
    const completed = (bookingHistory || [])
      .filter(item => item.status === 'completed')
      .sort((a, b) => (parseAsUTC(b.created_at || b.date).getTime()) - (parseAsUTC(a.created_at || a.date).getTime()));
    if (completed.length === 0) return [];
    const tz = 'America/Santiago';
    const getParts = (value) => {
      const d = parseAsUTC(value);
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
      const get = (type) => parts.find(p => p.type === type)?.value || '';
      return { y: get('year'), m: get('month'), d: get('day'), date: d };
    };
    const getDayKey = (value) => {
      const p = getParts(value);
      return `${p.y}-${p.m}-${p.d}`;
    };
    const getWeekKey = (value) => {
      const p = getParts(value);
      const y = parseInt(p.y, 10); const m = parseInt(p.m, 10) - 1; const d = parseInt(p.d, 10);
      const v = new Date(y, m, d);
      const dow = v.getDay();
      const mon = new Date(v);
      mon.setDate(v.getDate() - (dow === 0 ? 6 : dow - 1));
      return mon.toISOString().slice(0, 10);
    };
    const getMonthKey = (value) => {
      const p = getParts(value);
      return `${p.y}-${p.m}`;
    };
    const getYearKey = (value) => {
      return getParts(value).y;
    };
    const getKey = (item) => {
      const v = item.created_at || item.date;
      if (historyView === 'day') return getDayKey(v);
      if (historyView === 'week') return getWeekKey(v);
      if (historyView === 'month') return getMonthKey(v);
      return getYearKey(v);
    };
    const getLabel = (key) => {
      if (historyView === 'year') return key;
      if (historyView === 'month') {
        const [y, m] = key.split('-');
        return formatDateWithTZ(new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1), { month: 'long', year: 'numeric' });
      }
      if (historyView === 'week') {
        const [y, m, d] = key.split('-').map(Number);
        const mon = new Date(y, m - 1, d);
        const sun = new Date(mon);
        sun.setDate(mon.getDate() + 6);
        return `Semana ${mon.getDate()}-${sun.getDate()} ${formatDateWithTZ(mon, { month: 'short' })} ${y}`;
      }
      const [y, m, d] = key.split('-').map(Number);
      return formatDateWithTZ(new Date(y, m - 1, d), { day: 'numeric', month: 'long', year: 'numeric' });
    };
    const byKey = new Map();
    completed.forEach(item => {
      const key = getKey(item);
      if (!byKey.has(key)) byKey.set(key, { key, label: getLabel(key), items: [] });
      byKey.get(key).items.push(item);
    });
    return Array.from(byKey.values()).sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
  }, [bookingHistory, historyView]);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingBarbershop, setBookingBarbershop] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showAppMenu, setShowAppMenu] = useState(false);
  const [showBarberProfileHub, setShowBarberProfileHub] = useState(false);
  const [showJoinBarbershop, setShowJoinBarbershop] = useState(false);
  const [joinSearch, setJoinSearch] = useState('');
  const [joinResults, setJoinResults] = useState([]);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const confirmResolvedRef = useRef(false);
  const adminProfileImageInputRef = useRef(null);
  
  function showNotification(title, message, type = 'info') {
    const normalizedTitle = String(title || '').toLowerCase();
    const shouldToast =
      user?.user_type === 'barber' &&
      (normalizedTitle.includes('nuevo mensaje') || normalizedTitle.includes('nueva solicitud'));

    if (shouldToast) {
      toast({
        title,
        description: message,
        variant: type,
        duration: 3000
      });
    }
    if (user) {
      setUserNotifications((prev) => {
        const entry = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title,
          message,
          createdAt: Date.now()
        };
        return [entry, ...prev].slice(0, 60);
      });
    }
  }

  const requestConfirmation = ({ title, description, confirmText = 'Confirmar', variant = 'default' }) => {
    return new Promise((resolve) => {
      confirmResolvedRef.current = false;
      setConfirmDialogData({
        title,
        description,
        confirmText,
        variant,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      });
      setShowConfirmDialog(true);
    });
  };

  const onboardingSteps = useMemo(() => {
    if (user?.user_type === 'barber') {
      return [
        {
          title: 'Configura tu perfil y barbería',
          description: 'Actualiza tu imagen, datos de contacto, servicios y horarios para aparecer correctamente.'
        },
        {
          title: 'Gestiona solicitudes',
          description: 'Revisa y responde solicitudes de reservas y cortes rápidos desde “Solicitudes”.'
        },
        {
          title: 'Organiza tus citas',
          description: 'Usa el calendario para confirmar, reagendar o cancelar con claridad.'
        },
        {
          title: 'Historial e ingresos',
          description: 'Revisa cortes realizados, exporta CSV y controla tus ingresos.'
        },
        {
          title: 'Conversaciones y reseñas',
          description: 'Chatea con clientes y gestiona reseñas para fortalecer tu reputación.'
        }
      ];
    }
    if (user?.user_type === 'admin') {
      return [];
    }
    return [
      {
        title: 'Edita tu perfil',
        description: 'Cambia tu imagen, tu apodo y verifica tu contacto desde la pestaña “Perfil”.'
      },
      {
        title: 'Explora barberías',
        description: 'Busca barberías cerca de ti, revisa comentarios y reserva tu cita.'
      },
      {
        title: 'Corte rápido',
        description: 'Busca cortes rápidos cerca de ti de forma instantánea. Consigue un corte en minutos.'
      },
      {
        title: 'Solicitudes aceptadas',
        description: 'Una vez aceptadas tus solicitudes, chatea con el barbero o indica que ya llegaste.'
      },
      {
        title: 'Reseñas y comentarios',
        description: 'Una vez completados tus cortes, deja reseñas y comentarios para el barbero.'
      }
    ];
  }, [user?.user_type]);

  const helpSections = useMemo(() => {
    if (user?.user_type === 'barber') {
      return [
        {
          title: 'Citas y solicitudes',
          items: [
            'Acepta o rechaza solicitudes desde la pestaña Solicitudes.',
            'Marca completadas para que el cliente pueda calificar.',
            'Puedes usar “Solo agenda” si no quieres cortes rápidos.'
          ]
        },
        {
          title: 'Perfil de barbería',
          items: [
            'Sube portada y foto de perfil para un look profesional.',
            'Agrega servicios, precios y portafolio de trabajos.',
            'Mantén dirección y teléfono actualizados.'
          ]
        },
        {
          title: 'Soporte',
          items: [
            'Si algo falla, cierra sesión e inicia nuevamente.',
            'Revisa tu conexión antes de aceptar solicitudes.',
            'Escríbenos desde el chat si necesitas ayuda.'
          ]
        }
      ];
    }
    if (user?.user_type === 'admin') {
      return [
        {
          title: 'Gestión de plataforma',
          items: [
            'Edita barberías, usuarios y reseñas desde el panel.',
            'Usa filtros y exportaciones para auditoría rápida.',
            'Las acciones quedan registradas en el log.'
          ]
        },
        {
          title: 'Moderación',
          items: [
            'Puedes ocultar o eliminar reseñas problemáticas.',
            'Envía mensajes a barberías desde el panel.',
            'Activa mantenimiento si es necesario.'
          ]
        },
        {
          title: 'Soporte',
          items: [
            'Revisa la configuración de entorno si falla el deploy.',
            'Contacta al equipo técnico vía chat interno.',
            'Guarda cambios antes de salir.'
          ]
        }
      ];
    }
    return [
      {
        title: 'Reservas y cortes rápidos',
        items: [
          'Puedes reservar con fecha y hora exacta o solicitar corte rápido.',
          'Si tu solicitud no es aceptada, puedes reintentar hasta 3 veces cada 15 min.',
          'Cuando el barbero cancela, recibirás notificación y mensaje en el chat.'
        ]
      },
      {
        title: 'Perfil y reseñas',
        items: [
          'Sube tu foto o elige un avatar desde el perfil.',
          'Revisa tus reseñas y el historial de cortes.',
          'Tus cambios se reflejan en toda la app.'
        ]
      },
      {
        title: 'Soporte',
        items: [
          'Si algo falla, cierra sesión e inicia nuevamente.',
          'Revisa tu conexión antes de confirmar una reserva.',
          'Escríbenos desde el chat si necesitas ayuda.'
        ]
      }
    ];
  }, [user?.user_type]);

  const closeOnboarding = () => {
    if (user?.id) {
      localStorage.setItem(`onboarding_seen_${user.id}`, '1');
    }
    setShowOnboarding(false);
    setOnboardingStep(0);
  };
  
  // Review states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: '',
    images: []
  });
  const [showReviewImagesModal, setShowReviewImagesModal] = useState(false);
  const [reviewImagesPreview, setReviewImagesPreview] = useState([]);
  const [completedQuickCut, setCompletedQuickCut] = useState(null);
  const [bookingRequests, setBookingRequests] = useState([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('dismissedReviewItems');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setDismissedReviewItems(parsed);
        }
      }
    } catch (error) {
      // ignore parse errors
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('dismissedReviewItems', JSON.stringify(dismissedReviewItems));
    } catch (error) {
      // ignore storage errors
    }
  }, [dismissedReviewItems]);

  useEffect(() => {
    try {
      localStorage.setItem('recentCancelledBookings', JSON.stringify(recentCancelledBookings));
    } catch (error) {
      // ignore storage errors
    }
  }, [recentCancelledBookings]);

  useEffect(() => {
    if (!priceInputFocused) {
      setPriceInput(formatClp(priceLimit[0]));
    }
  }, [priceLimit, priceInputFocused]);

  function registerCancelledBooking(bookingId, barbershopId) {
    if (!bookingId || !barbershopId) return;
    const now = Date.now();
    setRecentCancelledBookings((prev) => ({
      ...prev,
      [bookingId]: { barbershopId, timestamp: now }
    }));
  }

  // ============= HELPER FUNCTIONS & COMPONENTS (moved inside) =============


    const handleQuickCutResponse = async (requestId, accept) => {
      try {
        await axios.post(`${BACKEND_URL}/api/quick-cuts/${requestId}/respond`, {
          accept
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      
        loadBarberData(); // Refresh data
        setSuccess(accept ? 'Solicitud aceptada' : 'Solicitud rechazada');
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Error al responder la solicitud');
      }
    };

    const handleCreateBarbershop = async (barbershopData) => {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/barbershops`, barbershopData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      
        setBarbershopData(response.data);
        setShowCreateBarbershop(false);
        setSuccess('¡Barbería creada exitosamente! Ya apareces en el mapa.');
        setTimeout(() => setSuccess(''), 5000);
      
        // Reload barbershops para mostrar la nueva en el mapa
        loadBarbershops();
      } catch (error) {
        setError(error.response?.data?.detail || 'Error al crear barbería');
      }
    };

    const openBookingModal = useCallback((barbershop) => {
      setBookingBarbershop(barbershop);
      setShowBooking(true);
    }, []);

  const barbershopCardPropsMemo = useMemo(
    () => ({
      recentCancelledBookings,
      setSelectedBarbershop,
      setSelectedUpcomingBooking,
      getBarbershopAvatarUrl,
      formatClp,
      openBookingModal
    }),
    [
      recentCancelledBookings,
      setSelectedBarbershop,
      setSelectedUpcomingBooking,
      getBarbershopAvatarUrl,
      formatClp,
      openBookingModal
    ]
  );

    // LoginForm se usa como componente externo

    const RegisterForm = () => {
      const { isLoaded, inputRef, setupPlaceChangedListener } = useGooglePlaces(GOOGLE_MAPS_API_KEY);
      const [selectedPlace, setSelectedPlace] = useState(null);
      const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        userType: 'client',
        address: '',
        phone: '',
        city: '',
        comuna: '',
        region: '',
        street: '',
        street_number: '',
        postal_code: ''
      });
      const [registerError, setRegisterError] = useState('');
      const [isLoading, setIsLoading] = useState(false);
      const [registerEmailSent, setRegisterEmailSent] = useState(true);
      const [registerEmail, setRegisterEmail] = useState('');
      const [resendLoading, setResendLoading] = useState(false);
      const [resendMessage, setResendMessage] = useState('');

      useEffect(() => {
        if (!isLoaded) return;
        setupPlaceChangedListener((place) => {
          const components = place.components || {};
          setSelectedPlace(place);
          setFormData((prev) => ({
            ...prev,
            address: place.formatted_address,
            lat: place.lat,
            lng: place.lng,
            city: components.city || '',
            comuna: components.comuna || components.city || '',
            region: components.region || components.state || '',
            street: components.street_name || '',
            street_number: components.street_number || '',
            postal_code: components.postal_code || ''
          }));
        });
      }, [isLoaded, setupPlaceChangedListener]);

      useEffect(() => {
        if (showRegister && registerInviteBarbershopId) {
          setFormData((prev) => ({ ...prev, userType: 'barber' }));
        }
      }, [showRegister, registerInviteBarbershopId]);

      return (
        <div className="space-y-4">
          {registerInviteBarbershopId && (
            <div className="p-3 bg-amber-500/15 border border-amber-500/40 rounded-lg text-sm text-amber-200">
              Te han invitado a unirte a una barbería. Regístrate como barbero para unirte automáticamente.
            </div>
          )}
          <div>
            <label className="text-white text-sm font-medium mb-2 block">
              ¿Qué tipo de usuario eres? *
            </label>
            <Select
              value={formData.userType}
              onValueChange={(v) => setFormData({ ...formData, userType: v })}
              disabled={!!registerInviteBarbershopId}
            >
              <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-amber-500">
                <SelectValue placeholder="Tipo de usuario" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client">👤 Cliente - Busco cortarme el pelo</SelectItem>
                <SelectItem value="barber">✂️ Barbero - Quiero ofrecer mis servicios</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Input
            placeholder="Nombre completo"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
          />
          <Input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
          />
          <Input
            type="password"
            placeholder="Contraseña"
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
          />
          <Input
            type="password"
            placeholder="Confirmar contraseña"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
          />

          {formData.userType === 'barber' && (
              <Input
                placeholder="Teléfono (requerido para barberos)"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
          )}

          <div className="space-y-2">
            <label className="text-white text-sm font-medium">
              Dirección real (Google) *
            </label>
            <div className="relative">
              <Input
                ref={inputRef}
                placeholder="Ingresa tu dirección y selecciona una sugerencia"
                value={formData.address}
                onChange={(e) => {
                  setFormData({...formData, address: e.target.value});
                  setSelectedPlace(null);
                }}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
              <div className="absolute right-3 top-3 flex items-center gap-1">
                {selectedPlace && (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                )}
                <MapPin className="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <p className="text-gray-400 text-xs">
              Selecciona una dirección válida de la lista.
            </p>
          </div>

          {registerError && (
            <div className="p-3 bg-red-900/20 border border-red-500 rounded-lg">
              <p className="text-red-400 text-sm">{registerError}</p>
            </div>
          )}

          <Button 
            onClick={async () => {
              
              // Validate required fields
              if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
                setRegisterError('Por favor completa todos los campos obligatorios');
                return;
              }
              
              if (formData.password !== formData.confirmPassword) {
                setRegisterError('Las contraseñas no coinciden');
                return;
              }
              
              if (formData.password.length < 6) {
                setRegisterError('La contraseña debe tener al menos 6 caracteres');
                return;
              }
              
              // Validate barber-specific fields
              if (formData.userType === 'barber' && !formData.phone) {
                  setRegisterError('El teléfono es obligatorio para barberos');
                  return;
                }
                if (!formData.address) {
                setRegisterError('La dirección es obligatoria para registrarte');
                  return;
                }
              if (!selectedPlace) {
                setRegisterError('Debes seleccionar una dirección válida desde Google');
                return;
              }
              
              setIsLoading(true);
              setRegisterError('');
              setResendMessage('');
              try {
                const payload = { ...formData };
                if (registerInviteBarbershopId) {
                  payload.barbershop_invite = registerInviteBarbershopId;
                  payload.userType = 'barber';
                }
                const response = await axios.post(`${BACKEND_URL}/api/auth/register`, payload);
                setRegisterEmail(formData.email);
                setRegisterEmailSent(response.data?.email_sent !== false);
                setRegisterInviteBarbershopId(null);
                setShowRegister(false);
                if (response.data?.email_sent === false) {
                  setSuccess('Cuenta creada, pero no pudimos enviar el email. Reenvíalo desde la pantalla de inicio de sesión.');
                } else {
                  setSuccess('Te enviamos un correo de verificación. Confirma tu email para activar tu cuenta.');
                }
              } catch (error) {
                console.error('❌ REGISTER ERROR:', error);
                const errorDetail = error.response?.data?.detail;
                
                // Convertir CUALQUIER tipo de error a string
                if (typeof errorDetail === 'string') {
                  setRegisterError(errorDetail);
                } else if (Array.isArray(errorDetail)) {
                  setRegisterError(errorDetail.map(e => e.msg || e.message || String(e)).join(', '));
                } else if (errorDetail && typeof errorDetail === 'object') {
                  setRegisterError(errorDetail.message || errorDetail.msg || 'Error en el registro');
                } else {
                  setRegisterError('No se pudo completar el registro. Por favor verifica los datos.');
                }
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className="w-full bg-amber-500 hover:bg-amber-700"
          >
            {isLoading ? 'Registrando...' : 'Registrarse'}
          </Button>
          {!registerEmailSent && registerEmail && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg text-xs text-amber-200">
              No se pudo enviar el correo de verificación. Puedes reenviarlo desde Inicio de sesión.
            </div>
          )}
        </div>
      );
    };

    // Create Barbershop Form
    const CreateBarbershopForm = () => {
      const { isLoaded, inputRef, setupPlaceChangedListener } = useGooglePlaces(GOOGLE_MAPS_API_KEY);
      const [selectedPlace, setSelectedPlace] = useState(null);
      const [addressError, setAddressError] = useState('');
    
      const [formData, setFormData] = useState({
        name: '',
        description: '',
        address: user?.address || '',
        phone: user?.phone || '',
        city: '',
        comuna: '',
        region: '',
        street: '',
        street_number: '',
        postal_code: '',
        services: [
          { name: 'Corte de pelo', price: 12000, duration: 30 },
          { name: 'Corte + barba', price: 18000, duration: 45 }
        ],
        working_hours: {
          monday: { open: '09:00', close: '18:00', isOpen: true },
          tuesday: { open: '09:00', close: '18:00', isOpen: true },
          wednesday: { open: '09:00', close: '18:00', isOpen: true },
          thursday: { open: '09:00', close: '18:00', isOpen: true },
          friday: { open: '09:00', close: '18:00', isOpen: true },
          saturday: { open: '09:00', close: '16:00', isOpen: true },
          sunday: { open: '10:00', close: '15:00', isOpen: false }
        }
      });

      // Set up Google Places listener when loaded
      useEffect(() => {
        if (isLoaded) {
        setupPlaceChangedListener((place) => {
          const components = place.components || {};
          setSelectedPlace(place);
          setFormData(prev => ({
            ...prev,
            address: place.formatted_address,
            lat: place.lat,
            lng: place.lng,
            city: components.city || '',
            comuna: components.comuna || components.city || '',
            region: components.region || components.state || '',
            street: components.street_name || '',
            street_number: components.street_number || '',
            postal_code: components.postal_code || ''
          }));
          
            showNotification(
              '📍 Ubicación Confirmada',
              `Dirección: ${place.formatted_address}`,
              'success'
            );
          });
        }
      }, [isLoaded, setupPlaceChangedListener]);

      const addService = () => {
        setFormData({
          ...formData,
          services: [...formData.services, { name: '', price: 0, duration: 30 }]
        });
      };

      const removeService = (index) => {
        const newServices = formData.services.filter((_, i) => i !== index);
        setFormData({ ...formData, services: newServices });
      };

      const updateService = (index, field, value) => {
        const newServices = [...formData.services];
        newServices[index] = { ...newServices[index], [field]: value };
        setFormData({ ...formData, services: newServices });
      };

      const handleSubmit = (e) => {
        e.preventDefault();
        if (!selectedPlace) {
          setAddressError('Debes seleccionar una dirección válida desde Google');
          return;
        }
        setAddressError('');
        handleCreateBarbershop(formData);
      };

      return (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              placeholder="Nombre de tu barbería"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              required
            />
            <Input
              placeholder="Teléfono"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              required
            />
          </div>

          {/* Google Places Autocomplete Address Input */}
          <div className="space-y-2">
            <label className="text-white text-sm font-medium">Dirección de tu barbería *</label>
            <div className="relative">
              <Input
                ref={inputRef}
                id="address-autocomplete"
                placeholder="Ingresa la dirección de tu barbería..."
                value={formData.address}
                onChange={(e) => {
                  setFormData({...formData, address: e.target.value});
                  setSelectedPlace(null);
                }}
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                required
              />
              <div className="absolute right-3 top-3 flex items-center gap-1">
                {selectedPlace && (
                  <CheckCircle className="w-4 h-4 text-green-400" />
                )}
                <MapPin className="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-400" />
              <p className="text-xs text-gray-400">
                💡 Escribe y selecciona una sugerencia para obtener la ubicación exacta.
              </p>
            </div>
          
            {selectedPlace && (
              <div className="bg-green-900/20 border border-green-700 p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 text-sm font-medium">Ubicación Confirmada</span>
                </div>
                <p className="text-green-200 text-xs">{selectedPlace.formatted_address}</p>
                <p className="text-green-300 text-xs mt-1">
                  📍 {selectedPlace.lat.toFixed(6)}, {selectedPlace.lng.toFixed(6)}
                </p>
              </div>
            )}
            {addressError && (
              <p className="text-xs text-red-400">{addressError}</p>
            )}
          </div>

          <Textarea
            placeholder="Describe tu barbería y tus especialidades..."
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="min-h-[80px] bg-gray-800 border-gray-700 text-white placeholder-gray-400"
          />

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium">Servicios</h4>
              <Button type="button" onClick={addService} size="sm" className="bg-amber-500 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>
          
            <div className="space-y-2">
              {formData.services.map((service, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    placeholder="Servicio"
                    value={service.name}
                    onChange={(e) => updateService(index, 'name', e.target.value)}
                    className="flex-1 bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Precio"
                    value={service.price}
                    onChange={(e) => updateService(index, 'price', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                    className="w-24 bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Min"
                    value={service.duration}
                    onChange={(e) => updateService(index, 'duration', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                    className="w-16 bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                  {formData.services.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeService(index)}
                      size="sm"
                      variant="outline"
                      className="border-red-400 text-red-400"
                    >
                      ×
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-700">
            Crear Mi Barbería
          </Button>
        </form>
      );
    };

    const SwipeableListItem = ({ children, onSwipeLeft, enabled = false, className = '' }) => {
      const [offsetX, setOffsetX] = useState(0);
      const [isSwiping, setIsSwiping] = useState(false);
      const startXRef = useRef(0);
      const startYRef = useRef(0);
      const threshold = 110;

      const handleTouchStart = (event) => {
        if (!enabled) return;
        const touch = event.touches[0];
        startXRef.current = touch.clientX;
        startYRef.current = touch.clientY;
        setIsSwiping(true);
      };

      const handleTouchMove = (event) => {
        if (!enabled || !isSwiping) return;
        const touch = event.touches[0];
        const deltaX = touch.clientX - startXRef.current;
        const deltaY = touch.clientY - startYRef.current;
        if (Math.abs(deltaY) > Math.abs(deltaX)) return;
        if (deltaX < 0) {
          setOffsetX(Math.max(deltaX, -160));
        }
      };

      const handleTouchEnd = () => {
        if (!enabled) return;
        if (offsetX <= -threshold) {
          onSwipeLeft?.();
        }
        setIsSwiping(false);
        setOffsetX(0);
      };

              return (
        <div
          className={className}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            transform: `translateX(${offsetX}px)`,
            transition: isSwiping ? 'none' : 'transform 300ms ease'
          }}
        >
          {children}
                          </div>
      );
    };

    const MobileTabBar = ({ items, value, onChange }) => (
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-gray-950/95 border-t border-gray-800 px-2 py-2 z-40">
        <div className="flex items-center justify-around gap-2">
          {items.map((item) => {
            const isActive = value === item.value;
            const Icon = item.icon;
            return (
                                <button
                key={item.value}
                type="button"
                onClick={() => onChange(item.value)}
                className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg text-xs transition-colors ${
                  isActive ? 'text-amber-300 bg-amber-500/10' : 'text-gray-400'
                }`}
              >
                <span className="relative">
                  <Icon
                    className={`w-4 h-4 mb-1 ${
                      item.alert ? 'text-amber-300 drop-shadow-[0_0_10px_rgba(245,158,11,0.9)] animate-pulse' : ''
                    }`}
                  />
                  {item.alert && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)]" />
                  )}
                </span>
                <span className="text-[11px]">{item.label}</span>
              </button>
            );
          })}
          </div>
                </div>
    );

    const QuickCutSection = () => {
      const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      };
      const pendingClientBookings = (upcomingBookings || []).filter((booking) =>
        ['pending', 'accepted'].includes(booking.status)
      );
      const hasPendingActivity =
        pendingClientBookings.length > 0 || clientAcceptedQuickCuts.length > 0;

      return (
        <div className="space-y-4 sm:space-y-6">
          <Dialog open={showNoBarbersModal} onOpenChange={setShowNoBarbersModal}>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white">No hay barberos disponibles</DialogTitle>
                <DialogDescription className="sr-only">
                  Aviso de no disponibilidad y opción de nueva búsqueda.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-gray-300 text-sm">
                  Ninguna de las barberías está disponible en este momento. ¿Te gustaría buscar nuevamente?
                </p>
                <div className="text-xs text-gray-500">
                  Intentos usados: {quickCutSearchAttempts} / 3 (cada 15 minutos)
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-amber-500 hover:bg-amber-700"
                    onClick={() => handleQuickSearch({ isRetry: true })}
                  >
                    Buscar nuevamente
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-gray-700 text-white"
                    onClick={() => setShowNoBarbersModal(false)}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="text-center px-2">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">Corte Rápido</h2>
            <p className="text-gray-400 text-sm sm:text-base">Encuentra un barbero disponible ahora mismo</p>
          </div>

          {/* Actividad por cumplir */}
          {hasPendingActivity && (
            <Card className="bg-gradient-to-br from-amber-900/30 to-gray-900 border-amber-500">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  Actividad por cumplir ({pendingClientBookings.length + clientAcceptedQuickCuts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingClientBookings.map((booking) => (
                  <Card key={`booking-${booking.id}`} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-white font-medium">{booking.service || 'Corte de pelo'}</h4>
                            <Badge className="bg-blue-500 text-white">📅 Reserva</Badge>
                          </div>
                          <p className="text-gray-300 text-sm mb-1">
                            <strong>Barbería:</strong> {booking.barbershop?.name || booking.barbershop_name || 'Barbería'}
                          </p>
                          {booking.barbershop?.address && (
                            <p className="text-gray-400 text-xs mb-1">
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {booking.barbershop.address}
                            </p>
                          )}
                          {booking.date && (
                            <p className="text-gray-400 text-xs">
                              📅 {formatDateTimeWithTZ(booking.date, { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-400 text-amber-300 hover:bg-amber-500/10"
                          onClick={() => setSelectedUpcomingBooking(booking)}
                        >
                          Ver detalle
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {clientAcceptedQuickCuts.map((qc) => (
                  <Card key={`quick-${qc.id}`} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-white font-medium">{qc.service}</h4>
                            <Badge className="bg-amber-500 text-white">⚡ Aceptado</Badge>
                          </div>
                          {qc.barbershop && (
                            <>
                              <p className="text-gray-300 text-sm mb-1">
                                <strong>Barbería:</strong> {qc.barbershop.name}
                              </p>
                              {qc.barbershop.address && (
                                <p className="text-gray-400 text-xs mb-1">
                                  <MapPin className="w-3 h-3 inline mr-1" />
                                  {qc.barbershop.address}
                                </p>
                              )}
                              {qc.barbershop.phone && (
                                <p className="text-gray-400 text-xs mb-1">
                                  <Phone className="w-3 h-3 inline mr-1" />
                                  {qc.barbershop.phone}
                                </p>
                              )}
                            </>
                          )}
                          <p className="text-amber-400 text-sm font-medium mt-2">
                            Presupuesto: ${qc.max_price?.toLocaleString()}
                          </p>
                          {qc.preferred_time && (
                            <p className="text-blue-400 text-xs mt-1">
                              ⏱️ {
                                qc.preferred_time === 'asap' ? 'Lo antes posible' :
                                qc.preferred_time === '30min' ? 'En 30 minutos' :
                                qc.preferred_time === '1hour' ? 'En 1 hora' :
                                qc.preferred_time === '2hours' ? 'En 2 horas' :
                                'Horario flexible'
                              }
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => {
                            setSelectedBookingChat({
                              id: qc.id,
                              status: qc.status,
                              service: qc.service,
                              type: 'quick_cut',
                              barber: qc.barber,
                              barbershop: qc.barbershop,
                              price: qc.price || qc.max_price
                            });
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 min-w-[120px]"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Chatear
                        </Button>
                        <Button
                          onClick={() => handleMarkArrived(qc)}
                          className="flex-1 bg-green-600 hover:bg-green-700 min-w-[120px]"
                          disabled={qc.client_arrived}
                        >
                          {qc.client_arrived ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Ya llegaste
                            </>
                          ) : (
                            <>
                              <MapPin className="w-4 h-4 mr-2" />
                              He llegado
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleCancelQuickCutRequest(qc)}
                          variant="outline"
                          className="flex-1 border-red-400 text-red-400 hover:bg-red-400 hover:text-white min-w-[120px]"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancelar
                        </Button>
                        {qc.barbershop && qc.barbershop.address && (
                          <Button
                            onClick={() => {
                              window.open(`https://maps.google.com/maps?q=${encodeURIComponent(qc.barbershop.address)}`, '_blank');
                            }}
                            variant="outline"
                            className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                          >
                            <Navigation className="w-4 h-4 mr-2" />
                            Ver mapa
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}

          {quickCutStatus === 'idle' && (
            <Card className="bg-gray-900 border-gray-700 max-w-2xl mx-auto w-full">
              <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div>
                  <label className="text-white text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 block">
                    Servicio requerido
                  </label>
                  <Select value={selectedService} onValueChange={setSelectedService}>
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white font-semibold hover:bg-gray-700">
                      <SelectValue placeholder="Selecciona un servicio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Corte de pelo" className="text-white focus:bg-gray-700">
                        Corte de pelo
                      </SelectItem>
                      <SelectItem value="Corte + barba" className="text-white focus:bg-gray-700">
                        Corte + barba
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-gradient-to-br from-amber-900/20 to-gray-800/20 p-4 rounded-xl border border-amber-500/30">
                  <label className="text-white text-sm font-semibold mb-3 block flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                    Presupuesto máximo (CLP)
                  </label>
                  
                  <div>
                      <Input
                      type="text"
                      inputMode="numeric"
                      value={priceInputFocused ? priceInput : formatClp(priceLimit[0])}
                        onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setPriceInput(digits);
                        const numericValue = digits ? parseInt(digits, 10) : 15000;
                        const clamped = Math.max(3000, Math.min(100000, numericValue));
                        setPriceLimit([clamped]);
                      }}
                      onFocus={() => {
                        setPriceInputFocused(true);
                        setPriceInput(String(priceLimit[0] || 15000));
                      }}
                      onBlur={() => {
                        setPriceInputFocused(false);
                        setPriceInput(formatClp(priceLimit[0]));
                      }}
                      className="bg-gray-800 border-gray-600 text-white text-lg font-semibold px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 rounded-md"
                      placeholder="CLP $15.000"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 block">
                    Distancia máxima: {maxDistance[0]} km
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[1, 3, 5, 10].map(km => (
                      <Button
                        key={km}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setMaxDistance([km])}
                        className={`text-xs sm:text-sm px-3 ${maxDistance[0] === km ? "bg-amber-500 text-black border-amber-500 hover:bg-amber-600" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700"}`}
                      >
                        {km}km
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    Ubicación del servicio
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant={serviceLocation === 'local' ? 'default' : 'outline'}
                      onClick={() => setServiceLocation('local')}
                      className={serviceLocation === 'local' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      En el local
                    </Button>
                    <Button
                      type="button"
                      variant={serviceLocation === 'home' ? 'default' : 'outline'}
                      onClick={() => setServiceLocation('home')}
                      className={serviceLocation === 'home' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      A domicilio
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    ¿Cuándo quieres cortarte?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant={preferredTime === 'asap' ? 'default' : 'outline'}
                      onClick={() => setPreferredTime('asap')}
                      className={preferredTime === 'asap' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      Lo antes posible
                    </Button>
                    <Button
                      type="button"
                      variant={preferredTime === '30min' ? 'default' : 'outline'}
                      onClick={() => setPreferredTime('30min')}
                      className={preferredTime === '30min' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      En 30 minutos
                    </Button>
                    <Button
                      type="button"
                      variant={preferredTime === '1hour' ? 'default' : 'outline'}
                      onClick={() => setPreferredTime('1hour')}
                      className={preferredTime === '1hour' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      En 1 hora
                    </Button>
                    <Button
                      type="button"
                      variant={preferredTime === '2hours' ? 'default' : 'outline'}
                      onClick={() => setPreferredTime('2hours')}
                      className={preferredTime === '2hours' ? "bg-amber-500 hover:bg-amber-700 text-black px-3" : "text-white border-gray-600 hover:border-amber-500 hover:text-amber-500 hover:bg-gray-700 px-3"}
                    >
                      En 2 horas
                    </Button>
                  </div>
                </div>

                {/* Características Reales - Chat en Tiempo Real */}
                <div className="bg-gradient-to-br from-blue-900/20 to-gray-800/20 p-4 rounded-xl border border-blue-500/30 space-y-3">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-400" />
                    Comunicación en Tiempo Real
                  </h4>
                
                  <div className="bg-gray-800/50 p-3 rounded-lg">
                    <p className="text-gray-300 text-sm mb-2">
                      💬 Una vez que un barbero acepte tu solicitud, podrás chatear con él en tiempo real para coordinar detalles del servicio.
                    </p>
                    <div className="flex items-center gap-2 text-blue-400 text-xs">
                      <MessageCircle className="w-4 h-4" />
                      <span>Chat disponible cuando el corte sea aceptado</span>
                    </div>
                  </div>

                  {/* Notificaciones */}
                  <div className="flex items-center justify-between bg-gray-800/50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-green-400" />
                      <span className="text-white text-sm">Notificaciones</span>
                    </div>
                    <Button
                      size="sm"
                      variant={notificationsEnabled ? "default" : "outline"}
                      onClick={requestNotificationPermission}
                      className={notificationsEnabled ? "bg-green-600 hover:bg-green-700" : "text-white border-gray-600"}
                    >
                      {notificationsEnabled ? 'Activado' : 'Activar'}
                    </Button>
                  </div>
                </div>

                {/* Smart Recommendations */}
                {smartRecommendations.length > 0 && (
                  <div className="bg-gradient-to-r from-gray-900/30 to-blue-900/30 p-4 rounded-lg">
                    <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-amber-400" />
                      Recomendaciones Inteligentes
                    </h4>
                    {smartRecommendations.slice(0, 2).map((rec, index) => (
                      <div key={index} className="bg-gray-800/50 p-3 rounded mb-2 last:mb-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-medium">{rec.title}</p>
                            <p className="text-gray-400 text-xs">{rec.description}</p>
                          </div>
                          {rec.discount && (
                            <Badge className="bg-amber-500 text-white">
                              -{rec.discount}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Loyalty Points Display */}
                {loyaltyPoints > 0 && (
                  <div className="bg-gradient-to-r from-amber-900/30 to-yellow-900/30 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-400" />
                        <div>
                          <p className="text-white font-medium">Nivel {clientLevel}</p>
                          <p className="text-amber-300 text-sm">{loyaltyPoints} puntos</p>
                        </div>
                      </div>
                      <Badge className="bg-amber-500 text-white">
                        {totalCuts} cortes
                      </Badge>
                    </div>
                  </div>
                )}

                <Button 
                  onClick={handleQuickSearch}
                  disabled={!selectedService || quickCutStatus !== 'idle'}
                  className="w-full bg-amber-500 hover:bg-amber-700 text-white"
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    Buscar Corte Rápido
                  </div>
                </Button>
              </CardContent>
            </Card>
          )}

          {quickCutStatus === 'searching' && (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <div className="relative">
                    <LoadingSpinner size="xl" className="text-amber-400 mx-auto mb-4" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 bg-amber-400 rounded-full animate-pulse opacity-50"></div>
                    </div>
                  </div>
                
                  <h3 className="text-white text-xl font-semibold mb-2">Buscando barbero disponible...</h3>
                  <p className="text-gray-400 mb-4">
                    Notificamos a {matchedBarber ? '1' : '3-5'} barberos cercanos
                  </p>
                
                  {/* Progress bar */}
                  <div className="mb-4">
                    <Progress value={quickCutProgress} className="w-full" />
                    <p className="text-xs text-gray-500 mt-1">
                      {quickCutProgress < 20 ? 'Iniciando búsqueda...' :
                       quickCutProgress < 40 ? 'Localizando barberos...' :
                       quickCutProgress < 60 ? 'Enviando notificaciones...' :
                       quickCutProgress < 80 ? 'Esperando respuestas...' :
                       'Finalizando coincidencia...'}
                    </p>
                  </div>
                
                  <div className="text-amber-400 text-lg font-mono bg-gray-800 p-3 rounded-lg">
                    ⏰ Tiempo restante: {formatTime(searchTimeLeft)}
                  </div>
                </div>
              
                {/* Enhanced search details */}
                <div className="bg-gray-800 p-4 rounded-lg space-y-3">
                  <h4 className="text-white font-medium">Detalles de tu solicitud:</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-amber-400" />
                      <span className="text-gray-300">Servicio:</span>
                    </div>
                    <span className="text-white">{selectedService}</span>
                  
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Presupuesto:</span>
                    </div>
                    <span className="text-white font-semibold">{formatClp(priceLimit[0])}</span>
                  
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-400" />
                      <span className="text-gray-300">Distancia:</span>
                    </div>
                    <span className="text-white">Hasta {maxDistance[0]} km</span>
                  
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className="text-gray-300">Tiempo:</span>
                    </div>
                    <span className="text-white">
                      {preferredTime === 'asap' ? 'Lo antes posible' :
                       preferredTime === '30min' ? 'En 30 minutos' :
                       preferredTime === '1hour' ? 'En 1 hora' :
                       'En 2 horas'}
                    </span>
                  </div>
                </div>

                {/* Real-time updates */}
                {realTimeUpdates && (
                  <div className="bg-blue-900/20 border border-blue-700 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Navigation className="w-4 h-4 text-blue-400 animate-pulse" />
                      <span className="text-blue-300 text-sm font-medium">Actualizaciones en tiempo real</span>
                    </div>
                    <div className="text-xs text-blue-200 space-y-1">
                      <p>• {formatTimeWithTZ(new Date(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}: Búsqueda iniciada</p>
                      <p>• {formatTimeWithTZ(new Date(Date.now() - 5000), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}: 3 barberos notificados</p>
                      {matchedBarber && (
                        <p>• {formatTimeWithTZ(new Date(Date.now() - 2000), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}: Barbero encontrado</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={handleCancelQuickCutSearch}
                    variant="outline"
                    className="flex-1 border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                
                  <Button 
                    onClick={() => {
                      setSearchTimeLeft(getQuickCutResponseWindowSeconds(preferredTime));
                      updateQuickCutProgress('searching');
                    }}
                    variant="outline"
                    className="flex-1 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-white"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Buscar más
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {quickCutStatus === 'matched' && matchedBarber && (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-white text-xl font-semibold mb-2">¡Barbero encontrado!</h3>
                  <p className="text-gray-400 mb-4">
                    {matchedBarber.name} ha aceptado tu solicitud
                  </p>
                </div>
              
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{matchedBarber.name}</h4>
                      <p className="text-gray-400 text-sm">{matchedBarber.barbershop}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-amber-400">${matchedBarber.price?.toLocaleString()}</span>
                        <span className="text-blue-400">{matchedBarber.distance} km</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Estado pendiente hasta completar */}
                <div className="bg-yellow-900/20 border border-yellow-700 p-4 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                    <div>
                      <p className="text-yellow-300 font-medium">Estado: PENDIENTE</p>
                      <p className="text-yellow-400 text-sm">Esperando a que el barbero complete el servicio</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-300">
                  <div className="flex justify-between">
                    <span>Servicio:</span>
                    <span>{selectedService}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ubicación:</span>
                    <span>{serviceLocation === 'home' ? 'A domicilio' : 'En el local'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tiempo preferido:</span>
                    <span>{
                      preferredTime === 'asap' ? 'Lo antes posible' :
                      preferredTime === '30min' ? 'En 30 minutos' :
                      preferredTime === '1hour' ? 'En 1 hora' :
                      'En 2 horas'
                    }</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={() => {
                      // Buscar el Quick Cut aceptado que coincida con este barbero
                      const acceptedQuickCut = clientAcceptedQuickCuts.find(
                        qc => qc.barber?.id === matchedBarber.id || 
                              qc.barbershop?.barber_id === matchedBarber.id ||
                              qc.barber_id === matchedBarber.id
                      );
                      
                      if (acceptedQuickCut) {
                        setSelectedBookingChat({
                          id: acceptedQuickCut.id,
                          status: acceptedQuickCut.status,
                          service: selectedService || acceptedQuickCut.service,
                          type: 'quick_cut',
                          barber: {
                            id: matchedBarber.id,
                            name: matchedBarber.name,
                            ...matchedBarber
                          },
                          barbershop: acceptedQuickCut.barbershop,
                          price: matchedBarber.price || acceptedQuickCut.price || acceptedQuickCut.max_price || acceptedQuickCut.accepted_price
                        });
                      } else if (matchedBarber.quick_cut_id) {
                        // Usar el quick_cut_id del matchedBarber si está disponible
                        setSelectedBookingChat({
                          id: matchedBarber.quick_cut_id,
                          status: 'accepted',
                          service: selectedService,
                          type: 'quick_cut',
                          barber: {
                            id: matchedBarber.id,
                            name: matchedBarber.name,
                            ...matchedBarber
                          },
                          price: matchedBarber.price
                        });
                      } else {
                        // Fallback: usar el primer Quick Cut aceptado
                        const firstAccepted = clientAcceptedQuickCuts[0];
                        if (firstAccepted) {
                          setSelectedBookingChat({
                            id: firstAccepted.id,
                            status: firstAccepted.status,
                            service: selectedService || firstAccepted.service,
                            type: 'quick_cut',
                            barber: {
                              id: matchedBarber.id,
                              name: matchedBarber.name,
                              ...matchedBarber
                            },
                            barbershop: firstAccepted.barbershop,
                            price: matchedBarber.price || firstAccepted.price || firstAccepted.max_price || firstAccepted.accepted_price
                          });
                        } else {
                          console.error('No se encontró Quick Cut aceptado para el chat');
                          setError('No se pudo abrir el chat. Por favor, intenta nuevamente.');
                        }
                      }
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat con Barbero
                  </Button>
                
                  <Button 
                    onClick={() => {
                      if (serviceLocation === 'local') {
                        window.open(`https://maps.google.com/maps?q=${matchedBarber.lat},${matchedBarber.lng}`, '_blank');
                      }
                    }}
                    variant="outline"
                    className="flex-1 text-white border-gray-600"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {serviceLocation === 'home' ? 'Barbero en camino' : 'Ir al Local'}
                  </Button>
                </div>
              
                {/* Botón para marcar como completado - solo cliente puede hacerlo */}
                <Button 
                  onClick={() => {
                    setCompletedQuickCut({
                      barber: matchedBarber,
                      service: selectedService,
                      price: matchedBarber.price
                    });
                    setShowReviewModal(true);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Marcar como Completado
                </Button>
              </CardContent>
            </Card>
          )}

          {quickCutStatus === 'pending' && (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                  <h3 className="text-white text-xl font-semibold mb-2">Corte Pendiente</h3>
                  <p className="text-gray-400">Tu barbero te está esperando</p>
                </div>

                <div className="bg-gray-800 p-4 rounded-lg">
                  <h5 className="text-white font-medium mb-2">Estado del corte:</h5>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
                    <span className="text-amber-400">En progreso</span>
                  </div>
                </div>

                <Button 
                  onClick={() => {
                    setCompletedQuickCut({
                      barber: matchedBarber,
                      service: selectedService,
                      price: matchedBarber.price
                    });
                    setShowReviewModal(true);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Marcar como Completado
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      );
    };

  // Load client reviews function
  async function loadClientReviews() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setClientReviews([]);
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/reviews/client/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error loading client reviews:', error);
      setClientReviews([]);
    }
  }

  const handleBookingArrived = async (booking) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/bookings/${booking.id}/arrived`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('✅ Llegada marcada', 'Has marcado tu llegada correctamente', 'success');
      loadUpcomingBookings();
    } catch (error) {
      console.error('Error marking booking arrival:', error);
      showNotification('❌ Error', 'No se pudo marcar la llegada', 'destructive');
    }
  };

  const handleCancelBookingByClient = async (booking) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/bookings/${booking.id}/cancel-by-client`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Reserva cancelada', 'Tu reserva fue cancelada', 'info');
      registerCancelledBooking(booking.id, booking.barbershop?.id || booking.barbershop_id);
      loadUpcomingBookings();
      setSelectedUpcomingBooking(null);
    } catch (error) {
      console.error('Error canceling booking by client:', error);
      showNotification('❌ Error', 'No se pudo cancelar la reserva', 'destructive');
    }
  };

  // Handle edit review
  const handleEditReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}`, {
        rating: editReviewData.rating,
        comment: editReviewData.comment
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      setEditingReview(null);
      setEditReviewData({ rating: 0, comment: '' });
      loadClientReviews();
      setSuccess('Reseña actualizada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error editing review:', error);
      setError(error.response?.data?.detail || 'Error al editar la reseña');
    }
  };

  // Handle delete review
  const handleDeleteReview = async (reviewId) => {
    const confirmed = await requestConfirmation({
      title: 'Eliminar reseña',
      description: '¿Estás seguro de que quieres eliminar esta reseña? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      variant: 'destructive'
    });
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      await axios.delete(`${BACKEND_URL}/api/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      loadClientReviews();
      setSuccess('Reseña eliminada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting review:', error);
      setError(error.response?.data?.detail || 'Error al eliminar la reseña');
    }
  };

  // Publish/unpublish review (for client)
  const handlePublishReview = async (reviewId, published) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}/publish`, {
        published: published
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      loadClientReviews();
      setSuccess(published ? 'Reseña publicada exitosamente' : 'Reseña despublicada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error publishing review:', error);
      setError(error.response?.data?.detail || 'Error al publicar la reseña');
    }
  };

  // Hide/show review (for client)
  const handleHideReview = async (reviewId, hidden) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      await axios.put(`${BACKEND_URL}/api/reviews/${reviewId}/publish`, {
        hidden: hidden
      }, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      loadClientReviews();
      setSuccess(hidden ? 'Reseña ocultada exitosamente' : 'Reseña mostrada exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error hiding review:', error);
      setError(error.response?.data?.detail || 'Error al ocultar la reseña');
    }
  };

  // Load client profile for barber to view
  async function loadClientProfileForBarber(clientId, clientName) {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sesión expirada', 'Inicia sesión nuevamente.', 'destructive');
        return;
      }

      // Obtener todas las reseñas del cliente
      const [reviewsResponse, statsResponse, profileResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reviews/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/clients/${clientId}/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/clients/${clientId}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setClientProfileReviews(reviewsResponse.data.reviews || []);
      setClientProfileStats(statsResponse.data || null);
      setSelectedClientProfile(profileResponse.data?.user || { id: clientId, name: clientName });
    } catch (error) {
      console.error('Error loading client profile:', error);
      // Si el endpoint no existe, intentar con el endpoint de cliente
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/reviews/client/my`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { client_id: clientId }
        });

        setClientProfileReviews(response.data.reviews || []);
        setClientProfileStats(null);
        setSelectedClientProfile({ id: clientId, name: clientName });
      } catch (error2) {
        console.error('Error loading client reviews:', error2);
        showNotification('Error', 'Error al cargar el perfil del cliente', 'destructive');
      }
    }
    };

    const ClientProfile = () => (
      <div className="space-y-6">
        <div className="text-center">
          <Avatar className="w-24 h-24 mx-auto mb-4">
            <AvatarImage src={getUserAvatarUrl(user)} alt={user?.name} />
            <AvatarFallback className="text-2xl">{user?.name?.[0] || 'U'}</AvatarFallback>
          </Avatar>
          <h2 className="text-2xl font-bold mb-2 text-white">{user?.name}</h2>
          <p className="text-gray-400 mb-6">{user?.email}</p>
        </div>

        <div className="max-w-sm mx-auto space-y-4">
          <Button 
            onClick={() => setShowHistory(true)}
            variant="outline" 
            className="w-full"
          >
            <History className="w-4 h-4 mr-2" />
            Historial de Cortes
          </Button>
          <Button 
            onClick={() => setShowSettings(true)}
            variant="outline" 
            className="w-full"
          >
            <Settings className="w-4 h-4 mr-2" />
            Editar perfil
          </Button>
          <Button 
            onClick={() => setShowClientReviews((prev) => !prev)}
            variant="outline" 
            className="w-full"
          >
            <Star className="w-4 h-4 mr-2" />
            {showClientReviews ? 'Ocultar reseñas' : 'Ver reseñas'}
          </Button>
          <Button 
            onClick={() => setShowNotifications(true)}
            variant="outline" 
            className="w-full"
          >
            <Bell className="w-4 h-4 mr-2" />
            Notificaciones
          </Button>
          <Button 
            variant="outline" 
            className="w-full text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>

        {/* Historial de Reseñas - Estilo Facebook/Muro */}
        {showClientReviews && (
        <div className="mt-8">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-amber-400" />
            Mis Reseñas ({clientReviews.length})
          </h3>
          
          {/* Mostrar cortes completados sin reseña */}
          {bookingHistory.filter(item => 
            item.status === 'completed' && 
            !item.review && 
            !dismissedReviewItems.includes(item.id || `${item.service}-${item.created_at || item.date}`)
          ).length > 0 && (
            <div className="mb-6 space-y-3">
              <h4 className="text-white font-medium mb-2">Cortes Completados - Dejar Reseña</h4>
              {bookingHistory
                .filter(item => 
                  item.status === 'completed' && 
                  !item.review && 
                  !dismissedReviewItems.includes(item.id || `${item.service}-${item.created_at || item.date}`)
                )
                .map((item, index) => {
                  const itemId = item.id || `${item.service}-${item.created_at || item.date}`;
                  return (
                    <Card key={`pending-review-${index}`} className="bg-gray-800 border-gray-700 relative">
                      {/* Botón de eliminar en la esquina superior derecha */}
                      <button
                        onClick={() => {
                          setDismissedReviewItems(prev => [...prev, itemId]);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors z-10"
                        title="Eliminar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 pr-8">
                          <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-white font-medium">{item.service || 'Corte de pelo'}</h4>
                              <Badge variant="outline" className="text-green-400 border-green-400">
                                ✓ Completada
                            </Badge>
                          </div>
                          <p className="text-gray-400 text-sm">
                            {item.barbershop?.name || item.barbershop_name || 'Barbería'}
                          </p>
                            <p className="text-amber-400 text-sm">${(item.price || item.max_price || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500">
                            {formatDateTimeWithTZ(item.created_at || item.date, {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                        <Button
                          onClick={() => {
                            setCompletedQuickCut({
                              barber: {
                                name: item.barbershop?.name || 'Barbero',
                                barbershop_id: item.barbershop?.id || item.barbershop_id
                              },
                              service: item.service,
                              price: item.price || item.max_price
                            });
                            setShowReviewModal(true);
                          }}
                          className="w-full bg-amber-500 hover:bg-amber-700"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Dejar Reseña
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
          )}

          {/* Reseñas existentes - Formato igual al barbero */}
          {clientReviews.length === 0 ? (
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="p-8 text-center">
                <Star className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-400">Aún no has dejado ninguna reseña</p>
                <p className="text-gray-500 text-sm mt-2">Tus reseñas aparecerán aquí después de completar un servicio</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {clientReviews.map((review) => (
                <Card key={review.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    {/* Header con nombre y fecha */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-amber-500 text-white">
                            {user?.name?.[0] || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-medium">{user?.name || 'Cliente'}</p>
                            <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                  className={`w-4 h-4 ${
                                    star <= review.rating
                                      ? 'fill-amber-400 text-amber-400'
                                      : 'text-gray-400'
                                  }`}
                              />
                            ))}
                          </div>
                            {review.published === false && (
                              <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs ml-2">
                                No publicada
                              </Badge>
                            )}
                            {review.hidden === true && (
                              <Badge variant="outline" className="text-red-400 border-red-400 text-xs ml-2">
                                Ocultada
                              </Badge>
                          )}
                        </div>
                          <p className="text-gray-400 text-xs">
                            {formatDateTimeWithTZ(review.created_at, {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            {review.updated_at && review.updated_at !== review.created_at && (
                              <span className="text-gray-500 ml-2">(editado)</span>
                            )}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            {review.barbershop?.name || 'Barbería'}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            setEditingReview(review.id);
                            setEditReviewData({
                              rating: review.rating,
                              comment: review.comment || ''
                            });
                          }}
                          size="sm"
                          variant="outline"
                          className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Editar
                        </Button>
                        <Button
                          onClick={() => handleDeleteReview(review.id)}
                          size="sm"
                          variant="outline"
                          className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Eliminar
                        </Button>
                      </div>
                    </div>

                    {/* Comentario */}
                    {editingReview === review.id ? (
                      <div className="space-y-3 mt-3">
                        <div>
                          <label className="text-white text-sm font-medium mb-2 block">Calificación</label>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                type="button"
                                onClick={() => setEditReviewData({...editReviewData, rating: star})}
                                className="text-2xl hover:scale-110 transition-transform"
                              >
                                <Star
                                  className={
                                    star <= editReviewData.rating
                                      ? 'fill-amber-400 text-amber-400'
                                      : 'text-gray-400'
                                  }
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <Textarea
                          value={editReviewData.comment}
                          onChange={(e) => setEditReviewData({...editReviewData, comment: e.target.value})}
                          className="bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          placeholder="Escribe tu comentario..."
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditReview(review.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Guardar
                          </Button>
                          <Button
                            onClick={() => {
                              setEditingReview(null);
                              setEditReviewData({ rating: 0, comment: '' });
                            }}
                            size="sm"
                            variant="outline"
                            className="text-white border-gray-600"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {review.comment && (
                          <p className="text-gray-300 text-sm mb-3">{review.comment}</p>
                        )}
                        
                        {/* Respuesta del barbero */}
                        {review.barber_response && (
                          <div className="bg-gray-900 p-3 rounded-lg mt-3 border-l-4 border-blue-500">
                            <div className="flex items-center gap-2 mb-2">
                              <Reply className="w-4 h-4 text-blue-400" />
                              <span className="text-blue-300 text-sm font-medium">
                                Respuesta de {review.barbershop?.name || 'la barbería'}
                              </span>
                            </div>
                            <p className="text-gray-300 text-sm">{review.barber_response}</p>
                            {review.response_date && (
                              <p className="text-gray-500 text-xs mt-2">
                                {formatDateWithTZ(review.response_date, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Botones de acción - Igual que en barbero */}
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <Button
                            onClick={() => handlePublishReview(review.id, !(review.published !== false))}
                            size="sm"
                            variant="outline"
                            className={(review.published !== false) ? "text-green-400 border-green-400 hover:bg-green-400 hover:text-white text-xs" : "text-yellow-400 border-yellow-400 hover:bg-yellow-400 hover:text-white text-xs"}
                          >
                            {(review.published !== false) ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Publicada
                              </>
                            ) : (
                              <>
                                <Upload className="w-3 h-3 mr-1" />
                                Publicar
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleHideReview(review.id, !(review.hidden === true))}
                            size="sm"
                            variant="outline"
                            className={(review.hidden === true) ? "text-red-400 border-red-400 hover:bg-red-400 hover:text-white text-xs" : "text-gray-400 border-gray-400 hover:bg-gray-400 hover:text-white text-xs"}
                          >
                            {(review.hidden === true) ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Mostrar
                              </>
                            ) : (
                              <>
                                <X className="w-3 h-3 mr-1" />
                                Ocultar
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                      )}
                    </CardContent>
                  </Card>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Historial Modal - overlay opaco para ocultar pestañeo del fondo; foco dentro del diálogo evita aviso aria-hidden */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent
            className="bg-gray-900 border-gray-700 max-w-4xl !duration-0 data-[state=open]:!animate-none data-[state=closed]:!animate-none"
            overlayClassName="!bg-black !duration-0 data-[state=open]:!animate-none data-[state=closed]:!animate-none"
          >
            <DialogHeader>
              <DialogTitle className="text-white text-xl font-semibold">Historial de Cortes</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {bookingHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Scissors className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400 text-lg">No tienes cortes registrados aún</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-gray-400 text-sm">Vista:</span>
                      {['day', 'week', 'month', 'year'].map((view) => (
                        <Button
                          key={view}
                          size="sm"
                          variant={historyView === view ? 'default' : 'outline'}
                          className={historyView === view ? 'bg-amber-500 text-black hover:bg-amber-600' : 'border-gray-600 text-gray-300 hover:bg-gray-800'}
                          onClick={() => { setHistoryView(view); setHistoryExpanded(new Set()); }}
                        >
                          {view === 'day' && 'Por día'}
                          {view === 'week' && 'Por semana'}
                          {view === 'month' && 'Por mes'}
                          {view === 'year' && 'Por año'}
                        </Button>
                      ))}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-800">
                            Columnas
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-48 bg-amber-400" align="start">
                          <div className="px-2 py-2 text-xs uppercase text-black border-b border-amber-600">Mostrar columnas</div>
                          {[{ id: 'fecha', label: 'Fecha' }, { id: 'lugar', label: 'Lugar' }, { id: 'barbero', label: 'Barbero' }, { id: 'servicio', label: 'Servicio' }, { id: 'cobro', label: 'Cobro' }].map((col) => (
                            <DropdownMenuItem key={col.id} className="cursor-pointer focus:bg-amber-500 text-black" onSelect={(e) => e.preventDefault()}>
                              <label className="flex items-center gap-2 cursor-pointer w-full py-1">
                                <Checkbox checked={historyColumnsVisible[col.id] !== false} onCheckedChange={(c) => setHistoryColumnsVisible((prev) => ({ ...prev, [col.id]: !!c }))} className="border-gray-800" />
                                <span>{col.label}</span>
                              </label>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="text-amber-400 font-semibold text-lg">
                      Total: ${(bookingHistory.filter((i) => i.status === 'completed').reduce((s, i) => s + (Number(i.price || i.max_price || i.accepted_price || 0) || 0), 0)).toLocaleString('es-CL')}
                    </div>
                  </div>
                  <div className="overflow-x-auto space-y-2">
                    {historialGrouped.length === 0 ? (
                      <p className="text-gray-400 text-center py-8">No hay cortes completados</p>
                    ) : (
                      historialGrouped.map((group) => {
                        const isOpen = historyExpanded.has(group.key);
                        const groupTotal = group.items.reduce((s, i) => s + (Number(i.price || i.max_price || i.accepted_price || 0) || 0), 0);
                        const cols = [{ id: 'fecha', label: 'Fecha' }, { id: 'lugar', label: 'Lugar' }, { id: 'barbero', label: 'Barbero' }, { id: 'servicio', label: 'Servicio' }, { id: 'cobro', label: 'Cobro' }].filter((c) => historyColumnsVisible[c.id] !== false);
                        const formatHistorialFecha = (item, view) => {
                          const v = item.created_at || item.date;
                          if (view === 'day') return formatTimeWithTZ(v, { hour: '2-digit', minute: '2-digit' });
                          if (view === 'week' || view === 'month') return `${formatDateWithTZ(v, { day: 'numeric' })} ${formatTimeWithTZ(v, { hour: '2-digit', minute: '2-digit' })}`;
                          if (view === 'year') return formatDateWithTZ(v, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                          return formatDateWithTZ(v, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                        };
                        return (
                          <Collapsible
                            key={group.key}
                            open={isOpen}
                            onOpenChange={() => setHistoryExpanded((prev) => { const n = new Set(prev); if (n.has(group.key)) n.delete(group.key); else n.add(group.key); return n; })}
                          >
                            <div className="rounded-lg border border-gray-700 bg-gray-800/50 overflow-hidden">
                              <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-4 text-left hover:bg-gray-800 transition-colors">
                                {isOpen ? <ChevronDown className="w-4 h-4 text-amber-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-amber-400 shrink-0" />}
                                <span className="text-white font-medium">{group.label}</span>
                                <span className="text-gray-400 text-sm">({group.items.length} {group.items.length === 1 ? 'corte' : 'cortes'})</span>
                                <span className="text-amber-400 text-sm ml-auto">${groupTotal.toLocaleString('es-CL')}</span>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="border-t border-gray-700">
                                  <div className="grid gap-4 pb-2 pt-1 px-4 text-gray-400 font-semibold text-xs uppercase tracking-wide" style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}>
                                    {cols.map((c) => <div key={c.id} className={c.id === 'cobro' ? 'text-right' : ''}>{c.label}</div>)}
                                  </div>
                                  {group.items.map((item, index) => (
                                    <div
                                      key={item.id || index}
                                      role="button"
                                      tabIndex={0}
                                      className="grid gap-4 py-2 px-4 border-t border-gray-800 hover:bg-gray-800/50 transition-colors cursor-pointer"
                                      style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}
                                      onClick={() => setHistorialActionItem(item)}
                                      onKeyDown={(e) => e.key === 'Enter' && setHistorialActionItem(item)}
                                    >
                                      {cols.map((c) => {
                                        if (c.id === 'fecha') return <div key={c.id} className="text-white text-sm">{formatHistorialFecha(item, historyView)}</div>;
                                        if (c.id === 'lugar') return (
                                          <div key={c.id} className="min-w-0">
                                            <p className="text-white text-sm truncate">{item.barbershop?.name || item.barbershop_name || 'Barbería'}</p>
                                            {item.barbershop?.address && <p className="text-gray-500 text-xs truncate">{item.barbershop.address}</p>}
                                          </div>
                                        );
                                        if (c.id === 'barbero') return <div key={c.id} className="text-white text-sm truncate">{item.barber?.name || item.barber_name || item.barbershop?.barber_name || '—'}</div>;
                                        if (c.id === 'servicio') return (
                                          <div key={c.id} className="flex items-center gap-2">
                                            <span className="text-white text-sm">{item.service || 'Corte de pelo'}</span>
                                            {item.type === 'quick_cut' && <Badge variant="outline" className="text-amber-400 border-amber-400 text-xs px-1.5 py-0">⚡</Badge>}
                                          </div>
                                        );
                                        if (c.id === 'cobro') return <div key={c.id} className="text-right"><span className="text-amber-400 font-semibold text-sm">${(item.price || item.max_price || item.accepted_price || 0).toLocaleString('es-CL')}</span></div>;
                                        return null;
                                      })}
                                    </div>
                                  ))}
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de acción por corte (Hablar / Reseña / Ayuda) */}
        <Dialog open={!!historialActionItem} onOpenChange={(open) => !open && setHistorialActionItem(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white">¿Qué deseas hacer?</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-2">
              <Button
                className="w-full justify-start bg-gray-800 hover:bg-gray-700 text-white border border-gray-600"
                onClick={() => {
                  if (historialActionItem) {
                    setSelectedBookingChat({
                      id: historialActionItem.id,
                      type: historialActionItem.type || 'booking',
                      service: historialActionItem.service,
                      date: historialActionItem.date || historialActionItem.created_at,
                      barbershop: historialActionItem.barbershop,
                      barber: historialActionItem.barber || { id: historialActionItem.barber_id, name: historialActionItem.barber_name || historialActionItem.barber?.name || 'Barbero' },
                      price: historialActionItem.price || historialActionItem.max_price || historialActionItem.accepted_price
                    });
                    setShowHistory(false);
                    setHistorialActionItem(null);
                  }
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Hablar con barbero
              </Button>
              <Button
                className="w-full justify-start bg-gray-800 hover:bg-gray-700 text-white border border-gray-600"
                onClick={() => {
                  if (historialActionItem) {
                    setCompletedQuickCut({
                      barber: {
                        name: historialActionItem.barber?.name || historialActionItem.barber_name || historialActionItem.barbershop?.name || 'Barbero',
                        barbershop_id: historialActionItem.barbershop?.id || historialActionItem.barbershop_id
                      },
                      service: historialActionItem.service,
                      price: historialActionItem.price || historialActionItem.max_price
                    });
                    setShowReviewModal(true);
                    setShowHistory(false);
                    setHistorialActionItem(null);
                  }
                }}
              >
                <Star className="w-4 h-4 mr-2" />
                Dejar reseña
              </Button>
              <Button
                className="w-full justify-start bg-gray-800 hover:bg-gray-700 text-white border border-gray-600"
                onClick={() => {
                  if (historialActionItem) {
                    setSelectedBookingChat({
                      id: 'admin-help',
                      type: 'admin_help',
                      service: 'Ayuda',
                      date: new Date().toISOString(),
                      barbershop: null,
                      barber: { id: 'admin', name: 'Administración' },
                      price: null,
                      initialMessage: 'Por favor cuéntanos en qué te podemos ayudar. Los administradores verán el mensaje y te darán una solución.'
                    });
                    setShowHistory(false);
                    setHistorialActionItem(null);
                  }
                }}
              >
                <Info className="w-4 h-4 mr-2" />
                Ayuda
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Settings Modal */}
        <Dialog
          open={showSettings}
          onOpenChange={(open) => {
            setShowSettings(open);
            if (open) {
              setEditingProfile(true);
              setUserSettings({
                name: user?.name || '',
                email: user?.email || '',
                phone: user?.phone || '',
                age: user?.age || '',
                address: user?.address || '',
                hairPreference: user?.hairPreference || '',
                notifications: user?.notifications || { offers: true, reminders: true }
              });
            }
          }}
        >
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Editar perfil</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Nombre</label>
                <Input
                  value={userSettings.name}
                  onChange={(e) => setUserSettings({...userSettings, name: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Email</label>
                <Input
                  value={user?.email || ''}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  readOnly
                />
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Teléfono</label>
                <Input
                  value={userSettings.phone}
                  onChange={(e) => setUserSettings({...userSettings, phone: e.target.value})}
                  placeholder="Ej: +56 9 1234 5678"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Edad (opcional)</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={userSettings.age}
                  onChange={(e) => setUserSettings({...userSettings, age: e.target.value.replace(/\D/g, '')})}
                  placeholder="Ej: 25"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Dirección/Comuna (opcional)</label>
                <Input
                  value={userSettings.address}
                  onChange={(e) => setUserSettings({...userSettings, address: e.target.value})}
                  placeholder="Ej: Las Condes, Santiago"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Preferencia de Corte</label>
                  <Select
                    value={userSettings.hairPreference}
                    onValueChange={(value) => setUserSettings({...userSettings, hairPreference: value})}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Selecciona tu estilo preferido" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="corto">Corto</SelectItem>
                      <SelectItem value="degrade">Degradé</SelectItem>
                      <SelectItem value="largo">Largo</SelectItem>
                      <SelectItem value="teñido">Con teñido</SelectItem>
                      <SelectItem value="clasico">Clásico</SelectItem>
                      <SelectItem value="moderno">Moderno</SelectItem>
                    </SelectContent>
                  </Select>
              </div>
            
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Notificaciones</label>
                <div className="space-y-2">
                  <label className="flex items-center text-white text-sm">
                    <input 
                      type="checkbox" 
                      className="mr-2" 
                      checked={userSettings.notifications?.offers || true}
                      onChange={(e) => setUserSettings({
                        ...userSettings, 
                        notifications: {
                          ...userSettings.notifications,
                          offers: e.target.checked
                        }
                      })}
                    />
                    Recibir ofertas especiales
                  </label>
                  <label className="flex items-center text-white text-sm">
                    <input 
                      type="checkbox" 
                      className="mr-2" 
                      checked={userSettings.notifications?.reminders || true}
                      onChange={(e) => setUserSettings({
                        ...userSettings, 
                        notifications: {
                          ...userSettings.notifications,
                          reminders: e.target.checked
                        }
                      })}
                    />
                    Recordatorios de citas
                  </label>
                </div>
              </div>
            
              <div className="flex gap-2">
                    <Button 
                      onClick={async () => {
                        try {
                          await axios.put(`${BACKEND_URL}/api/auth/profile`, userSettings, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                          });
                          setEditingProfile(false);
                          setSuccess('Perfil actualizado correctamente');
                          setUser({...user, ...userSettings});
                      setShowSettings(false);
                        } catch (error) {
                          setError('Error al actualizar perfil');
                        }
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </Button>
                    <Button 
                  onClick={() => setShowSettings(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );

    const getStatusMeta = (value) => {
      const status = (value || '').toLowerCase();
      if (status === 'pending') {
        return { label: 'Pendiente', className: 'text-amber-300 border-amber-500/40 bg-amber-900/20', icon: Clock };
      }
      if (status === 'accepted') {
        return { label: 'Aceptada', className: 'text-green-300 border-green-500/40 bg-green-900/20', icon: CheckCircle };
      }
      if (status === 'completed') {
        return { label: 'Completada', className: 'text-emerald-300 border-emerald-500/40 bg-emerald-900/20', icon: CheckCircle };
      }
      if (status === 'cancelled') {
        return { label: 'Cancelada', className: 'text-red-300 border-red-500/60 bg-red-900/30', icon: XCircle };
      }
      if (status === 'rejected') {
        return { label: 'Rechazada', className: 'text-red-300 border-red-500/60 bg-red-900/30', icon: XCircle };
      }
      return { label: 'Estado', className: 'text-gray-300 border-gray-500/40 bg-gray-800/40', icon: AlertCircle };
    };

    const BarberCalendar = ({ barberAcceptedQuickCuts, barberCompletedQuickCuts, barberBookings, quickCutRequests, loadBarberData, user, BACKEND_URL, setSelectedBookingChat, loadClientProfileForBarber, barbershopData }) => {
      const [selectedAppointment, setSelectedAppointment] = useState(null);
      const [calendarView, setCalendarView] = useState('week');
      const [calendarDate, setCalendarDate] = useState(new Date());
      const [showPast, setShowPast] = useState(false);
      const [showToday, setShowToday] = useState(true);
      const [showNext, setShowNext] = useState(false);
      const [showCancelled, setShowCancelled] = useState(false);
      
      // Citas solo aceptadas o completadas (pendientes van a Solicitudes)
      const calendarAppointments = [
        ...barberBookings
          .filter(b => ['accepted', 'completed', 'cancelled'].includes(b.status))
          .map(b => ({...b, type: 'booking'})),
        ...(barberAcceptedQuickCuts || []).map(qc => ({...qc, type: 'quick_cut', status: qc.status || 'accepted'})),
        ...(barberCompletedQuickCuts || []).map(qc => ({...qc, type: 'quick_cut', status: qc.status || 'completed'}))
      ];

      // Eliminar duplicados basándose en el id y type
      const uniqueAppointments = calendarAppointments.filter((appointment, index, self) =>
        index === self.findIndex(a => a.id === appointment.id && a.type === appointment.type)
      );

      const handleCompleteQuickCut = async (quickCutId) => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/quick-cuts/${quickCutId}/complete`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          showNotification('✅ Completado', 'Quick cut marcado como completado', 'success');
          loadBarberData();
        } catch (error) {
          console.error('Error completing quick cut:', error);
          showNotification('❌ Error', 'No se pudo completar el servicio', 'destructive');
        }
      };

      const handleMarkComplete = async (appointment) => {
        try {
          const token = localStorage.getItem('token');
          
          if (appointment.type === 'booking') {
            await axios.put(`${BACKEND_URL}/api/bookings/${appointment.id}/status`, {
              status: 'completed'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            showNotification('✅ Completado', 'Reserva marcada como completada', 'success');
          } else {
            await axios.post(`${BACKEND_URL}/api/quick-cuts/${appointment.id}/complete`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            showNotification('✅ Completado', 'Quick cut marcado como completado', 'success');
          }
          
          setSelectedAppointment(null);
          loadBarberData();
        } catch (error) {
          console.error('Error completing appointment:', error);
          showNotification('❌ Error', 'No se pudo marcar como completada', 'destructive');
        }
      };

      const getItemDate = (item) => {
        const value = item.completed_at || item.date || item.created_at;
        return value ? new Date(value) : new Date();
      };

      const startOfDay = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

      const startOfWeek = (value) => {
        const date = startOfDay(value);
        const day = (date.getDay() + 6) % 7; // Monday start
        date.setDate(date.getDate() - day);
        return date;
      };

      const startOfMonth = (value) => new Date(value.getFullYear(), value.getMonth(), 1);

      const addDays = (value, days) => {
        const date = new Date(value);
        date.setDate(date.getDate() + days);
        return date;
      };

      const getRange = () => {
        if (calendarView === 'day') {
          const start = startOfDay(calendarDate);
          const end = addDays(start, 1);
          return { start, end };
        }
        const start = startOfWeek(calendarDate);
        const end = addDays(start, 7);
        return { start, end };
      };

      const { start: rangeStart, end: rangeEnd } = getRange();

      const calendarItems = uniqueAppointments
        .map(item => ({ ...item, _date: getItemDate(item) }))
        .filter(item => item._date >= rangeStart && item._date < rangeEnd)
        .filter(item => (showCancelled ? true : (item.status || 'accepted') !== 'cancelled'))
        .filter(item => {
          const now = new Date();
          const itemDay = startOfDay(item._date);
          const today = startOfDay(now);
          const isToday = itemDay.getTime() === today.getTime();
          const isPast = item._date < now;
          const isNext = item._date > now;

          const matchesPast = showPast && (isPast || (isToday && item._date < now));
          const matchesToday = showToday && isToday;
          const matchesNext = showNext && (isNext || (isToday && item._date > now));
          const hasTimeFilter = showPast || showToday || showNext;

          return hasTimeFilter ? (matchesPast || matchesToday || matchesNext) : true;
        })
        .sort((a, b) => a._date - b._date);

      const actionableAppointments = uniqueAppointments
        .filter(item => ['accepted', 'pending'].includes(item.status || 'accepted'))
        .map(item => ({ ...item, _date: getItemDate(item) }))
        .sort((a, b) => a._date - b._date);

      const groupedItems = calendarItems.reduce((acc, item) => {
        const key = startOfDay(item._date).toISOString();
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      const formatDateLabel = (value) => formatDateWithTZ(value, {
        weekday: 'long',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

      const formatTimeLabel = (value) => formatTimeWithTZ(value, {
        hour: '2-digit',
        minute: '2-digit'
      });

      const getBarbershopName = (item) => item.barbershop?.name || barbershopData?.name || 'Barbería';
      const getBarbershopPlace = (item) => {
        if (item.service_location === 'home') return 'A domicilio';
        return item.barbershop?.address || barbershopData?.address || 'En el local';
      };

      const registerCancelledBooking = (bookingId, barbershopId) => {
        if (!bookingId || !barbershopId) return;
        const now = Date.now();
        setRecentCancelledBookings((prev) => ({
          ...prev,
          [bookingId]: { barbershopId, timestamp: now }
        }));
      };

      const handleCancelAppointment = async (appointment) => {
        const confirmed = await requestConfirmation({
          title: 'Cancelar cita',
          description: '¿Quieres cancelar la cita? Esto influirá en tu puntuación.',
          confirmText: 'Cancelar',
          variant: 'destructive'
        });
        if (!confirmed) return;
        try {
          const token = localStorage.getItem('token');
          if (appointment.type === 'booking') {
            await axios.put(`${BACKEND_URL}/api/bookings/${appointment.id}/status`, {
              status: 'cancelled'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (appointment.client_id) {
              await axios.post(`${BACKEND_URL}/api/chat/send`, {
                service_id: appointment.id,
                recipient_id: appointment.client_id,
                message: 'Reserva cancelada',
                type: 'system',
                booking_id: appointment.id,
                barbershop_id: appointment.barbershop?.id || barbershopData?.id
              }, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
            }
          } else {
            await axios.post(`${BACKEND_URL}/api/quick-cuts/${appointment.id}/cancel-by-barber`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (appointment.client_id) {
              await axios.post(`${BACKEND_URL}/api/chat/send`, {
                service_id: appointment.id,
                recipient_id: appointment.client_id,
                message: 'Reserva cancelada',
                type: 'system',
                booking_id: appointment.id,
                barbershop_id: appointment.barbershop?.id || barbershopData?.id
              }, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
            }
          }

          if (appointment.type === 'booking') {
            registerCancelledBooking(appointment.id, appointment.barbershop?.id || barbershopData?.id);
          }
          showNotification('Reserva cancelada', 'La cita fue cancelada', 'info');
          loadBarberData();
        } catch (error) {
          console.error('Error canceling appointment:', error);
          showNotification('❌ Error', 'No se pudo cancelar la cita', 'destructive');
        }
      };

      const handlePrevRange = () => {
        if (calendarView === 'day') {
          setCalendarDate(addDays(calendarDate, -1));
          return;
        }
        setCalendarDate(addDays(calendarDate, -7));
      };

      const handleNextRange = () => {
        if (calendarView === 'day') {
          setCalendarDate(addDays(calendarDate, 1));
          return;
        }
        setCalendarDate(addDays(calendarDate, 7));
      };

      const rangeLabel = (() => {
        if (calendarView === 'day') {
          return formatDateLabel(calendarDate);
        }
        const weekStart = startOfWeek(calendarDate);
        const weekEnd = addDays(weekStart, 6);
        return `${formatDateLabel(weekStart)} - ${formatDateLabel(weekEnd)}`;
      })();

      const timeFiltersLabel = [
        showPast ? 'Anterior' : null,
        showToday ? 'Hoy' : null,
        showNext ? 'Siguiente' : null
      ].filter(Boolean).join(' · ') || 'Sin filtro';

      return (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Citas Aceptadas</h2>
            <p className="text-gray-400">Gestiona y completa tus servicios agendados</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Citas pendientes</h3>
              <span className="text-xs text-gray-400">
                {actionableAppointments.length} en cola
              </span>
            </div>
            {actionableAppointments.length === 0 ? (
              <Card className="bg-gray-900 border-gray-700">
                <CardContent className="p-8 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-white text-xl mb-2">No hay citas pendientes</h3>
                  <p className="text-gray-400">Las citas con acciones aparecerán aquí</p>
                </CardContent>
              </Card>
            ) : (
              actionableAppointments.map((appointment, index) => (
                <SwipeableListItem
                  key={`${appointment.type}-${appointment.id}-${index}`}
                  enabled={isMobile}
                  onSwipeLeft={() => handleCancelAppointment(appointment)}
                >
                  <Card 
                    className={appointment.type === 'quick_cut' && barberAcceptedQuickCuts?.some(qc => qc.id === appointment.id)
                      ? "bg-gradient-to-br from-green-900/30 to-gray-900 border-green-500"
                      : "bg-gray-900 border-gray-700"
                    }
                  >
                    <CardContent className="p-4 relative">
                      {!isMobile && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleCancelAppointment(appointment)}
                          className="absolute right-2 top-2 h-7 w-7 text-red-400 hover:text-white hover:bg-red-600/80"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        {(() => {
                          const statusMeta = getStatusMeta(appointment.status || 'accepted');
                          const StatusIcon = statusMeta.icon;
                          return (
                            <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border ${statusMeta.className} mb-2`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusMeta.label}
                            </div>
                          );
                        })()}
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-white font-medium">{appointment.service}</h3>
                          <Badge 
                            variant="outline" 
                            className={appointment.type === 'quick_cut' ? "text-amber-400 border-amber-400" : "text-blue-400 border-blue-400"}
                          >
                            {appointment.type === 'quick_cut' ? '⚡ Quick Cut' : '📅 Reserva'}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 text-sm mb-1">
                          <strong>Cliente:</strong>{' '}
                          <button
                            onClick={() => {
                              if (loadClientProfileForBarber && appointment.client_id) {
                                loadClientProfileForBarber(appointment.client_id, appointment.client_name);
                              }
                            }}
                            className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
                          >
                            {appointment.client_name}
                          </button>
                        </p>
                        
                        {appointment.client_address && (
                          <p className="text-gray-400 text-xs mb-1">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            {appointment.client_address}
                          </p>
                        )}
                        
                        <p className="text-amber-400 text-sm font-medium mt-2">
                          Precio: {formatClp(appointment.price || appointment.max_price || appointment.accepted_price)}
                        </p>
                        
                        {appointment.preferred_time && (
                          <p className="text-blue-400 text-xs mt-1">
                            ⏱️ {
                              appointment.preferred_time === 'asap' ? 'Lo antes posible' :
                              appointment.preferred_time === '30min' ? 'En 30 minutos' :
                              appointment.preferred_time === '1hour' ? 'En 1 hora' :
                              appointment.preferred_time === '2hours' ? 'En 2 horas' :
                              'Horario flexible'
                            }
                          </p>
                        )}
                        
                        {appointment.date && (
                          <p className="text-gray-400 text-sm mt-1">
                            📅 {formatDateTimeWithTZ(appointment.date, {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                        
                        {appointment.client_arrived && (
                          <Badge className="bg-amber-500 text-black mt-2">
                            <MapPin className="w-3 h-3 mr-1" />
                            El cliente ya llegó a tu barbería
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        onClick={() => {
                          if (setSelectedBookingChat) {
                            setSelectedBookingChat({
                              id: appointment.id,
                              status: appointment.status,
                              service: appointment.service,
                              type: appointment.type === 'quick_cut' ? 'quick_cut' : 'booking',
                              client_id: appointment.client_id,
                              client_name: appointment.client_name,
                              price: appointment.price || appointment.max_price || appointment.accepted_price,
                              barbershop: appointment.barbershop,
                              date: appointment.created_at || appointment.date
                            });
                          }
                        }}
                        className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Chatear con Cliente
                    </Button>
                      <Button
                        onClick={() => {
                          if (appointment.type === 'quick_cut') {
                            handleCompleteQuickCut(appointment.id);
                          } else {
                            handleMarkComplete(appointment);
                          }
                        }}
                        className="w-full sm:flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Completar
                      </Button>
                      {(appointment.status || 'accepted') === 'accepted' && (
                        <Button
                          onClick={() => handleCancelAppointment(appointment)}
                          variant="outline"
                          className="w-full sm:flex-1 border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                </SwipeableListItem>
              ))
            )}
          </div>

          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-4 space-y-4">
              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-3 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto border-gray-700 text-gray-200">
                        Vista y filtros
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64">
                      <div className="px-2 py-2 text-xs uppercase text-gray-500">Vista</div>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setCalendarView('day')}>
                        Día {calendarView === 'day' ? '✓' : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setCalendarView('week')}>
                        Semana {calendarView === 'week' ? '✓' : ''}
                      </DropdownMenuItem>
                      <div className="px-2 py-2 text-xs uppercase text-gray-500">Filtros</div>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setShowPast((prev) => !prev)}>
                        Anterior {showPast ? '✓' : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setShowToday((prev) => !prev)}>
                        Hoy {showToday ? '✓' : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setShowNext((prev) => !prev)}>
                        Siguiente {showNext ? '✓' : ''}
                      </DropdownMenuItem>
                      <DropdownMenuItem className="cursor-pointer" onClick={() => setShowCancelled((prev) => !prev)}>
                        Canceladas {showCancelled ? '✓' : ''}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="text-xs text-gray-400">
                    {calendarView === 'day' ? 'Vista: Día' : 'Vista: Semana'} · {timeFiltersLabel}
                    {showCancelled ? ' · Canceladas' : ''}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-400">
                <span>{rangeLabel}</span>
                <span>{timeFiltersLabel}</span>
              </div>

              {calendarItems.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  No hay solicitudes en este período.
                </div>
              ) : calendarView === 'day' ? (
                <div className="flex gap-3">
                  <div className="flex flex-col gap-6 text-xs text-gray-400 pt-2">
                    {Array.from({ length: 13 }).map((_, idx) => {
                      const hour = 8 + idx;
                      return (
                        <div key={hour} className="h-24 flex items-start">
                          {hour.toString().padStart(2, '0')}:00
                        </div>
                      );
                    })}
                  </div>
                  <div className="relative flex-1 h-[1560px] rounded-lg border border-gray-800 bg-gray-950/40">
                    {Array.from({ length: 13 }).map((_, idx) => {
                      const top = idx * 120;
                      return (
                        <div key={idx} className="absolute left-0 right-0 border-t border-gray-800" style={{ top }} />
                      );
                    })}
                    {calendarItems.map((item) => {
                      const dayStart = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate(), 8, 0, 0);
                      const minutesFromStart = Math.max(0, Math.min(12 * 60, Math.floor((item._date - dayStart) / 60000)));
                      const top = minutesFromStart * 2;
                      const durationMinutes = item.duration || 30;
                      const height = Math.max(48, durationMinutes * 2);
                      return (
                        <div
                          key={`${item.type}-${item.id}`}
                          className="absolute left-2 right-2 rounded-lg border border-gray-700 bg-gray-900/80 p-2 text-xs text-gray-200"
                          style={{ top, height }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-amber-300">{item.service || 'Corte de pelo'}</span>
                            <span className="text-gray-400">{item.type === 'quick_cut' ? 'Rápido' : 'Reserva'}</span>
                          </div>
                          <div className="text-white font-medium">
                            {item.client_name || item.client?.name || 'Cliente'}
                          </div>
                          <div className="text-gray-400 text-[11px]">
                            {formatTimeLabel(item._date)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(groupedItems)
                    .sort()
                    .map((key) => {
                      const dayItems = groupedItems[key];
                      const dayDate = new Date(key);
                      return (
                        <div key={key} className="rounded-lg border border-gray-800 bg-gray-950/40 p-3">
                          <div className="text-amber-300 text-sm font-semibold mb-2">
                            {formatDateLabel(dayDate)}
                          </div>
                          <div className="space-y-2">
                            {dayItems.map((item, index) => {
                              const status = item.status || 'accepted';
                              const statusMeta = getStatusMeta(status);
                              const StatusIcon = statusMeta.icon;

                              return (
                                <SwipeableListItem
                                  key={`${item.type}-${item.id || index}`}
                                  enabled={isMobile && (item.status || 'accepted') === 'accepted'}
                                  onSwipeLeft={() => handleCancelAppointment(item)}
                                  className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-gray-900/70 border border-gray-800 rounded-lg px-3 py-2"
                                >
                                  {!isMobile && (item.status || 'accepted') === 'accepted' && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleCancelAppointment(item)}
                                      className="self-end sm:absolute sm:right-2 sm:top-2 h-7 w-7 text-red-400 hover:text-white hover:bg-red-600/80"
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <div className="text-gray-300 text-sm">
                                    {formatTimeLabel(item._date)}
                                  </div>
                                  <div className="text-white text-sm font-medium">
                                  {item.client_id ? (
                                    <button
                                      type="button"
                                      onClick={() => loadClientProfileForBarber(item.client_id, item.client_name || item.client?.name || 'Cliente')}
                                      className="text-blue-400 hover:text-blue-300 underline"
                                    >
                                      {item.client_name || item.client?.name || 'Cliente'}
                                    </button>
                                  ) : (
                                    item.client_name || item.client?.name || 'Cliente'
                                  )}
                                  </div>
                                  <div className="text-gray-300 text-sm">
                                    {item.service || 'Corte de pelo'}
                                  </div>
                                  <div className="text-gray-400 text-xs">
                                    {getBarbershopName(item)} · {getBarbershopPlace(item)}
                                  </div>
                                  <div className="text-amber-300 text-xs">
                                    {item.type === 'quick_cut' ? 'Rápido' : 'Reserva'}
                                  </div>
                                  <div className={`text-xs px-2 py-0.5 rounded-md border inline-flex items-center gap-1 ${statusMeta.className}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusMeta.label}
                                  </div>
                                </SwipeableListItem>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal de Acciones de Cita */}
          {selectedAppointment && (
            <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
              <DialogContent className="modal-premium text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-premium-heading text-xl mb-2">
                    Gestionar Cita
                  </DialogTitle>
                </DialogHeader>

                <div className="space-y-3 mt-4">
                  {/* Info de la cita */}
                  <div className="card-premium p-4">
                    <h4 className="text-white font-medium mb-2">{selectedAppointment.service}</h4>
                    <p className="text-gray-400 text-sm">
                      Cliente:{' '}
                      {selectedAppointment.client_id ? (
                        <button
                          type="button"
                          onClick={() => loadClientProfileForBarber(selectedAppointment.client_id, selectedAppointment.client_name)}
                          className="text-blue-400 hover:text-blue-300 underline"
                        >
                          {selectedAppointment.client_name}
                        </button>
                      ) : (
                        selectedAppointment.client_name
                      )}
                    </p>
                    <p className="text-premium-gold text-sm mt-1">
                      ${(selectedAppointment.price || selectedAppointment.max_price)?.toLocaleString()}
                    </p>
                  </div>

                  {/* Botones de acción */}
                  <Button
                    onClick={() => handleMarkComplete(selectedAppointment)}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Completada
                  </Button>

                  <Button
                    onClick={() => {
                      setSelectedBookingChat(selectedAppointment);
                      setSelectedAppointment(null);
                    }}
                    className="w-full btn-premium-gold"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat
                  </Button>

                  <Button
                    onClick={() => setSelectedAppointment(null)}
                    className="w-full btn-premium-silver"
                  >
                    Cancelar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      );
    };

    const BarberRequests = ({
      quickCutRequests,
      bookingRequests,
      barberBookings,
      barberAcceptedQuickCuts,
      loadBarberData,
      user,
      BACKEND_URL,
      loadClientProfileForBarber,
      isMobile,
      setActiveTab,
      mobileStage,
      setMobileStage,
      mobileRequest,
      setMobileRequest,
      mobileAcceptedUntil,
      setMobileAcceptedUntil
    }) => {
      // Combinar quick cuts y reservas tradicionales PENDIENTES (solo las que están pendientes)
      const allRequests = [
        ...quickCutRequests.filter(req => req.status === 'pending').map(req => ({ ...req, type: 'quick_cut' })),
        ...barberBookings.filter(booking => booking.status === 'pending').map(booking => ({ ...booking, type: 'traditional_booking' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      const acceptedRequests = [
        ...(barberAcceptedQuickCuts || []).map(qc => ({ ...qc, type: 'quick_cut', status: qc.status || 'accepted' })),
        ...barberBookings
          .filter(booking => booking.status === 'accepted')
          .map(booking => ({ ...booking, type: 'traditional_booking', status: booking.status || 'accepted' }))
      ].sort((a, b) => new Date(a.date || a.created_at || 0) - new Date(b.date || b.created_at || 0));

      useEffect(() => {
        if (!isMobile) return;
        const pendingQuickCuts = allRequests.filter(r => r.type === 'quick_cut');
        if (pendingQuickCuts.length === 0) {
          if (mobileStage === 'incoming') {
            setMobileStage('idle');
            setMobileRequest(null);
          }
          return;
        }
        if (!mobileRequest || mobileRequest.id !== pendingQuickCuts[0].id) {
          setMobileRequest(pendingQuickCuts[0]);
          setMobileStage('incoming');
        }
      }, [isMobile, allRequests, mobileRequest, mobileStage]);

      const handleCompleteQuickCut = async (quickCutId) => {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${BACKEND_URL}/api/quick-cuts/${quickCutId}/complete`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          showNotification('✅ Completado', 'Quick cut marcado como completado', 'success');
          loadBarberData();
        } catch (error) {
          console.error('Error completing quick cut:', error);
          showNotification('❌ Error', 'No se pudo completar el servicio', 'destructive');
        }
      };
    
      const getPreferredTimeMinutes = (value) => {
        if (value === '30min') return 30;
        if (value === '1hour') return 60;
        if (value === '2hours') return 120;
        return 15;
      };

      const getPreferredTimeLabel = (value) => {
        if (value === '30min') return '30 min';
        if (value === '1hour') return '1 hora';
        if (value === '2hours') return '2 horas';
        return '15 min';
      };

      const formatTimeLeft = (expiresAt) => {
        const now = Date.now();
        const expires = new Date(expiresAt).getTime();
        const timeLeft = Math.max(0, Math.floor((expires - now) / 1000));
      
        const hours = Math.floor(timeLeft / 3600);
        const minutes = Math.floor((timeLeft % 3600) / 60);
        const seconds = timeLeft % 60;

        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
      };

      const showCalendarFallback = (calendarUrl) => {
        if (!calendarUrl) return;
        confirmResolvedRef.current = false;
        setConfirmDialogData({
          title: 'Agregar a calendario',
          description: 'Si no se abrió automáticamente, agrega el evento a tu calendario aquí.',
          confirmText: 'Agregar a calendario',
          variant: 'default',
          onConfirm: () => openCalendarUrl(calendarUrl),
          onCancel: () => {}
        });
        setShowConfirmDialog(true);
      };

      const handleAcceptRequest = async (request) => {
        try {
          const token = localStorage.getItem('token');
        
          if (request.type === 'quick_cut') {
            // Accept quick cut request
            const response = await axios.post(`${BACKEND_URL}/api/quick-cuts/${request.id}/respond`, {
              accept: true
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          
            showNotification('✅ Solicitud Aceptada', `Corte rápido de ${request.client_name} aceptado`, 'success');
            const calendarUrl =
              response?.data?.calendar_url ||
              `${BACKEND_URL}/api/quick-cuts/${request.id}/calendar`;
            if (calendarUrl) {
              const ok = await downloadCalendarFile(calendarUrl, `cortate-quick-cut-${request.id}.ics`);
              if (!ok) {
                openCalendarUrl(calendarUrl);
              }
              showCalendarFallback(calendarUrl);
            }

            if (isMobile) {
              const preferredMinutes = getPreferredTimeMinutes(request.preferred_time);
              setMobileAcceptedUntil(Date.now() + preferredMinutes * 60 * 1000);
              setMobileStage('accepted');
            }
          } else {
            // Accept traditional booking
            const response = await axios.put(`${BACKEND_URL}/api/bookings/${request.id}/status`, {
              status: 'accepted'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          
            showNotification('✅ Reserva Aceptada', `Reserva de ${request.client_name} aceptada`, 'success');
            const calendarUrl =
              response?.data?.calendar_url ||
              `${BACKEND_URL}/api/bookings/${request.id}/calendar`;
            if (calendarUrl) {
              const ok = await downloadCalendarFile(calendarUrl, `cortate-booking-${request.id}.ics`);
              if (!ok) {
                openCalendarUrl(calendarUrl);
              }
              showCalendarFallback(calendarUrl);
            }
          }
        
          // Refresh data
          loadBarberData();
        
        } catch (error) {
          console.error('Error accepting request:', error);
          showNotification('❌ Error', 'No se pudo aceptar la solicitud', 'destructive');
        }
      };

      const handleRejectRequest = async (request) => {
        try {
          const token = localStorage.getItem('token');
        
          if (request.type === 'quick_cut') {
            // Reject quick cut request
            await axios.post(`${BACKEND_URL}/api/quick-cuts/${request.id}/respond`, {
              accept: false
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          
            showNotification('❌ Solicitud Rechazada', `Corte rápido de ${request.client_name} rechazado`, 'info');
            if (isMobile && mobileRequest?.id === request.id) {
              setMobileStage('idle');
              setMobileRequest(null);
            }
          } else {
            // Reject traditional booking
            await axios.put(`${BACKEND_URL}/api/bookings/${request.id}/status`, {
              status: 'rejected'
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          
            showNotification('❌ Reserva Rechazada', `Reserva de ${request.client_name} rechazada`, 'info');
          }
        
          // Refresh data
          loadBarberData();
        
        } catch (error) {
          console.error('Error rejecting request:', error);
          showNotification('❌ Error', 'No se pudo rechazar la solicitud', 'destructive');
        }
      };

      return (
        <div className="space-y-6">
          {isMobile && mobileStage === 'incoming' && mobileRequest && (
            <div className="fixed inset-0 z-50 bg-black/95 text-white flex flex-col justify-between p-6">
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <span className="sr-only">{countdownTick}</span>
                <div className="w-20 h-20 rounded-full border-2 border-amber-400/70 flex items-center justify-center mb-4 animate-pulse">
                  <Zap className="w-8 h-8 text-amber-400" />
              </div>
                <h2 className="text-2xl font-bold mb-2">Nueva solicitud rápida</h2>
                <p className="text-gray-400 mb-6">
                  {mobileRequest.client_name || 'Cliente'} · {mobileRequest.service || 'Corte de pelo'}
                </p>
                <div className="bg-gray-900 border border-amber-500/40 rounded-2xl px-6 py-4">
                  <p className="text-xs text-amber-200 uppercase tracking-wide">Tiempo restante</p>
                  <p className="text-3xl font-bold text-amber-300 font-mono">
                    {formatTimeLeft(mobileRequest.expires_at)}
                  </p>
                      </div>
                    </div>
              <div className="space-y-3">
                      <Button
                  onClick={() => handleAcceptRequest(mobileRequest)}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Aceptar solicitud
                      </Button>
                      <Button
                  onClick={() => handleRejectRequest(mobileRequest)}
                  variant="outline"
                  className="w-full border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                      >
                  Rechazar
                      </Button>
                    </div>
            </div>
          )}

          {isMobile && mobileStage === 'accepted' && mobileRequest && (
            <div className="fixed inset-0 z-50 bg-black/95 text-white flex flex-col justify-between p-6">
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <span className="sr-only">{countdownTick}</span>
                <div className="w-20 h-20 rounded-full bg-green-600/20 border border-green-500/60 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Solicitud aceptada</h2>
                <p className="text-gray-400 mb-6">
                  {mobileRequest.client_name || 'Cliente'} · {mobileRequest.service || 'Corte de pelo'}
                </p>
                <div className="bg-gray-900 border border-green-500/40 rounded-2xl px-6 py-4">
                  <p className="text-xs text-green-200 uppercase tracking-wide">Cronómetro</p>
                  <p className="text-3xl font-bold text-green-300 font-mono">
                    {mobileAcceptedUntil ? formatTimeLeft(mobileAcceptedUntil) : '00m 00s'}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={() => setMobileStage('idle')}
                  className="w-full bg-amber-500 hover:bg-amber-700"
                >
                  Ver detalles en solicitudes
                </Button>
              </div>
            </div>
          )}

          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Solicitudes</h2>
            <p className="text-gray-400">Gestiona cortes rápidos y reservas tradicionales</p>
          </div>

          <div className="space-y-4">
            {acceptedRequests.length > 0 && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold text-white">Citas pendientes</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-400 text-amber-300 hover:bg-amber-500/10"
                    onClick={() => setActiveTab?.('calendar')}
                  >
                    Ver en Citas pendientes
                  </Button>
                </div>
                {acceptedRequests.map((request, index) => (
                  <Card key={`accepted-${request.type}-${request.id}-${index}`} className="bg-gray-900 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="text-white font-medium">
                              {request.service || 'Corte de pelo'}
                            </h4>
                            <Badge className={request.type === 'quick_cut' ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'}>
                              {request.type === 'quick_cut' ? '⚡ Corte rápido' : '📅 Reserva'}
                            </Badge>
                            <Badge className="bg-green-600 text-white">Aceptada</Badge>
                          </div>
                          <p className="text-gray-400 text-sm">
                            Cliente:{' '}
                            <button
                              onClick={() => {
                                loadClientProfileForBarber(request.client_id, request.client_name);
                              }}
                              className="text-blue-400 hover:text-blue-300 underline"
                            >
                              {request.client_name || 'Cliente'}
                            </button>
                          </p>
                          {request.date && (
                            <p className="text-gray-400 text-xs mt-1">
                              📅 {formatDateTimeWithTZ(request.date, {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-gray-700 text-gray-200"
                          onClick={() => setActiveTab?.('calendar')}
                        >
                          Ir a Citas pendientes
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Solicitudes nuevas</h3>
              {allRequests.length === 0 ? (
                <Card className="bg-gray-900 border-gray-700">
                  <CardContent className="p-8 text-center">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-white text-xl mb-2">No hay solicitudes pendientes</h3>
                    <p className="text-gray-400">Las nuevas solicitudes aparecerán aquí</p>
                  </CardContent>
                </Card>
              ) : (
                allRequests.map((request, index) => (
                <SwipeableListItem
                  key={`${request.type}-${request.id}-${index}`} 
                  enabled={isMobile}
                  onSwipeLeft={() => handleRejectRequest(request)}
                >
                  <Card 
                  className={`border-2 transition-all ${
                    request.type === 'quick_cut' 
                      ? 'bg-gradient-to-br from-amber-900/30 to-gray-900 border-amber-500 shadow-lg shadow-amber-500/20' 
                      : 'bg-gray-900 border-gray-700'
                  }`}
                >
                    <CardContent className="p-4 relative">
                      {!isMobile && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleRejectRequest(request)}
                          className="absolute right-2 top-2 h-7 w-7 text-red-400 hover:text-white hover:bg-red-600/80"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    {request.type === 'quick_cut' && request.expires_at && (
                      <div className="mb-3 p-4 bg-gradient-to-r from-red-900/40 to-gray-900/40 border border-red-500/60 rounded-xl">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <AlertCircle className="w-6 h-6 text-red-400 animate-pulse" />
                            <div>
                              <p className="text-red-300 text-xs uppercase tracking-wide">Urgente</p>
                              <p className="text-red-400 font-semibold text-base">Corte rápido requiere respuesta</p>
                              <p className="text-xs text-red-200">
                                Plazo del cliente: {getPreferredTimeLabel(request.preferred_time)} ·
                                Máximo {getPreferredTimeMinutes(request.preferred_time)} min
                              </p>
                          </div>
                            </div>
                          <div className="bg-black/40 border border-red-500/60 rounded-lg px-4 py-2 text-center">
                            <p className="text-xs text-red-200">Tiempo restante</p>
                            <p className="text-2xl font-bold text-red-400 font-mono">
                              {formatTimeLeft(request.expires_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-white font-medium text-lg">{request.service}</h3>
                          <Badge 
                            variant="outline" 
                            className={request.type === 'quick_cut' ? "text-amber-400 border-amber-400 font-semibold" : "text-blue-400 border-blue-400"}
                          >
                            {request.type === 'quick_cut' ? '⚡ Corte Rápido' : '📅 Reserva'}
                          </Badge>
                        </div>
                      
                        <p className="text-gray-400">
                          Cliente:{' '}
                          <button
                            onClick={() => {
                              // Cargar perfil del cliente
                              loadClientProfileForBarber(request.client_id, request.client_name);
                            }}
                            className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
                          >
                            {request.client_name}
                          </button>
                        </p>
                        <p className="text-amber-400 font-semibold">
                          {request.type === 'quick_cut' 
                            ? `Presupuesto máximo: ${formatClp(request.max_price)}`
                            : `Precio: ${formatClp(request.price)}`
                          }
                        </p>
                        {request.type === 'quick_cut' && (
                          <p className="text-gray-400 text-xs mt-1">
                            El precio final será el de tu barbería al aceptar
                          </p>
                        )}
                      
                        {request.type === 'traditional_booking' && (
                          <p className="text-amber-400 text-sm">
                            📅 Fecha: {formatDateTimeWithTZ(request.date, {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      
                        {request.preferred_time && request.type === 'quick_cut' && (
                          <p className="text-blue-400 text-sm">
                            ⏱️ {
                              request.preferred_time === 'asap' ? 'Lo antes posible' :
                              request.preferred_time === '30min' ? 'En 30 minutos' :
                              request.preferred_time === '1hour' ? 'En 1 hora' :
                              request.preferred_time === '2hours' ? 'En 2 horas' :
                              'Horario flexible'
                            }
                          </p>
                        )}
                      
                        {request.service_location === 'home' && (
                          <Badge variant="outline" className="text-blue-400 border-blue-400 mt-1">
                            🏠 A domicilio
                          </Badge>
                        )}
                      
                        {request.notes && (
                          <p className="text-gray-300 text-sm mt-2">
                            💬 {request.notes}
                          </p>
                        )}
                      </div>
                    
                      <div className="text-right">
                        <Badge variant="outline" className="mb-2">
                          <MapPin className="w-3 h-3 mr-1" />
                          {request.distance || 'N/A'} km
                        </Badge>
                      
                        {request.type === 'quick_cut' && request.expires_at && (
                          <div className="text-xs text-gray-500">
                            Responder antes de: {formatTimeWithTZ(request.expires_at, { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      
                        {request.lat && request.lng && (
                          <p className="text-xs text-gray-500 mt-1">
                            📍 Zona: {request.lat.toFixed(3)}, {request.lng.toFixed(3)}
                          </p>
                        )}
                      </div>
                    </div>
                  
                    {/* Additional details */}
                    <div className="bg-gray-800 p-3 rounded-lg mb-4">
                      <h4 className="text-white text-sm font-medium mb-2">Detalles de la Solicitud:</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-400">Solicitud creada:</span>
                          <p className="text-white">{formatTimeWithTZ(request.created_at, { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <div>
                          <span className="text-gray-400">Tipo:</span>
                          <p className="text-white">{request.type === 'quick_cut' ? 'Corte Rápido' : 'Reserva Tradicional'}</p>
                        </div>
                        {request.max_distance && (
                          <div>
                            <span className="text-gray-400">Máx. distancia:</span>
                            <p className="text-white">{request.max_distance} km</p>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-400">Estado:</span>
                          <p className="text-green-400">Pendiente</p>
                        </div>
                      </div>
                    </div>
                  
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleAcceptRequest(request)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Aceptar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                </SwipeableListItem>
              ))
            )}
            </div>
          </div>
        </div>
      );
    };

    const BarberBusiness = ({ barbershopData, barberBarbershopsList, setActiveBarbershop, user, BACKEND_URL, loadBarberData, handleLogout, showCreateBarbershop, setShowCreateBarbershop }) => (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Mi Negocio</h2>
          <p className="text-gray-400">Gestiona tu barbería profesionalmente</p>
        </div>

        {barberBarbershopsList && barberBarbershopsList.length > 1 && (
          <Card className="bg-gray-900 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">Barbería activa</CardTitle>
              <p className="text-gray-400 text-sm">Solo puedes estar activo en una barbería a la vez. Selecciona cuál usar ahora.</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {barberBarbershopsList.map((shop) => (
                <div key={shop.id} className="flex items-center justify-between rounded-lg border border-gray-700 bg-gray-800/50 p-3">
                  <div>
                    <p className="text-white font-medium">{shop.name}</p>
                    {shop.address && <p className="text-gray-400 text-xs truncate">{shop.address}</p>}
                  </div>
                  {barbershopData?.id === shop.id ? (
                    <Badge variant="secondary" className="bg-amber-500/20 text-amber-200 border-amber-500/40">Activa</Badge>
                  ) : (
                    <Button size="sm" variant="outline" className="border-amber-500/50 text-amber-200 hover:bg-amber-500/20" onClick={() => setActiveBarbershop(shop.id)}>
                      Activar
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {!barbershopData ? (
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-8 text-center">
              <Scissors className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-white text-xl mb-2">¡Crea tu barbería!</h3>
              <p className="text-gray-400 mb-4">
                Registra tu barbería para aparecer en el mapa y recibir clientes
              </p>
              <Button 
                onClick={() => setShowCreateBarbershop(true)}
                className="bg-amber-500 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear Mi Barbería
              </Button>
            </CardContent>
          </Card>
        ) : (
          <BarbershopManagement 
            barbershop={barbershopData}
            onUpdate={(updatedBarbershop) => setBarbershopData(updatedBarbershop)}
            BACKEND_URL={BACKEND_URL}
            onNotify={showNotification}
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          />
        )}

        <div className="max-w-sm mx-auto space-y-4">
          <Button 
            variant="outline" 
            className="w-full text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>

        {/* Create Barbershop Modal */}
        <Dialog open={showCreateBarbershop} onOpenChange={setShowCreateBarbershop}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-white">Crear Mi Barbería</DialogTitle>
            </DialogHeader>
            <CreateBarbershopForm />
          </DialogContent>
        </Dialog>
      </div>
    );


  // ============= CONTINUATION OF AppContent =============


  // Initialize map function
  // Google Maps eliminado completamente - usando lista simple

  // Handle quick search function
  const loadPublicMaintenance = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/config/maintenance`);
      setMaintenanceBanner({ enabled: response.data.enabled, message: response.data.message });
    } catch (error) {
      console.error('Error loading public maintenance:', error);
    }
  }

  // Login and Register handlers
  const handleLogin = async (email, password) => {
    setLoading(true);
    setError('');
    setVerificationEmail(email || '');
    setVerificationResendMessage('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email, password
      });
      // Store token and user data
      localStorage.setItem('token', response.data.access_token);
      setUser(applyDefaultAvatar(response.data.user));
      setShowLogin(false);
      setSuccess('Inicio de sesión exitoso');
      
      // Load specific data based on user type
      if (response.data.user.user_type === 'barber') {
        setTimeout(() => loadBarberData(), 1000);
      } else if (response.data.user.user_type === 'client') {
        setTimeout(() => {
          loadBarbershops();
          loadClientHistory();
        }, 1000);
      }
      
      setTimeout(() => setSuccess(''), 3000);
      return { ok: true };
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Error en el inicio de sesión';
      if (status !== 401 && typeof console !== 'undefined' && console.warn) {
        console.warn('Login:', message);
      }
      if (message === 'Email no verificado') {
        setError('Tu correo no está verificado. Revisa tu email para activar la cuenta.');
        setVerificationResendMessage('');
        setShowLogin(false);
        return { ok: false, error: 'Tu correo no está verificado.' };
      }
      if (message === 'Email o contraseña incorrectos') {
        return { ok: false, error: message };
      }
      setError(message);
      return { ok: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData) => {
    setLoading(true);
    setError('');
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const joinBarbershopId = params?.get('join_barbershop');
    const payload = { ...userData };
    if (joinBarbershopId) {
      payload.barbershop_invite = joinBarbershopId;
      payload.userType = 'barber';
    }
    try {
      await axios.post(`${BACKEND_URL}/api/auth/register`, payload);
      recordVisitEvent('register');
      setSuccess('Te enviamos un correo de verificación. Confirma tu email para activar tu cuenta.');
      setShowRegister(false);
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      console.error('Register error:', error);
      setError(error.response?.data?.detail || 'Error en el registro');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) {
      setVerificationResendMessage('Ingresa tu email para reenviar la verificación.');
      return;
    }
    try {
      setVerificationResendLoading(true);
      setVerificationResendMessage('');
      const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification-public`, {
        email: verificationEmail
      });
      setVerificationResendMessage(response.data?.message || 'Si el correo existe, enviaremos un enlace de verificación.');
    } catch (error) {
      setVerificationResendMessage(error.response?.data?.detail || 'No se pudo reenviar el correo. Inténtalo más tarde.');
    } finally {
      setVerificationResendLoading(false);
    }
  };

  const handleContactSupport = useCallback(() => {
    const base = 'https://wa.me/56976488856';
    const message = verificationEmail
      ? `Hola, necesito ayuda con la verificación de mi cuenta. Email: ${verificationEmail}`
      : 'Hola, necesito ayuda con la verificación de mi cuenta.';
    window.open(`${base}?text=${encodeURIComponent(message)}`, '_blank');
  }, [verificationEmail]);

  const handleRequestPasswordReset = async () => {
    if (!resetEmail) {
      setResetMessage('Ingresa tu email para recuperar la contraseña.');
      return;
    }
    try {
      setResetLoading(true);
      setResetMessage('');
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: resetEmail
      });
      setResetMessage(response.data?.message || 'Si el correo existe, enviaremos un enlace para restablecer la contraseña.');
    } catch (error) {
      setResetMessage(error.response?.data?.detail || 'No se pudo enviar el correo de recuperación.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetToken) {
      setResetMessage('El enlace de recuperación no es válido.');
      return;
    }
    if (!resetPasswordDraft || !resetPasswordConfirm) {
      setResetMessage('Completa ambos campos de contraseña.');
      return;
    }
    try {
      setResetLoading(true);
      setResetMessage('');
      const response = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        password: resetPasswordDraft,
        confirm_password: resetPasswordConfirm
      });
      setSuccess('Contraseña actualizada correctamente. Ya puedes iniciar sesión.');
      setShowResetPassword(false);
      setResetToken('');
      setResetPasswordDraft('');
      setResetPasswordConfirm('');
      setTimeout(() => setSuccess(''), 4000);
      return response.data;
    } catch (error) {
      setResetMessage(error.response?.data?.detail || 'No se pudo restablecer la contraseña.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setBarbershopData(null);
    setActiveTab('map');
  }, []);

  // Check for existing user on app load
  // Auth check moved to checkAuthStatus (single source of truth)

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const verifyStatus = params.get('verify');
    const resetParam = params.get('reset') || params.get('token');
    if (verifyStatus === 'success') {
      setSuccess('Correo de verificación aceptado');
    } else if (verifyStatus === 'expired') {
      setError('El enlace de verificación expiró. Reenvía el correo para continuar.');
    } else if (verifyStatus === 'invalid') {
      setError('El enlace de verificación no es válido. Reenvía el correo para continuar.');
    }
    if (resetParam) {
      setResetToken(resetParam);
      setShowResetPassword(true);
      setResetMessage('');
    }
    if (verifyStatus || resetParam) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    setTimeout(() => setSuccess(''), 4000);
  }, []);

  // Listen for login events and success messages
  useEffect(() => {
    const handleUserLogin = (event) => {
      console.log('🎉 User login event received:', event.detail);
      setUser(applyDefaultAvatar(event.detail));
    };
    
    const handleShowSuccess = (event) => {
      console.log('✅ Success message:', event.detail);
      setSuccess(event.detail);
      setTimeout(() => setSuccess(''), 3000);
    };
    
    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('showSuccess', handleShowSuccess);
    
    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('showSuccess', handleShowSuccess);
    };
  }, []);

  // Real-time location tracking
  const handleLocationUpdate = (location) => {
    setCurrentLocation(location);
    if (realTimeTracking && matchedBarber) {
      updateBarberWithLocation(location);
    }
  };

  // Update barber with current location (for real-time tracking)
  const updateBarberWithLocation = async (location) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/quick-cuts/update-location`, {
        lat: location.lat,
        lng: location.lng,
        accuracy: location.accuracy
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error updating location:', error);
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      return permission === 'granted';
    }
    return false;
  };

  // Dynamic pricing calculation
  const calculateDynamicPrice = (basePrice, demand, distance, timeOfDay, weather) => {
    if (!dynamicPricing) return basePrice;
    
    let multiplier = 1;
    
    // Demand surge pricing (like Uber)
    if (demand > 0.8) multiplier += 0.5;
    else if (demand > 0.6) multiplier += 0.3;
    else if (demand > 0.4) multiplier += 0.1;
    
    // Distance factor
    if (distance > 5) multiplier += 0.2;
    
    // Time of day (peak hours)
    const hour = new Date().getHours();
    if ((hour >= 17 && hour <= 20) || (hour >= 11 && hour <= 14)) {
      multiplier += 0.2;
    }
    
    return Math.round(basePrice * multiplier);
  };

  // AI-powered recommendations
  const generateSmartRecommendations = async () => {
    try {
      const userPreferences = {
        previousServices: bookingHistory.map(b => b.service),
        averageSpent: totalSpent / Math.max(totalCuts, 1),
        preferredTimes: bookingHistory.map(b => new Date(b.created_at).getHours()),
        location: userLocation
      };

      // Simulate AI recommendations (in real app, call ML API)
      const recommendations = [
        {
          type: 'service',
          title: 'Servicio Recomendado',
          description: 'Basado en tu historial, te recomendamos "Corte + Barba"',
          discount: 15,
          validUntil: new Date(Date.now() + 24 * 60 * 60 * 1000)
        },
        {
          type: 'barber',
          title: 'Barbero Destacado',
          description: 'Juan Carlos tiene 98% de satisfacción en tu zona',
          badge: 'Top Rated'
        },
        {
          type: 'time',
          title: 'Mejor Horario',
          description: 'Los martes a las 3 PM tienes 40% menos tiempo de espera',
          savings: '15 min'
        }
      ];

      setSmartRecommendations(recommendations);
    } catch (error) {
      console.error('Error generating recommendations:', error);
    }
  };

  // Loyalty program calculations
  const calculateLoyaltyRewards = (spent, cuts) => {
    const points = Math.floor(spent / 1000); // 1 point per $1000 spent
    let level = 'Bronze';
    
    if (cuts >= 50) level = 'Platinum';
    else if (cuts >= 25) level = 'Gold';
    else if (cuts >= 10) level = 'Silver';
    
    setLoyaltyPoints(points);
    setClientLevel(level);
    
    // Check for achievements
    const newAchievements = [];
    if (cuts === 1) newAchievements.push({ id: 'first_cut', name: 'Primer Corte', icon: '🎉' });
    if (cuts === 10) newAchievements.push({ id: 'loyal_client', name: 'Cliente Leal', icon: '💎' });
    if (cuts === 25) newAchievements.push({ id: 'vip_member', name: 'Miembro VIP', icon: '👑' });
    
    if (newAchievements.length > 0) {
      setAchievements(prev => [...prev, ...newAchievements]);
      // Achievement notifications disabled per user request
      // newAchievements.forEach(achievement => {
      //   showNotification('¡Logro Desbloqueado!', `${achievement.icon} ${achievement.name}`, 'success');
      // });
    }
  };

  // useEffect hooks
  useEffect(() => {
    checkAuthStatus();
    getUserLocation();
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener('change', update);
    } else {
      media.addListener(update);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', update);
      } else {
        media.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    if (user && user.user_type === 'client') {
      if (showBooking) {
        return;
      }
      if (showTopRanking) {
        loadWeeklyRanking();
      } else {
      loadBarbershops();
      }
      loadClientHistory();
      loadClientAcceptedQuickCuts();
      loadClientReviews();
      loadUpcomingBookings();
      
      // Initialize enterprise features for clients
      // generateSmartRecommendations();
      // calculateLoyaltyRewards(totalSpent, totalCuts);
    } else if (user && user.user_type === 'barber') {
      // Establecer la pestaña "Citas" como predeterminada para barberos
      setActiveTab('calendar');
      loadBarberData();
      loadBarberAcceptedQuickCuts();
    } else if (user && user.user_type === 'admin') {
      setAdminTab('barbershops');
      loadAdminBarbershops();
      loadAdminUsers();
      loadAdminReviews();
    }
  }, [user, showBooking, showTopRanking]);

  useEffect(() => {
    if (!user?.id) return;
    if (!['client', 'barber'].includes(user.user_type)) return;
    const key = `onboarding_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    if (showOnboarding) return;
    setOnboardingStep(0);
    setShowOnboarding(true);
  }, [user, showOnboarding]);

  useEffect(() => {
    if (!barbershops.length) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const barbershopId = params.get('barbershop_id');
    if (!barbershopId) return;
    const target = barbershops.find((shop) => shop.id === barbershopId);
    if (target) {
      setSelectedBarbershop(target);
    }
  }, [barbershops]);

  useEffect(() => {
    if (!barbershops.length) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const barbershopId = params.get('barbershop_id');
    if (!barbershopId) return;
    const target = barbershops.find((shop) => shop.id === barbershopId);
    if (target) {
      setSelectedBarbershop(target);
    }
  }, [barbershops]);

  useEffect(() => {
    if (!showOnboarding) return;
    if (user?.user_type === 'client') {
      const tabByStep = ['profile', 'map', 'quick', 'map', 'map'];
      const nextTab = tabByStep[onboardingStep] || 'map';
      setActiveTab(nextTab);
    }
    if (user?.user_type === 'barber') {
      const tabByStep = ['business', 'requests', 'calendar', 'history', 'requests'];
      const nextTab = tabByStep[onboardingStep] || 'calendar';
      setActiveTab(nextTab);
    }
  }, [showOnboarding, onboardingStep, user?.user_type, setActiveTab]);

  useEffect(() => {
    loadPublicMaintenance();
  }, []);

  useEffect(() => {
    if (!user || user.user_type !== 'barber') return;
    const params = new URLSearchParams(window.location.search);
    const joinBarbershopId = params.get('join_barbershop');
    if (!joinBarbershopId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .post(`${BACKEND_URL}/api/barbershops/join-by-invite`, { barbershop_id: joinBarbershopId }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => {
        if (typeof window !== 'undefined') {
          const u = new URL(window.location.href);
          u.searchParams.delete('join_barbershop');
          window.history.replaceState({}, '', u.toString());
        }
        if (typeof showNotification === 'function') {
          showNotification('Listo', 'Te has unido a la barbería', 'success');
        }
      })
      .catch(() => {});
  }, [user?.id, user?.user_type]);

  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(window.location.search);
    const joinId = params.get('join_barbershop');
    if (joinId) {
      setRegisterInviteBarbershopId(joinId);
      setShowRegister(true);
      setShowLogin(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user && !visitTrackedRef.current) {
      visitTrackedRef.current = true;
      recordVisitEvent('page_visit');
    }
  }, [user, recordVisitEvent]);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') return;

    if (adminTab === 'users') {
      loadAdminUsers();
    }
    if (adminTab === 'reviews') {
      loadAdminReviews();
    }
    if (adminTab === 'settings') {
      loadAdminMetrics();
      loadAdminAuditLogs();
      loadAdminNotifications();
      loadAdminMaintenance();
    }
    if (adminTab === 'barbershops') {
      loadAdminBarbershops();
    }
    if (adminTab === 'visits') {
      loadAdminVisitStats();
      loadAdminVisitEvents();
    }
  }, [
    user,
    adminTab,
    debouncedAdminUserSearch,
    adminUserTypeFilter,
    adminUserBlockedFilter,
    debouncedAdminReviewFilters,
    adminMetricsStart,
    adminMetricsEnd
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminUserSearch(adminUserSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [adminUserSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminBarbershopSearch(adminBarbershopSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [adminBarbershopSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAdminReviewFilters(adminReviewFilters);
    }, 400);
    return () => clearTimeout(timer);
  }, [adminReviewFilters]);


  useEffect(() => {
    if (!adminEditBarbershop || !adminPlacesLoaded) return;
    const instance = initializeAdminAutocomplete?.();
    if (!instance) return;
    const listener = setupAdminPlaceChangedListener((place) => {
      const components = place.components || {};
      setAdminSelectedPlace(place);
      setAdminEditBarbershopDraft((prev) => ({
        ...prev,
        address: place.formatted_address,
        lat: place.lat,
        lng: place.lng,
        city: components.city || '',
        comuna: components.comuna || components.city || '',
        region: components.region || components.state || '',
        street: components.street_name || '',
        street_number: components.street_number || '',
        postal_code: components.postal_code || ''
      }));
    }, instance);
    return () => listener?.remove?.();
  }, [adminEditBarbershop, adminPlacesLoaded, setupAdminPlaceChangedListener, initializeAdminAutocomplete]);

  useEffect(() => {
    if (!showBarberProfileHub || user?.user_type !== 'barber') return;
    loadJoinRequests();
  }, [showBarberProfileHub, user]);

  useEffect(() => {
    if (!showJoinBarbershop) return;
    loadJoinableBarbershops(joinSearch);
  }, [showJoinBarbershop]);

  const groupedNotifications = useMemo(() => {
    const grouped = {};
    userNotifications.forEach((item) => {
      const key = `${item.title}::${item.message}`;
      if (!grouped[key]) {
        grouped[key] = {
          id: key,
          title: item.title,
          message: item.message,
          count: 0,
          latestAt: 0
        };
      }
      grouped[key].count += 1;
      grouped[key].latestAt = Math.max(grouped[key].latestAt, item.createdAt || 0);
    });
    return Object.values(grouped).sort((a, b) => b.latestAt - a.latestAt);
  }, [userNotifications]);

  const inboxGroups = useMemo(() => {
    const groups = {
      booking: { title: 'Reservas', items: [] },
      quick_cut: { title: 'Cortes rápidos', items: [] },
      admin: { title: 'Administración', items: [] }
    };
    inboxItems.forEach((item) => {
      if (groups[item.type]) {
        const key = `${item.type}:${item.id}`;
        if (!deletedInboxKeys.includes(key)) {
          groups[item.type].items.push(item);
        }
      }
    });
    return Object.values(groups).filter((group) => group.items.length > 0);
  }, [inboxItems, deletedInboxKeys]);

  const inboxSortedByDate = useMemo(() => {
    const list = inboxItems
      .filter((item) => !deletedInboxKeys.includes(`${item.type}:${item.id}`))
      .map((item) => ({
        ...item,
        _sortTime: item.lastMessage?.timestamp ? new Date(item.lastMessage.timestamp).getTime() : (item.createdAt || 0)
      }))
      .sort((a, b) => (b._sortTime || 0) - (a._sortTime || 0));
    return list;
  }, [inboxItems, deletedInboxKeys]);

  const markConversationUnread = (target) => {
    const targetKey = `${target.type}:${target.id}`;
    chatLastSeenRef.current[targetKey] = null;
    chatUnreadRef.current[targetKey] = 1;
    setInboxItems((prev) =>
      prev.map((entry) =>
        `${entry.type}:${entry.id}` === targetKey
          ? { ...entry, unreadCount: 1 }
          : entry
      )
    );
  };

  const markAllConversationsRead = () => {
    setInboxItems((prev) =>
      prev.map((entry) => {
        const targetKey = `${entry.type}:${entry.id}`;
        if (entry.lastMessage) {
          chatLastSeenRef.current[targetKey] = entry.lastMessage.id || entry.lastMessage.timestamp;
        }
        chatUnreadRef.current[targetKey] = 0;
        return { ...entry, unreadCount: 0 };
      })
    );
  };

  const markAllConversationsUnread = () => {
    setInboxItems((prev) =>
      prev.map((entry) => {
        const targetKey = `${entry.type}:${entry.id}`;
        chatLastSeenRef.current[targetKey] = null;
        chatUnreadRef.current[targetKey] = 1;
        return { ...entry, unreadCount: 1 };
      })
    );
  };

  useEffect(() => {
    if (!user?.id) return;
    const key = `inbox_archived_${user.id}`;
    const deletedKey = `inbox_deleted_${user.id}`;
    try {
      const archived = JSON.parse(localStorage.getItem(key) || '[]');
      const deleted = JSON.parse(localStorage.getItem(deletedKey) || '[]');
      setArchivedInboxKeys(Array.isArray(archived) ? archived : []);
      setDeletedInboxKeys(Array.isArray(deleted) ? deleted : []);
    } catch (error) {
      setArchivedInboxKeys([]);
      setDeletedInboxKeys([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    localStorage.setItem(`inbox_archived_${user.id}`, JSON.stringify(archivedInboxKeys));
  }, [user?.id, archivedInboxKeys]);

  useEffect(() => {
    if (!user?.id) return;
    localStorage.setItem(`inbox_deleted_${user.id}`, JSON.stringify(deletedInboxKeys));
  }, [user?.id, deletedInboxKeys]);

  const { chatTargets, inboxTargets, loadInbox } = useInboxData({
    user,
    BACKEND_URL,
    upcomingBookings,
    clientAcceptedQuickCuts,
    bookingHistory,
    barberBookings,
    barberAcceptedQuickCuts,
    barberCompletedQuickCuts,
    barberAdminChats,
    chatUnreadRef,
    setInboxItems,
    setInboxLoading
  });

  const {
    loadClientAcceptedQuickCuts,
    loadClientHistory,
    loadUpcomingBookings
  } = useClientDataLoaders({
    BACKEND_URL,
    user,
    setBookingHistory,
    setUpcomingBookings,
    setRecentCancelledBookings,
    setClientAcceptedQuickCuts
  });

  const {
    loadAdminUsers,
    loadAdminReviews,
    loadAdminBarbershops,
    loadAdminMetrics,
    loadAdminAuditLogs,
    loadAdminNotifications,
    loadAdminMaintenance,
    loadAdminUserStats,
    loadAdminVisitStats,
    loadAdminVisitEvents
  } = useAdminLoaders({
    BACKEND_URL,
    adminCacheRef,
    debouncedAdminUserSearch,
    adminUserTypeFilter,
    adminUserBlockedFilter,
    setAdminUsers,
    debouncedAdminReviewFilters,
    setAdminReviews,
    setAdminReviewsLoading,
    setAdminBarbershops,
    adminMetricsStart,
    adminMetricsEnd,
    setAdminMetrics,
    setAdminAuditLogs,
    setAdminNotifications,
    setMaintenanceEnabled,
    setMaintenanceMessage,
    setAdminUserStats,
    adminVisitsStart,
    adminVisitsEnd,
    setAdminVisitStats,
    setAdminVisitEvents
  });

  async function uploadImageToImageKit(file, folder) {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No auth token');

    const authRes = await axios.get(`${BACKEND_URL}/api/imagekit/auth`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', `${Date.now()}-${file.name}`);
    formData.append('publicKey', authRes.data.publicKey);
    formData.append('signature', authRes.data.signature);
    formData.append('expire', authRes.data.expire);
    formData.append('token', authRes.data.token);
    if (folder) {
      formData.append('folder', folder);
    }

    const uploadRes = await axios.post('https://upload.imagekit.io/api/v1/files/upload', formData);
    return uploadRes.data.url;
  }

  const { handleQuickSearch, handleCancelQuickCutSearch } = useQuickCutFlow({
    BACKEND_URL,
    selectedService,
    quickCutWindowStart,
    quickCutSearchAttempts,
    setQuickCutWindowStart,
    setQuickCutSearchAttempts,
    setShowNoBarbersModal,
    getQuickCutResponseWindowSeconds,
    preferredTime,
    setMatchedBarber,
    setQuickCutStatus,
    setSearchTimeLeft,
    setError,
    currentQuickCutRequestRef,
    quickCutTimerRef,
    quickCutMatchRef,
    setCurrentQuickCutRequestId,
    setSuccess,
    showNotification,
    priceLimit,
    maxDistance,
    serviceLocation,
    user,
    loadClientAcceptedQuickCuts
  });

  const { exportAdminUsersCsv, exportAdminReviewsCsv, exportAdminVisitsCsv } = useAdminExports({
    adminUsers,
    adminReviews,
    adminVisitEvents
  });

  const { handleMarkArrived, handleCancelQuickCutRequest } = useQuickCutActions({
    BACKEND_URL,
    showNotification,
    loadClientAcceptedQuickCuts,
    registerCancelledBooking,
    setMatchedBarber,
    setQuickCutStatus,
    setCurrentQuickCutRequestId,
    currentQuickCutRequestRef
  });

  const { updateQuickCutProgress } = useQuickCutProgress({
    matchedBarber,
    setQuickCutProgress,
    setEstimatedArrival
  });

  const {
    handleAdminUpdateUser,
    handleAdminResetPassword,
    handleAdminDeleteReview,
    handleAdminDeleteEvent,
    handleAdminFlagReview,
    handleAdminSendBarbershopMessage,
    handleAdminEditBarbershop,
    addAdminService,
    updateAdminService,
    removeAdminService,
    handleAdminUploadBarbershopImage,
    submitAdminBarbershopUpdate: submitAdminBarbershopUpdateAction,
    handleAdminDeleteBarbershop,
    markAdminNotificationRead,
    handleAdminSearch,
    updateAdminMaintenance,
    handleAdminSaveBlock,
    handleAdminSetBarberBarbershop
  } = useAdminActions({
    BACKEND_URL,
    user,
    adminPasswordDrafts,
    setAdminPasswordDrafts,
    adminReviewNotes,
    setAdminReviews,
    setAdminUsers,
    adminEventType,
    adminEventId,
    setAdminEventId,
    adminMessageBarbershop,
    adminMessageDraft,
    setAdminMessageDraft,
    setAdminMessageBarbershop,
    setAdminEditBarbershop,
    setAdminSelectedPlace,
    setAdminEditBarbershopDraft,
    setAdminBarbershops,
    setAdminNotifications,
    maintenanceEnabled,
    maintenanceMessage,
    setMaintenanceBanner,
    adminBlockDrafts,
    adminSearchQuery,
    setAdminSearchResults,
    requestConfirmation,
    showNotification,
    uploadImageToImageKit,
    setAdminImageUploading
  });

  const { downloadCalendarFile, openCalendarUrl, getCalendarUrlWithToken } = useCalendarDownload(showNotification);

  const submitAdminBarbershopUpdate = () => {
    submitAdminBarbershopUpdateAction(adminEditBarbershop, adminEditBarbershopDraft);
  };

  useEffect(() => {
    if (!user || user.user_type !== 'barber') {
      setHasRequestNotification(false);
      setLastPendingCount(0);
      return;
    }

    const pendingCount =
      quickCutRequests.filter(req => req.status === 'pending').length +
      barberBookings.filter(booking => booking.status === 'pending').length;

    if (activeTab === 'requests') {
      setHasRequestNotification(false);
      setLastPendingCount(pendingCount);
      return;
    }

    if (pendingCount === 0) {
      setHasRequestNotification(false);
      setLastPendingCount(0);
      return;
    }

    if (pendingCount < lastPendingCount) {
      setLastPendingCount(pendingCount);
      return;
    }

    if (pendingCount > lastPendingCount) {
      setHasRequestNotification(true);
    }
  }, [user, quickCutRequests, barberBookings, activeTab, lastPendingCount]);

  useEffect(() => {
    const loadEta = async () => {
      if (!selectedBarbershop?.id) {
        setSelectedBarbershopEta(null);
        return;
      }
      try {
        const response = await axios.get(`${BACKEND_URL}/api/barbershops/${selectedBarbershop.id}/eta`);
        setSelectedBarbershopEta(response.data);
      } catch (error) {
        console.error('Error loading ETA:', error);
        setSelectedBarbershopEta(null);
      }
    };
    loadEta();
  }, [selectedBarbershop?.id]);

  useEffect(() => {
    if (showReviewsModal) {
      setReviewsPage(1);
    }
  }, [showReviewsModal, selectedBarbershop?.id]);

  // Enterprise features initialization
  // Dynamic pricing updates
  useEffect(() => {
    if (dynamicPricing && selectedService) {
      const demand = Math.random(); // Simulate demand
      const distance = maxDistance[0];
      const dynamicPrice = calculateDynamicPrice(priceLimit[0], demand, distance);
      
      if (dynamicPrice !== priceLimit[0]) {
        setPriceLimit([dynamicPrice]);
        // Dynamic pricing notification disabled per user request
        // showNotification(
        //   '💰 Precio Dinámico',
        //   `Precio ajustado a $${dynamicPrice.toLocaleString()} por demanda actual`,
        //   'info'
        // );
      }
    }
  }, [selectedService, maxDistance, dynamicPricing]);

  // Google Maps eliminado - ya no se necesita este useEffect

  // Helper functions
  async function checkAuthStatus() {
    setAuthChecking(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthChecking(false);
      return;
    }
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      setUser(applyDefaultAvatar(response.data));
      } catch (error) {
        localStorage.removeItem('token');
    } finally {
      setAuthChecking(false);
    }
  }

  function getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.log('Error getting location:', error);
        }
      );
    }
  }

  async function loadBarbershops() {
    try {
      setLoadingBarbershops(true);
      const response = await axios.get(`${BACKEND_URL}/api/barbershops`);
      setBarbershops(response.data.barbershops || []);
    } catch (error) {
      console.error('Error loading barbershops:', error);
    } finally {
      setLoadingBarbershops(false);
    }
  }

  async function loadWeeklyRanking() {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/barbershops/ranking`, {
        params: { period: 'week' }
      });
      setRankingBarbershops(response.data.rankings || []);
    } catch (error) {
      console.error('Error loading ranking:', error);
    }
  }

  async function loadBarberData() {
    try {
      setLoadingBarberHistory(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      const safeGet = async (url, fallback) => {
        try {
          const response = await axios.get(url, { headers });
          return { ok: true, data: response.data };
        } catch (error) {
          return { ok: false, data: fallback };
        }
      };

      const [
        appointmentsRes,
        requestsRes,
        barbershopRes,
        acceptedQuickCutsRes,
        completedQuickCutsRes,
        adminChatsRes
      ] = await Promise.all([
        safeGet(`${BACKEND_URL}/api/bookings/barber`, { bookings: [] }),
        safeGet(`${BACKEND_URL}/api/quick-cuts/requests`, { requests: [] }),
        safeGet(`${BACKEND_URL}/api/barbershops/my`, { barbershop: null }),
        safeGet(`${BACKEND_URL}/api/quick-cuts/barber/accepted`, { quick_cuts: [] }),
        safeGet(`${BACKEND_URL}/api/quick-cuts/barber/completed`, { quick_cuts: [] }),
        safeGet(`${BACKEND_URL}/api/chat/admin`, { targets: [] })
      ]);
      
      console.log('Barber data loaded:', {
        appointments: appointmentsRes.data.bookings?.length || 0,
        requests: requestsRes.data.requests?.length || 0,
        acceptedQuickCuts: acceptedQuickCutsRes.data.quick_cuts?.length || 0,
        completedQuickCuts: completedQuickCutsRes.data.quick_cuts?.length || 0,
        barbershop: barbershopRes.data.barbershop?.name || 'None'
      });
      
      const nextBookings = appointmentsRes.data.bookings || [];
      const nextRequests = requestsRes.data.requests || [];
      const nextBarbershop = barbershopRes.data.barbershop;
      const nextBarbershopsList = barbershopRes.data.barbershops || [];
      const nextAccepted = acceptedQuickCutsRes.data.quick_cuts || [];
      const nextCompleted = completedQuickCutsRes.data.quick_cuts || [];
      const nextAdminTargets = adminChatsRes.data.targets || [];

      setAppointments((prev) => (appointmentsRes.ok ? nextBookings : prev));
      setBarberBookings((prev) => (appointmentsRes.ok ? nextBookings : prev));
      setQuickCutRequests((prev) => (requestsRes.ok ? nextRequests : prev));
      setBarbershopData((prev) => (barbershopRes.ok ? nextBarbershop : prev));
      setBarberBarbershopsList((prev) => (barbershopRes.ok ? nextBarbershopsList : prev));
      setBarberAcceptedQuickCuts((prev) => (acceptedQuickCutsRes.ok ? nextAccepted : prev));
      setBarberCompletedQuickCuts((prev) => (completedQuickCutsRes.ok ? nextCompleted : prev));
      setBarberAdminChats((prev) => (adminChatsRes.ok ? nextAdminTargets : prev));
      
    } catch (error) {
      console.error('Error loading barber data:', error);
    } finally {
      setLoadingBarberHistory(false);
    }
  }

  const setActiveBarbershop = async (barbershopId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await axios.put(
        `${BACKEND_URL}/api/users/me/active-barbershop`,
        { barbershop_id: barbershopId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadBarberData();
      showNotification('Barbería activa', 'Se actualizó la barbería activa', 'success');
    } catch (error) {
      console.error('Error setting active barbershop:', error);
      showNotification('Error', error.response?.data?.detail || 'No se pudo cambiar la barbería activa', 'destructive');
    }
  };

  async function loadBarberQuickCutRequests() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${BACKEND_URL}/api/quick-cuts/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuickCutRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error loading quick cut requests:', error);
    }
  }

  const loadJoinableBarbershops = async (query = '') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      setJoinLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/barbershops/joinable`, {
        params: query ? { q: query } : {},
        headers: { Authorization: `Bearer ${token}` }
      });
      setJoinResults(response.data.barbershops || []);
    } catch (error) {
      console.error('Error loading joinable barbershops:', error);
    } finally {
      setJoinLoading(false);
    }
  };

  const sendJoinRequest = async (barbershopId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/barbershops/${barbershopId}/join-request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Solicitud enviada', 'La barbería recibió tu solicitud', 'success');
      loadJoinableBarbershops(joinSearch);
    } catch (error) {
      console.error('Error sending join request:', error);
      showNotification('Error', error.response?.data?.detail || 'No se pudo enviar la solicitud', 'destructive');
    }
  };

  const loadJoinRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${BACKEND_URL}/api/barbershops/join-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJoinRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error loading join requests:', error);
    }
  };

  const acceptJoinRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/barbershops/join-requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Solicitud aceptada', 'El barbero fue agregado a la barbería', 'success');
      loadJoinRequests();
    } catch (error) {
      console.error('Error accepting join request:', error);
      showNotification('Error', 'No se pudo aceptar la solicitud', 'destructive');
    }
  };

  const rejectJoinRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/barbershops/join-requests/${requestId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Solicitud rechazada', 'Se rechazó la solicitud', 'info');
      loadJoinRequests();
    } catch (error) {
      console.error('Error rejecting join request:', error);
      showNotification('Error', 'No se pudo rechazar la solicitud', 'destructive');
    }
  };

  const deleteBarbershopByOwner = async () => {
    if (!barbershopData?.id) return;
    const confirmed = await requestConfirmation({
      title: 'Eliminar barbería',
      description: '¿Seguro que deseas eliminar esta barbería? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/barbershops/${barbershopData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBarbershopData(null);
      setBarberBarbershopsList([]);
      showNotification('Barbería eliminada', 'Tu barbería fue eliminada', 'success');
    } catch (error) {
      console.error('Error deleting barbershop:', error);
      showNotification('Error', 'No se pudo eliminar la barbería', 'destructive');
    }
  };

  async function loadBarberAcceptedQuickCuts() {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/quick-cuts/barber/accepted`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBarberAcceptedQuickCuts(response.data.quick_cuts || []);
    } catch (error) {
      console.error('Error loading barber accepted quick cuts:', error);
      setBarberAcceptedQuickCuts([]);
    }
  }

  const enableWebSockets = false;

  useNotificationsStream({
    user,
    BACKEND_URL,
    enableWebSockets,
    realtimeConnectedRef,
    showNotification,
    loadBarberData,
    loadBarberAcceptedQuickCuts,
    loadUpcomingBookings,
    loadClientAcceptedQuickCuts,
    loadClientHistory,
    setHasRequestNotification
  });

  useClientPolling({
    user,
    showBooking,
    realtimeConnectedRef,
    loadClientAcceptedQuickCuts,
    loadClientHistory,
    loadClientReviews
  });

  useBarberPolling({
    user,
    activeTab,
    loadBarberData,
    loadBarberAcceptedQuickCuts,
    loadBarberQuickCutRequests
  });

  useChatPolling({
    user,
    BACKEND_URL,
    selectedBookingChat,
    chatTargets,
    chatLastSeenRef,
    chatOpenedRef,
    chatUnreadRef,
    setSelectedBookingChat,
    setRecentCancelledBookings,
    showNotification
  });

  useQuickCutTimers({
    isMobile,
    mobileStage,
    mobileAcceptedUntil,
    mobileRequest,
    setCountdownTick,
    realTimeUpdates,
    quickCutStatus,
    updateQuickCutProgress
  });

  const getImageUrl = (value) => {
    if (!value) return value;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    return `${BACKEND_URL}${value}`;
  };

  if (authChecking) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" className="text-amber-400 mx-auto mb-4" />
          <p className="text-sm text-gray-400">Cargando tu sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Modern Header - MOBILE RESPONSIVE */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 px-3 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer"
            aria-label="Volver al inicio"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg sm:rounded-xl flex items-center justify-center">
              <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-lg sm:text-2xl font-black bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              CÓRTATE.CL
            </h1>
          </button>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {user ? (
              <div className="flex items-center gap-2 sm:gap-4">
                <Sheet open={showAppMenu} onOpenChange={setShowAppMenu}>
                  <SheetTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-300 hover:text-white hover:bg-white/10 px-2"
                      aria-label="Abrir menú"
                    >
                      <Menu className="w-4 h-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="bg-gray-950 border-gray-800 text-white w-72 max-h-[100dvh] overflow-y-auto">
                    <div className="space-y-4 pb-6">
                    <div>
                      <p className="text-xs uppercase text-gray-500">Notificaciones</p>
                      <div className="mt-2 space-y-2">
                        {groupedNotifications.length === 0 ? (
                          <div className="text-xs text-gray-400">Sin notificaciones</div>
                        ) : (
                          groupedNotifications.slice(0, 3).map((note) => (
                            <div key={note.id} className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 text-xs text-gray-200">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-white">{note.title}</span>
                                {note.count > 1 && (
                                  <span className="text-amber-300">x{note.count}</span>
                                )}
                              </div>
                              <div className="text-gray-400">{note.message}</div>
                            </div>
                          ))
                        )}
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-amber-300 hover:text-amber-200"
                          onClick={() => {
                            setShowNotifications(true);
                            setShowAppMenu(false);
                          }}
                        >
                          Ver todas
                        </Button>
                      </div>
                    </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500">Navegación</p>
                        <div className="mt-2 space-y-1">
                          {user.user_type === 'client' && (
                            <>
                              <Button
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={() => {
                                  setShowInbox(true);
                                  loadInbox();
                                  setShowAppMenu(false);
                                }}
                              >
                                Bandeja de entrada
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('map'); setShowAppMenu(false); }}>
                                Barberías
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('quick'); setShowAppMenu(false); }}>
                                Corte rápido
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('profile'); setShowAppMenu(false); }}>
                                Perfil
                              </Button>
                            </>
                          )}
                          {user.user_type === 'barber' && (
                            <>
                              <Button
                                variant="ghost"
                                className="w-full justify-start"
                                onClick={() => {
                                  setShowInbox(true);
                                  loadInbox();
                                  setShowAppMenu(false);
                                }}
                              >
                                Bandeja de entrada
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('calendar'); setShowAppMenu(false); }}>
                                Citas
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('requests'); setShowAppMenu(false); }}>
                                Solicitudes
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('history'); setShowAppMenu(false); }}>
                                Historial
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setActiveTab('business'); setShowAppMenu(false); }}>
                                Negocio
                              </Button>
                            </>
                          )}
                          {user.user_type === 'admin' && (
                            <>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setAdminTab('barbershops'); setShowAppMenu(false); }}>
                                Barberías
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setAdminTab('users'); setShowAppMenu(false); }}>
                                Usuarios
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setAdminTab('reviews'); setShowAppMenu(false); }}>
                                Centro de reseñas
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setAdminTab('visits'); setShowAppMenu(false); }}>
                                Visitas
                              </Button>
                              <Button variant="ghost" className="w-full justify-start" onClick={() => { setAdminTab('settings'); setShowAppMenu(false); }}>
                                Configuraciones
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-gray-500">Cuenta</p>
                        <div className="mt-2 space-y-1">
                          {user.user_type === 'barber' ? (
                            <Button variant="ghost" className="w-full justify-start" onClick={() => { setShowBarberProfileHub(true); setShowAppMenu(false); }}>
                              Perfil
                            </Button>
                          ) : (
                            <Button variant="ghost" className="w-full justify-start" onClick={() => { setShowSettings(true); setShowAppMenu(false); }}>
                              Editar perfil
                            </Button>
                          )}
                          {user.user_type === 'barber' && (
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => { setShowJoinBarbershop(true); setShowAppMenu(false); }}
                            >
                              Unirme a nueva peluquería
                            </Button>
                          )}
                          <div className="pt-2 mt-2 border-t border-gray-800">
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => {
                                setShowHelpCenter(true);
                                setShowAppMenu(false);
                              }}
                            >
                              Centro de ayuda
                            </Button>
                          </div>
                          <Button variant="ghost" className="w-full justify-start text-red-400 hover:text-red-300" onClick={handleLogout}>
                            Cerrar sesión
                          </Button>
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
                <div className="flex items-center gap-2 sm:gap-3">
                  <Avatar className="ring-2 ring-amber-500/50 w-8 h-8 sm:w-10 sm:h-10">
                    <AvatarImage
                      src={user?.user_type === 'barber' && barbershopData
                        ? getBarbershopAvatarUrl(barbershopData)
                        : getUserAvatarUrl(user)}
                      alt={user.name}
                    />
                    <AvatarFallback className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold text-xs sm:text-sm">
                      {user.name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-semibold text-white">{user.name}</p>
                    <p className="text-xs text-amber-300">
                      {user.user_type === 'client' ? '👤 Cliente' : user.user_type === 'admin' ? '🛡️ Administrador' : '✂️ Barbero'}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleLogout} 
                  size="sm" 
                  variant="ghost"
                  className="text-gray-300 hover:text-white hover:bg-white/10 px-2 sm:px-4"
                >
                  <LogOut className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Salir</span>
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 sm:gap-3">
                <button 
                  onClick={() => {
                    recordVisitEvent('click_login');
                    setShowLogin(true);
                  }}
                  className="btn-silver-premium btn-premium px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                >
                  <LogIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Acceder</span>
                </button>

                <button 
                  onClick={() => {
                    console.log('🔥 HEADER REGISTER CLICKED!');
                    setShowRegister(true);
                  }}
                  className="btn-gold-premium btn-premium rounded-lg sm:rounded-xl px-3 sm:px-4 py-1.5 sm:py-2 flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                >
                  <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Registrarse</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {maintenanceBanner.enabled && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-sm px-4 py-2 text-center">
          {maintenanceBanner.message || 'Estamos en mantenimiento. Intenta más tarde.'}
        </div>
      )}

      {/* Alerts */}
      {error && (
        <Alert className="mx-4 mt-4 border-red-400 bg-red-900/20">
          <AlertDescription className="text-red-400">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm">{error}</span>
              {error.toLowerCase().includes('no está verificado') && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-400 text-red-200 hover:bg-red-500/20"
                    onClick={handleResendVerification}
                    disabled={verificationResendLoading}
                  >
                    {verificationResendLoading ? 'Reenviando...' : 'Reenviar mail de verificación'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-400 text-red-200 hover:bg-red-500/20"
                    onClick={handleContactSupport}
                  >
                    Contactar soporte
                  </Button>
                </div>
              )}
            </div>
            {verificationResendMessage && error.toLowerCase().includes('no está verificado') && (
              <div className="mt-2 text-xs text-red-200">{verificationResendMessage}</div>
            )}
          </AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mx-4 mt-4 border-green-400 bg-green-900/20">
          <AlertDescription className="text-green-400">{success}</AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <main className="p-4 pb-20 md:pb-4">
        {!user ? (
          <div className="min-h-screen bg-black relative overflow-hidden">
            {/* Animated Gold/Silver Background */}
            <div className="absolute inset-0 w-full h-full">
              <div className="absolute top-0 -left-4 w-96 h-96 bg-gradient-to-r from-amber-500/30 to-yellow-500/30 rounded-full mix-blend-lighten filter blur-3xl opacity-50 animate-blob"></div>
              <div className="absolute top-0 -right-4 w-96 h-96 bg-gradient-to-r from-gray-400/20 to-gray-300/20 rounded-full mix-blend-lighten filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 left-20 w-96 h-96 bg-gradient-to-r from-yellow-600/20 to-amber-400/20 rounded-full mix-blend-lighten filter blur-3xl opacity-50 animate-blob animation-delay-4000"></div>
            </div>
            
            {/* Hero Section */}
            <div className="relative z-10 min-h-screen">
              {/* Main Hero - MOBILE RESPONSIVE */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
                <div className="text-center mb-12 sm:mb-16">
                  {/* Logo */}
                  <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl sm:rounded-3xl mb-6 sm:mb-8 shadow-2xl pulse-gold">
                    <Scissors className="w-10 h-10 sm:w-12 sm:h-12 text-black" />
                  </div>
                  
                  {/* Title with gold shine - Responsive */}
                  <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-4 sm:mb-6 gold-shine px-2">
                    CÓRTATE.CL
                  </h1>
                  
                  <p className="text-xl sm:text-2xl md:text-3xl text-transparent bg-clip-text bg-gradient-to-r from-gray-200 via-gray-100 to-gray-300 mb-3 sm:mb-4 font-semibold px-4">
                    Encuentra un peluquero de forma rápida e informada
                  </p>
                  
                  <p className="text-gray-400 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed px-6">
                    La plataforma que une a peluquerías con clientes en todo Chile.
                    Agenda tu corte o encuentra al primer barbero disponible. Siempre tendrás una opción.
                  </p>
                </div>

                {/* CTA Buttons with shine effect - MOBILE RESPONSIVE */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-12 sm:mb-16 md:mb-20 px-4">
                  <button 
                    onClick={() => { recordVisitEvent('click_login'); setShowLogin(true); }}
                    className="group relative w-full sm:w-auto bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 border-2 border-gray-500 text-white px-8 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 hover:scale-105 silver-glow flex items-center justify-center gap-2 sm:gap-3"
                  >
                    <LogIn className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-pulse" />
                    Iniciar Sesión
                  </button>
                  
                  <button 
                    onClick={() => setShowRegister(true)}
                    className="group relative w-full sm:w-auto bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black px-8 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg shadow-2xl transition-all duration-300 hover:scale-105 gold-glow-hover flex items-center justify-center gap-2 sm:gap-3"
                  >
                    <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-pulse" />
                    Registrarse Gratis
                  </button>
                </div>

                {/* Premium Features Grid - MOBILE RESPONSIVE */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16 md:mb-20 px-4">
                  {/* Feature 1 - Gold */}
                  <div className="group relative bg-gradient-to-br from-gray-900 to-black rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-amber-500/30 hover:border-amber-500 transition-all duration-300 hover:scale-105 gold-border-shine">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-xl sparkle">
                      <MapPin className="w-7 h-7 sm:w-8 sm:h-8 text-black" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-400 mb-2 sm:mb-3">
                      Encuentra por tu zona
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Con filtros rápidos puedes buscar cortes con tus preferencias de distancia y presupuesto.
                    </p>
                  </div>

                  {/* Feature 2 - Silver */}
                  <div className="group relative bg-gradient-to-br from-gray-900 to-black rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-gray-500/30 hover:border-gray-400 transition-all duration-300 hover:scale-105">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-gray-600 to-gray-400 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-xl">
                      <Zap className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-gray-100 mb-2 sm:mb-3">
                      Corte express
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      ¿Necesitas un corte rápido? Pídelo y la alerta la reciben barberos disponibles en tu zona.
                    </p>
                  </div>

                  {/* Feature 3 - Gold */}
                  <div className="group relative bg-gradient-to-br from-gray-900 to-black rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-amber-500/30 hover:border-amber-500 transition-all duration-300 hover:scale-105 gold-border-shine">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-xl sparkle">
                      <Star className="w-7 h-7 sm:w-8 sm:h-8 text-black" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-400 mb-2 sm:mb-3">
                      Sistema de reviews
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Puntúa tus cortes, sube fotos y revisa el ranking semanal de barberías y barberos.
                    </p>
                  </div>

                  {/* Feature 4 - Silver */}
                  <div className="group relative bg-gradient-to-br from-gray-900 to-black rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 border-gray-500/30 hover:border-gray-400 transition-all duration-300 hover:scale-105">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-gray-600 to-gray-400 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-xl">
                      <MapIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-gray-100 mb-2 sm:mb-3">
                      Cortes a domicilio
                    </h3>
                    <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                      Servicios a domicilio, ya sean instantáneos o reservados, para tu máxima comodidad.
                    </p>
                  </div>
                </div>

                {/* Stats Section with metallic shine - MOBILE RESPONSIVE */}
                <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 rounded-2xl md:rounded-3xl border border-amber-500/30 p-6 md:p-10">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-400 mb-2">
                        500+
                      </div>
                      <div className="text-gray-400 font-semibold text-sm sm:text-base">Barberos Profesionales</div>
                    </div>
                    <div className="text-center sm:border-x border-gray-700 py-4 sm:py-0">
                      <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-gray-100 mb-2">
                        10K+
                      </div>
                      <div className="text-gray-400 font-semibold text-sm sm:text-base">Cortes Realizados</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-400 mb-2">
                        4.9⭐
                      </div>
                      <div className="text-gray-400 font-semibold text-sm sm:text-base">Calificación Promedio</div>
                    </div>
                  </div>
                </div>

                {/* Extra CTA */}
                <div className="text-center mt-16">
                  <p className="text-gray-500 text-sm mb-4">¿Eres barbero profesional?</p>
                  <button 
                    onClick={() => setShowRegister(true)}
                    className="text-amber-400 hover:text-amber-300 font-semibold underline underline-offset-4 transition-colors"
                  >
                    Únete a nuestra red de profesionales →
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Suspense
            fallback={
              <div className="min-h-[40vh] flex items-center justify-center">
                <LoadingSpinner />
              </div>
            }
          >
            {user.user_type === 'admin' ? (
              <AdminInterface
                user={user}
                adminTab={adminTab}
                setAdminTab={setAdminTab}
                adminUsers={adminUsers}
                adminBarbershops={adminBarbershops}
                adminReviews={adminReviews}
                adminReviewsLoading={adminReviewsLoading}
                adminUserSearch={adminUserSearch}
                setAdminUserSearch={setAdminUserSearch}
                adminUserSearchDebounced={debouncedAdminUserSearch}
                adminBarbershopSearch={adminBarbershopSearch}
                setAdminBarbershopSearch={setAdminBarbershopSearch}
                adminBarbershopSearchDebounced={debouncedAdminBarbershopSearch}
                adminBarbershopStatus={adminBarbershopStatus}
                setAdminBarbershopStatus={setAdminBarbershopStatus}
                adminBarbershopRatingMin={adminBarbershopRatingMin}
                setAdminBarbershopRatingMin={setAdminBarbershopRatingMin}
                loadAdminBarbershops={loadAdminBarbershops}
                getBarbershopAvatarUrl={getBarbershopAvatarUrl}
                handleAdminEditBarbershop={handleAdminEditBarbershop}
                setAdminMessageBarbershop={setAdminMessageBarbershop}
                setAdminMessageDraft={setAdminMessageDraft}
                handleAdminDeleteBarbershop={handleAdminDeleteBarbershop}
                adminUserTypeFilter={adminUserTypeFilter}
                setAdminUserTypeFilter={setAdminUserTypeFilter}
                adminUserBlockedFilter={adminUserBlockedFilter}
                setAdminUserBlockedFilter={setAdminUserBlockedFilter}
                loadAdminUsers={loadAdminUsers}
                exportAdminUsersCsv={exportAdminUsersCsv}
                handleAdminUpdateUser={handleAdminUpdateUser}
                loadAdminUserStats={loadAdminUserStats}
                adminUserStats={adminUserStats}
                adminPasswordDrafts={adminPasswordDrafts}
                setAdminPasswordDrafts={setAdminPasswordDrafts}
                handleAdminResetPassword={handleAdminResetPassword}
                adminBlockDrafts={adminBlockDrafts}
                setAdminBlockDrafts={setAdminBlockDrafts}
                handleAdminSaveBlock={handleAdminSaveBlock}
                handleAdminSetBarberBarbershop={handleAdminSetBarberBarbershop}
                adminReviewFilters={adminReviewFilters}
                setAdminReviewFilters={setAdminReviewFilters}
                loadAdminReviews={loadAdminReviews}
                exportAdminReviewsCsv={exportAdminReviewsCsv}
                handleAdminDeleteReview={handleAdminDeleteReview}
                handleAdminFlagReview={handleAdminFlagReview}
                adminReviewNotes={adminReviewNotes}
                setAdminReviewNotes={setAdminReviewNotes}
                maintenanceEnabled={maintenanceEnabled}
                setMaintenanceEnabled={setMaintenanceEnabled}
                maintenanceMessage={maintenanceMessage}
                setMaintenanceMessage={setMaintenanceMessage}
                updateAdminMaintenance={updateAdminMaintenance}
                adminMetricsStart={adminMetricsStart}
                setAdminMetricsStart={setAdminMetricsStart}
                adminMetricsEnd={adminMetricsEnd}
                setAdminMetricsEnd={setAdminMetricsEnd}
                loadAdminMetrics={loadAdminMetrics}
                adminMetrics={adminMetrics}
                adminSearchQuery={adminSearchQuery}
                setAdminSearchQuery={setAdminSearchQuery}
                handleAdminSearch={handleAdminSearch}
                adminSearchResults={adminSearchResults}
                adminNotifications={adminNotifications}
                markAdminNotificationRead={markAdminNotificationRead}
                adminAuditLogs={adminAuditLogs}
                adminEventType={adminEventType}
                setAdminEventType={setAdminEventType}
                adminEventId={adminEventId}
                setAdminEventId={setAdminEventId}
                handleAdminDeleteEvent={handleAdminDeleteEvent}
                MobileTabBar={MobileTabBar}
                adminVisitStats={adminVisitStats}
                adminVisitEvents={adminVisitEvents}
                adminVisitsStart={adminVisitsStart}
                setAdminVisitsStart={setAdminVisitsStart}
                adminVisitsEnd={adminVisitsEnd}
                setAdminVisitsEnd={setAdminVisitsEnd}
                loadAdminVisitStats={loadAdminVisitStats}
                loadAdminVisitEvents={loadAdminVisitEvents}
                exportAdminVisitsCsv={exportAdminVisitsCsv}
                BACKEND_URL={BACKEND_URL}
              />
            ) : user.user_type === 'client' ? (
              <ClientInterface
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                map={map}
                barbershops={barbershops}
                selectedBarbershop={selectedBarbershop}
                setSelectedBarbershop={setSelectedBarbershop}
                userLocation={userLocation}
                bookingHistory={bookingHistory}
                user={user}
                BACKEND_URL={BACKEND_URL}
                handleLogout={handleLogout}
                upcomingBookings={upcomingBookings}
                showTopRanking={showTopRanking}
                setShowTopRanking={setShowTopRanking}
                rankingBarbershops={rankingBarbershops}
                loadingBarbershops={loadingBarbershops}
                clientAcceptedQuickCuts={clientAcceptedQuickCuts}
                BarbershopCard={BarbershopCard}
                barbershopCardProps={barbershopCardPropsMemo}
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                QuickCutSection={QuickCutSection}
                ClientProfile={ClientProfile}
                MobileTabBar={MobileTabBar}
                setSelectedUpcomingBooking={setSelectedUpcomingBooking}
                setSelectedPastBooking={setSelectedPastBooking}
              />
            ) : (
              <BarberInterface
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                quickCutRequests={quickCutRequests}
                barberBookings={barberBookings}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                appointments={appointments}
                loadBarberData={loadBarberData}
                user={user}
                BACKEND_URL={BACKEND_URL}
                barbershopData={barbershopData}
                barberBarbershopsList={barberBarbershopsList}
                setActiveBarbershop={setActiveBarbershop}
                bookingRequests={bookingRequests}
                barberAcceptedQuickCuts={barberAcceptedQuickCuts}
                barberCompletedQuickCuts={barberCompletedQuickCuts}
                setSelectedBookingChat={setSelectedBookingChat}
                loadClientProfileForBarber={loadClientProfileForBarber}
                hasRequestNotification={hasRequestNotification}
                handleLogout={handleLogout}
                isMobile={isMobile}
                showCreateBarbershop={showCreateBarbershop}
                setShowCreateBarbershop={setShowCreateBarbershop}
                mobileStage={mobileStage}
                setMobileStage={setMobileStage}
                mobileRequest={mobileRequest}
                setMobileRequest={setMobileRequest}
                mobileAcceptedUntil={mobileAcceptedUntil}
                setMobileAcceptedUntil={setMobileAcceptedUntil}
                historyFilter={historyFilter}
                setHistoryFilter={setHistoryFilter}
                historyStartDate={historyStartDate}
                setHistoryStartDate={setHistoryStartDate}
                historyEndDate={historyEndDate}
                setHistoryEndDate={setHistoryEndDate}
                historyFiltersOpen={historyFiltersOpen}
                setHistoryFiltersOpen={setHistoryFiltersOpen}
                loadingBarberHistory={loadingBarberHistory}
                showNotification={showNotification}
                BarberCalendar={BarberCalendar}
                BarberRequests={BarberRequests}
                BarberBusiness={BarberBusiness}
                MobileTabBar={MobileTabBar}
              />
            )}
          </Suspense>
        )}
      </main>

      {/* Login Modal - MOBILE RESPONSIVE */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl w-[95%] sm:w-full max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl sm:text-2xl font-bold">Iniciar Sesión</DialogTitle>
          </DialogHeader>
          <LoginForm
            BACKEND_URL={BACKEND_URL}
            handleLogin={handleLogin}
            showNotification={showNotification}
            setResetEmail={setResetEmail}
            setResetToken={setResetToken}
            setResetPasswordDraft={setResetPasswordDraft}
            setResetPasswordConfirm={setResetPasswordConfirm}
            setResetMessage={setResetMessage}
            setShowResetPassword={setShowResetPassword}
            setShowRegister={setShowRegister}
            setShowLogin={setShowLogin}
          />
        </DialogContent>
      </Dialog>

      {/* Register Modal - MOBILE RESPONSIVE */}
      <Dialog open={showRegister} onOpenChange={(open) => { setShowRegister(open); if (!open) setRegisterInviteBarbershopId(null); }}>
        <DialogContent className="bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl w-[95%] sm:w-full max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl sm:text-2xl font-bold">Crear Cuenta</DialogTitle>
          </DialogHeader>
          <RegisterForm />
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl w-[95%] sm:w-full max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-xl sm:text-2xl font-bold">
              Recuperar contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {resetToken ? (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Nueva contraseña</label>
                  <Input
                    type="password"
                    value={resetPasswordDraft}
                    onChange={(e) => setResetPasswordDraft(e.target.value)}
                    className="bg-white/5 border-white/20 text-white placeholder-gray-400 h-11 rounded-xl"
                    placeholder="Ingresa tu nueva contraseña"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Confirmar contraseña</label>
                  <Input
                    type="password"
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    className="bg-white/5 border-white/20 text-white placeholder-gray-400 h-11 rounded-xl"
                    placeholder="Confirma tu nueva contraseña"
                  />
                </div>
                {resetMessage && (
                  <div className="text-xs text-amber-200">{resetMessage}</div>
                )}
                <Button
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  className="w-full bg-amber-500 hover:bg-amber-700"
                >
                  {resetLoading ? 'Guardando...' : 'Cambiar contraseña'}
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">Email</label>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="bg-white/5 border-white/20 text-white placeholder-gray-400 h-11 rounded-xl"
                    placeholder="tucorreo@ejemplo.com"
                  />
                </div>
                {resetMessage && (
                  <div className="text-xs text-amber-200">{resetMessage}</div>
                )}
                <Button
                  onClick={handleRequestPasswordReset}
                  disabled={resetLoading}
                  className="w-full bg-amber-500 hover:bg-amber-700"
                >
                  {resetLoading ? 'Enviando...' : 'Enviar enlace de recuperación'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      {showBooking && bookingBarbershop && (
        <BookingModal
          showBooking={showBooking}
          setShowBooking={setShowBooking}
          bookingBarbershop={bookingBarbershop}
          setBookingBarbershop={setBookingBarbershop}
          loadClientHistory={loadClientHistory}
          loadUpcomingBookings={loadUpcomingBookings}
          setSuccess={setSuccess}
          loading={loading}
          setLoading={setLoading}
          BACKEND_URL={BACKEND_URL}
        />
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal 
          showReviewModal={showReviewModal}
          setShowReviewModal={setShowReviewModal}
          reviewData={reviewData}
          setReviewData={setReviewData}
          completedQuickCut={completedQuickCut}
          setCompletedQuickCut={setCompletedQuickCut}
          setQuickCutStatus={setQuickCutStatus}
          setMatchedBarber={setMatchedBarber}
          setSuccess={setSuccess}
          loadClientHistory={loadClientHistory}
          loadClientReviews={loadClientReviews}
          setError={setError}
          loading={loading}
          setLoading={setLoading}
          BACKEND_URL={BACKEND_URL}
          uploadImageToImageKit={uploadImageToImageKit}
        />
      )}

      {/* Edit Profile Modal */}
      {showSettings && user && (
        <Dialog open={showSettings} onOpenChange={setShowSettings}>
          <DialogContent className="modal-premium text-white max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-premium">
            <DialogHeader>
              <DialogTitle className="text-premium-heading text-2xl mb-2">
                ✏️ Editar Perfil
              </DialogTitle>
            </DialogHeader>

            <ProfileManagement 
              user={user}
              barbershop={barbershopData}
              onUpdate={(updatedUser) => {
                setUser(applyDefaultAvatar(updatedUser));
                setShowSettings(false);
                showNotification('Perfil Actualizado', 'Tus cambios han sido guardados exitosamente', 'success');
              }}
              BACKEND_URL={BACKEND_URL}
              onNotify={showNotification}
              googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Client Profile Modal for Barber */}
      {selectedClientProfile && (
        <Dialog
          open={!!selectedClientProfile}
          onOpenChange={() => {
            setSelectedClientProfile(null);
            setClientProfileReviews([]);
            setClientProfileStats(null);
          }}
        >
          <DialogContent className="bg-gray-900 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Perfil de {selectedClientProfile.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Client Info */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={getUserAvatarUrl(selectedClientProfile)} alt={selectedClientProfile.name} />
                    <AvatarFallback className="bg-amber-500 text-white text-xl">
                      {selectedClientProfile.name?.[0] || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{selectedClientProfile.name}</h3>
                    <p className="text-gray-400 text-sm">{clientProfileReviews.length} Reseña{clientProfileReviews.length !== 1 ? 's' : ''}</p>
                    <p className="text-gray-400 text-sm">
                      Solicitudes: {clientProfileStats?.total_requests ?? 0}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${star <= (clientProfileStats?.score || 0) ? 'fill-amber-400 text-amber-400' : 'text-gray-600'}`}
                          />
                        ))}
                  </div>
                      <span className="text-xs text-gray-500">
                        {clientProfileStats?.score ? `${clientProfileStats.score} / 5` : 'Sin score'}
                      </span>
                    </div>
                    <div className="mt-2">
                      <Progress value={Math.round(((clientProfileStats?.score || 0) / 5) * 100)} />
                      <p className="text-xs text-gray-500 mt-1">Score visual del cliente</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reviews - Same format as barber */}
              <div>
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                  Reseñas del Cliente
                </h4>
                
                {clientProfileReviews.length === 0 ? (
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-8 text-center">
                      <Star className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-400">Este cliente aún no ha dejado reseñas</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {clientProfileReviews.map((review) => (
                      <Card key={review.id} className="bg-gray-800 border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 flex-1">
                              <Avatar className="w-10 h-10">
                              <AvatarImage src={getUserAvatarUrl(selectedClientProfile)} alt={selectedClientProfile.name} />
                                <AvatarFallback className="bg-amber-500 text-white">
                                  {selectedClientProfile.name?.[0] || 'C'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="text-white font-medium">{selectedClientProfile.name}</p>
                                  <div className="flex">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={`w-4 h-4 ${
                                          star <= review.rating
                                            ? 'fill-amber-400 text-amber-400'
                                            : 'text-gray-400'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                  {review.published === false && (
                                    <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs ml-2">
                                      No publicada
                                    </Badge>
                                  )}
                                  {review.hidden === true && (
                                    <Badge variant="outline" className="text-red-400 border-red-400 text-xs ml-2">
                                      Ocultada
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-gray-400 text-xs">
                                  {formatDateWithTZ(review.created_at, {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                                <p className="text-gray-500 text-xs mt-1">
                                  {review.barbershop?.name || 'Barbería'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {review.comment && (
                            <p className="text-gray-300 text-sm mb-3">{review.comment}</p>
                          )}

                          {review.barber_response && (
                            <div className="bg-gray-900 p-3 rounded-lg mt-3 border-l-4 border-blue-500">
                              <div className="flex items-center gap-2 mb-2">
                                <Reply className="w-4 h-4 text-blue-400" />
                                <span className="text-blue-300 text-sm font-medium">
                                  Tu respuesta
                                </span>
                              </div>
                              <p className="text-gray-300 text-sm">{review.barber_response}</p>
                              {review.response_date && (
                                <p className="text-gray-500 text-xs mt-2">
                                  {formatDateWithTZ(review.response_date, {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reviews Modal */}
      {showReviewsModal && user?.user_type === 'client' && (
        <Dialog open={showReviewsModal} onOpenChange={setShowReviewsModal}>
          <DialogContent className="modal-premium text-white max-w-5xl max-h-[85vh] overflow-y-auto scrollbar-premium">
            <DialogHeader>
              <DialogTitle className="text-premium-heading text-xl md:text-2xl mb-2">
                ⭐ Reseñas de {selectedBarbershop?.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Lista de reseñas publicadas de la barbería.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {barbershopReviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400 text-lg">Aún no hay reseñas para esta barbería</p>
                  <p className="text-gray-500 text-sm mt-2">Sé el primero en dejar tu opinión</p>
                </div>
              ) : (
                (() => {
                  const REVIEWS_PER_PAGE = 8;
                  const totalPages = Math.max(1, Math.ceil(barbershopReviews.length / REVIEWS_PER_PAGE));
                  const startIndex = (reviewsPage - 1) * REVIEWS_PER_PAGE;
                  const pagedReviews = barbershopReviews.slice(startIndex, startIndex + REVIEWS_PER_PAGE);

                  return (
                    <>
                      <div className="grid gap-4 md:grid-cols-2">
                        {pagedReviews.map((review, index) => (
                          <div key={`${review.id || index}`} className="card-premium p-4 transition-premium bg-gray-900 border border-gray-700">
                            <div className="flex items-start gap-3">
                              <Avatar className="w-10 h-10">
                        <AvatarImage src={review.client_avatar} />
                        <AvatarFallback className="bg-gray-700 text-white">
                          {review.client_name?.[0] || 'C'}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="text-amber-200 font-semibold text-sm md:text-base">
                            {review.client_name || 'Cliente'}
                          </h4>
                                  <span className="text-gray-500 text-xs md:text-sm">
                            {formatDateWithTZ(review.created_at)}
                          </span>
                        </div>

                                <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                                      className={`w-4 h-4 ${
                                star <= review.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                                  <span className="text-amber-300 ml-2 text-xs font-semibold">
                            {review.rating}.0
                          </span>
                        </div>

                                <p className="text-gray-300 text-sm leading-relaxed">
                          {review.comment}
                        </p>

                        {review.images && review.images.length > 0 && (
                                  <div className="mt-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-amber-500 text-amber-300 hover:bg-amber-500/20"
                                      onClick={() => {
                                        setReviewImagesPreview(review.images.map(getImageUrl));
                                        setShowReviewImagesModal(true);
                                      }}
                                    >
                                      Ver fotos ({review.images.length})
                                    </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="flex items-center justify-between gap-3 mt-4">
                          <Button
                            variant="outline"
                            className="border-gray-700 text-amber-300 hover:bg-gray-800"
                            onClick={() => setReviewsPage((prev) => Math.max(1, prev - 1))}
                            disabled={reviewsPage === 1}
                          >
                            Anterior
                          </Button>
                          <div className="text-gray-400 text-sm">
                            Página {reviewsPage} de {totalPages}
                          </div>
                          <Button
                            variant="outline"
                            className="border-gray-700 text-amber-300 hover:bg-gray-800"
                            onClick={() => setReviewsPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={reviewsPage === totalPages}
                          >
                            Siguiente
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-800">
              <Button
                onClick={() => setShowReviewsModal(false)}
                className="w-full bg-amber-500 text-black hover:bg-amber-600"
              >
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showReviewImagesModal && (
        <Dialog open={showReviewImagesModal} onOpenChange={setShowReviewImagesModal}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Fotos de la reseña</DialogTitle>
              <DialogDescription className="sr-only">
                Galería de fotos adjuntadas por el cliente.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {reviewImagesPreview.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-700"
                  onClick={() => window.open(img, '_blank')}
                />
              ))}
            </div>
            <div className="mt-4">
              <Button className="w-full bg-amber-500 text-black hover:bg-amber-600" onClick={() => setShowReviewImagesModal(false)}>
                Cerrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Selected Barbershop Modal */}
      {selectedBarbershop && (
        <Dialog open={!!selectedBarbershop} onOpenChange={() => setSelectedBarbershop(null)}>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-white">{selectedBarbershop.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-800 overflow-hidden">
                <div className="h-40 md:h-52 bg-gray-800">
                  {selectedBarbershop.cover_image ? (
                    <img src={selectedBarbershop.cover_image} alt="Portada barbería" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                      Portada no disponible
                    </div>
                  )}
                </div>
                <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <Avatar className="w-16 h-16">
                    <AvatarImage src={getBarbershopAvatarUrl(selectedBarbershop)} alt={selectedBarbershop.name} />
                  <AvatarFallback>{selectedBarbershop.name[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedBarbershop.name}</h3>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="text-white">{selectedBarbershop.rating || 0} ({selectedBarbershop.reviews_count || 0} reseñas)</span>
                  </div>
                    {selectedBarbershop.solo_agenda && (
                      <Badge className="mt-2 bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        Solo agenda
                      </Badge>
                    )}
                  <p className="text-gray-400">{selectedBarbershop.address}</p>
                  {selectedBarbershop.phone && (
                    <p className="text-gray-400 flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {selectedBarbershop.phone}
                    </p>
                  )}
                  </div>
                </div>
              </div>
              
              {selectedBarbershop.description && (
                <p className="text-gray-300">{selectedBarbershop.description}</p>
              )}
              {selectedBarbershopEta && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200">
                  {selectedBarbershopEta.queue_count > 0 ? (
                    <span>Te atendemos en ~{selectedBarbershopEta.estimated_minutes} min</span>
                  ) : (
                    <span>Sin espera estimada en este momento</span>
                  )}
                </div>
              )}

              {selectedBarbershop.services && selectedBarbershop.services.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Servicios</h4>
                  <div className="space-y-2">
                    {selectedBarbershop.services.map((service, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-800 rounded">
                        <span className="text-white">{service.name}</span>
                        <div className="text-amber-400">
                          ${service.price?.toLocaleString()} - {service.duration}min
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedBarbershop.portfolio_images && selectedBarbershop.portfolio_images.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Portafolio</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedBarbershop.portfolio_images.map((imageUrl) => (
                      <div key={imageUrl} className="rounded-lg overflow-hidden border border-gray-700">
                        <img src={imageUrl} alt="Portafolio" loading="lazy" decoding="async" className="w-full h-24 object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  className="flex-1 btn-premium-gold"
                  onClick={() => {
                    openBookingModal(selectedBarbershop);
                  }}
                  disabled={selectedBarbershop?.accepting_bookings === false}
                >
                  {selectedBarbershop?.accepting_bookings === false ? 'Reservas pausadas' : 'Reservar Cita'}
                </Button>
                {user?.user_type === 'client' && (
                <Button 
                  className="flex-1 btn-premium-silver"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${BACKEND_URL}/api/reviews/barbershop/${selectedBarbershop.id}`);
                      setBarbershopReviews(response.data.reviews || []);
                      setShowReviewsModal(true);
                    } catch (error) {
                      console.error('Error loading reviews:', error);
                      showNotification('Error', 'No se pudieron cargar las reseñas', 'destructive');
                    }
                  }}
                >
                  Ver Reseñas ({selectedBarbershop.reviews_count || 0})
                </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {selectedUpcomingBooking && user?.user_type === 'client' && (
        <Dialog open={!!selectedUpcomingBooking} onOpenChange={() => setSelectedUpcomingBooking(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md w-[94vw] sm:w-full">
            <DialogHeader className="pr-16 text-left">
              <DialogTitle className="text-white">Detalle de tu reserva</DialogTitle>
            </DialogHeader>
            <button
              type="button"
              onClick={() => {
                const calendarUrl =
                  selectedUpcomingBooking.calendar_url ||
                  `${BACKEND_URL}/api/bookings/${selectedUpcomingBooking.id}/calendar`;
                openCalendarOptionsForBooking(selectedUpcomingBooking, calendarUrl);
              }}
              title="Agregar a calendario"
              className="absolute top-4 right-14 h-9 w-9 rounded-full border border-amber-400/40 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 flex items-center justify-center"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <div className="space-y-4 text-sm text-gray-200">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                <p className="text-white font-medium">{selectedUpcomingBooking.service || 'Corte de pelo'}</p>
                <p className="text-gray-400">
                  {formatDateWithTZ(selectedUpcomingBooking.date || selectedUpcomingBooking.created_at, {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}{' '}
                  •{' '}
                  {formatTimeWithTZ(selectedUpcomingBooking.date || selectedUpcomingBooking.created_at, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedUpcomingBooking.barbershop?.name && (
                  <p className="text-gray-300 mt-2">
                    <strong>Barbería:</strong> {selectedUpcomingBooking.barbershop.name}
                  </p>
                )}
                {selectedUpcomingBooking.barbershop?.address && (
                  <p className="text-gray-400 text-xs mt-1">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {selectedUpcomingBooking.barbershop.address}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    setSelectedBookingChat({
                      id: selectedUpcomingBooking.id,
                      status: selectedUpcomingBooking.status,
                      service: selectedUpcomingBooking.service,
                      type: 'booking',
                      barber: selectedUpcomingBooking.barber,
                      barbershop: selectedUpcomingBooking.barbershop,
                      price: selectedUpcomingBooking.price
                    });
                    setSelectedUpcomingBooking(null);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 min-w-[120px]"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chatear
                </Button>
                <Button
                  onClick={() => handleBookingArrived(selectedUpcomingBooking)}
                  className="flex-1 bg-green-600 hover:bg-green-700 min-w-[120px]"
                  disabled={selectedUpcomingBooking.client_arrived}
                >
                  {selectedUpcomingBooking.client_arrived ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Ya llegaste
                    </>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 mr-2" />
                      He llegado
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleCancelBookingByClient(selectedUpcomingBooking)}
                  variant="outline"
                  className="flex-1 border-red-400 text-red-400 hover:bg-red-400 hover:text-white min-w-[120px]"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                {selectedUpcomingBooking.barbershop?.address && (
                  <Button
                    onClick={() => {
                      window.open(`https://maps.google.com/maps?q=${encodeURIComponent(selectedUpcomingBooking.barbershop.address)}`, '_blank');
                    }}
                    variant="outline"
                    className="border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Ver mapa
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {selectedPastBooking && user?.user_type === 'client' && (
        <Dialog open={!!selectedPastBooking} onOpenChange={() => setSelectedPastBooking(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-md w-[94vw] sm:w-full">
            <DialogHeader className="text-left">
              <DialogTitle className="text-white">Detalle de tu reserva</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm text-gray-200">
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                <p className="text-white font-medium">{selectedPastBooking.service || 'Servicio'}</p>
                {(selectedPastBooking.date || selectedPastBooking.created_at) && (
                  <p className="text-gray-400">
                    {formatDateWithTZ(selectedPastBooking.date || selectedPastBooking.created_at, {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}{' '}
                    •{' '}
                    {formatTimeWithTZ(selectedPastBooking.date || selectedPastBooking.created_at, {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
                {selectedPastBooking.barbershop?.name && (
                  <p className="text-gray-300 mt-2">
                    <strong>Barbería:</strong> {selectedPastBooking.barbershop.name}
                  </p>
                )}
                {selectedPastBooking.barbershop?.address && (
                  <p className="text-gray-400 text-xs mt-1">
                    <MapPin className="w-3 h-3 inline mr-1" />
                    {selectedPastBooking.barbershop.address}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    const shop = getBarbershopFromBooking(selectedPastBooking);
                    if (shop) {
                      setSelectedBarbershop(shop);
                      setSelectedPastBooking(null);
                    } else {
                      showNotification('⚠️ Aviso', 'No se pudo encontrar la barbería', 'info');
                    }
                  }}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-200"
                >
                  Ver barbería
                </Button>
                <Button
                  onClick={() => {
                    const shop = getBarbershopFromBooking(selectedPastBooking);
                    if (shop) {
                      openBookingModal(shop);
                      setSelectedPastBooking(null);
                    } else {
                      showNotification('⚠️ Aviso', 'No se pudo abrir la reserva', 'info');
                    }
                  }}
                  className="flex-1 bg-amber-500 hover:bg-amber-700 text-black"
                >
                  Reservar denuevo
                </Button>
                <Button
                  onClick={() => {
                    openReviewForBooking(selectedPastBooking);
                    setSelectedPastBooking(null);
                  }}
                  variant="outline"
                  className="flex-1 border-blue-400 text-blue-300 hover:bg-blue-500/10"
                >
                  {selectedPastBooking.review ? 'Dejar otra reseña' : 'Dejar reseña'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showNotifications && user && (
        <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Notificaciones</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-between gap-2 mb-3">
              <Button
                variant="outline"
                className="border-gray-700 text-gray-200"
                onClick={() => setUserNotifications([])}
              >
                Limpiar
              </Button>
              <Button
                variant="outline"
                className="border-gray-700 text-gray-200"
                onClick={() => setUserNotifications([])}
              >
                Marcar como leídas
              </Button>
            </div>
            <div className="space-y-3">
              {groupedNotifications.length === 0 ? (
                <div className="text-center text-gray-400 py-6">Sin notificaciones aún</div>
              ) : (
                groupedNotifications.map((note) => (
                  <div key={note.id} className="rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-3 text-sm text-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-white">{note.title}</div>
                      {note.count > 1 && (
                        <div className="text-xs text-amber-300">x{note.count}</div>
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">{note.message}</div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {adminEditBarbershop && (
        <Dialog open={!!adminEditBarbershop} onOpenChange={() => setAdminEditBarbershop(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Editar barbería</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-gray-200">
              <div>
                <label className="text-gray-300 text-xs">Foto de perfil</label>
                <div className="flex items-center gap-3 mt-2">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={adminEditBarbershopDraft.profile_image || getBarbershopAvatarUrl(adminEditBarbershop)} />
                    <AvatarFallback className="bg-amber-500 text-black">
                      {adminEditBarbershopDraft.name?.[0] || 'B'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-700 text-gray-200"
                      onClick={() => adminProfileImageInputRef.current?.click()}
                      disabled={adminImageUploading}
                    >
                      {adminImageUploading ? 'Subiendo...' : 'Subir foto'}
                    </Button>
                    <input
                      ref={adminProfileImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAdminUploadBarbershopImage}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-500">JPG/PNG hasta 5MB</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-gray-300 text-xs">Nombre</label>
                <Input
                  value={adminEditBarbershopDraft.name}
                  onChange={(e) => setAdminEditBarbershopDraft({ ...adminEditBarbershopDraft, name: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 text-xs">Dirección</label>
                <Input
                  ref={adminAddressRef}
                  value={adminEditBarbershopDraft.address}
                  onChange={(e) => {
                    setAdminEditBarbershopDraft({ ...adminEditBarbershopDraft, address: e.target.value });
                    setAdminSelectedPlace(null);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {adminSelectedPlace ? 'Dirección validada' : 'Selecciona una dirección real desde la lista.'}
                </p>
              </div>
              <div>
                <label className="text-gray-300 text-xs">Teléfono</label>
                <Input
                  value={adminEditBarbershopDraft.phone}
                  onChange={(e) => setAdminEditBarbershopDraft({ ...adminEditBarbershopDraft, phone: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <label className="text-gray-300 text-xs">Descripción</label>
                <Textarea
                  value={adminEditBarbershopDraft.description}
                  onChange={(e) => setAdminEditBarbershopDraft({ ...adminEditBarbershopDraft, description: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white"
                  rows={3}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-gray-300 text-xs">Servicios</label>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-700 text-black" onClick={addAdminService}>
                    <Plus className="w-4 h-4 mr-1" />
                    Agregar
                  </Button>
                </div>
                <div className="space-y-2 max-h-52 overflow-y-auto">
                  {(adminEditBarbershopDraft.services || []).length === 0 ? (
                    <div className="text-xs text-gray-500">Sin servicios definidos</div>
                  ) : (
                    adminEditBarbershopDraft.services.map((service, index) => (
                      <div key={`${service.name}-${index}`} className="flex gap-2 items-center bg-gray-900 border border-gray-800 rounded-lg px-2 py-2">
                        <Input
                          value={service.name}
                          onChange={(e) => updateAdminService(index, 'name', e.target.value)}
                          placeholder="Servicio"
                          className="flex-1 bg-gray-800 border-gray-700 text-white"
                        />
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={service.price}
                          onChange={(e) => updateAdminService(index, 'price', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                          placeholder="Precio"
                          className="w-24 bg-gray-800 border-gray-700 text-white"
                        />
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={service.duration}
                          onChange={(e) => updateAdminService(index, 'duration', parseInt(e.target.value.replace(/\D/g, ''), 10) || 0)}
                          placeholder="Min"
                          className="w-16 bg-gray-800 border-gray-700 text-white"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white"
                          onClick={() => removeAdminService(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={adminEditBarbershopDraft.available}
                  onChange={(e) => setAdminEditBarbershopDraft({ ...adminEditBarbershopDraft, available: e.target.checked })}
                />
                <span className="text-gray-300 text-xs">Disponible</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-gray-700 text-gray-200" onClick={() => setAdminEditBarbershop(null)}>
                  Cancelar
                </Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-700" onClick={submitAdminBarbershopUpdate}>
                  Guardar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {adminMessageBarbershop && (
        <Dialog open={!!adminMessageBarbershop} onOpenChange={() => setAdminMessageBarbershop(null)}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Enviar mensaje</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-gray-200">
              <div className="text-gray-300 text-xs">
                Barbería: {adminMessageBarbershop.name}
              </div>
              <Textarea
                value={adminMessageDraft}
                onChange={(e) => setAdminMessageDraft(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white"
                rows={4}
                placeholder="Escribe tu mensaje..."
              />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-gray-700 text-gray-200" onClick={() => setAdminMessageBarbershop(null)}>
                  Cancelar
                </Button>
                <Button className="flex-1 bg-amber-500 hover:bg-amber-700" onClick={handleAdminSendBarbershopMessage}>
                  Enviar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showBarberProfileHub && user?.user_type === 'barber' && (
        <Dialog open={showBarberProfileHub} onOpenChange={setShowBarberProfileHub}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Perfil</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Button variant="outline" className="w-full" onClick={() => { setShowSettings(true); setShowBarberProfileHub(false); }}>
                Editar usuario
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { setActiveTab('business'); setShowBarberProfileHub(false); }}>
                Editar peluquería
              </Button>
              <Button variant="outline" className="w-full text-red-400 border-red-400 hover:bg-red-400 hover:text-white" onClick={deleteBarbershopByOwner}>
                Eliminar peluquería
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { setShowJoinBarbershop(true); setShowBarberProfileHub(false); }}>
                Unirme a una nueva peluquería
              </Button>
              <Button variant="outline" className="w-full" onClick={() => { setShowReviewsModal(true); setShowBarberProfileHub(false); }}>
                Ver reseñas
              </Button>
              <div className="pt-2 border-t border-gray-800">
                <p className="text-xs text-gray-400 mb-2">Solicitudes de unión pendientes</p>
                {joinRequests.length === 0 ? (
                  <div className="text-xs text-gray-500">No hay solicitudes pendientes</div>
                ) : (
                  <div className="space-y-2">
                    {joinRequests.map((req) => (
                      <div key={req.id} className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 text-xs text-gray-200">
                        <div className="flex items-center justify-between">
                          <span>{req.barber?.name || req.barber_name || 'Barbero'}</span>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => acceptJoinRequest(req.id)}>
                              Aceptar
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-400 text-red-400 hover:bg-red-400 hover:text-white" onClick={() => rejectJoinRequest(req.id)}>
                              Rechazar
                            </Button>
                          </div>
                        </div>
                        {req.barber?.email && (
                          <div className="text-gray-500 mt-1">{req.barber.email}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showJoinBarbershop && user?.user_type === 'barber' && (
        <Dialog open={showJoinBarbershop} onOpenChange={setShowJoinBarbershop}>
          <DialogContent className="bg-gray-900 border-gray-700 max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Unirme a una peluquería</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                value={joinSearch}
                onChange={(e) => setJoinSearch(e.target.value)}
                placeholder="Buscar barberías..."
                className="bg-gray-800 border-gray-700 text-white"
              />
              <Button
                variant="outline"
                className="border-gray-700 text-gray-200"
                onClick={() => loadJoinableBarbershops(joinSearch)}
              >
                Buscar
              </Button>
              {joinLoading ? (
                <div className="text-gray-400 text-sm">Buscando...</div>
              ) : (
                <div className="space-y-2">
                  {joinResults.length === 0 ? (
                    <div className="text-gray-500 text-sm">No hay barberías disponibles</div>
                  ) : (
                    joinResults.map((shop) => (
                      <div key={shop.id} className="rounded-lg border border-gray-800 bg-gray-900/60 px-3 py-2 text-sm text-gray-200">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-white">{shop.name}</div>
                            <div className="text-xs text-gray-500">{shop.address}</div>
                          </div>
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-700" onClick={() => sendJoinRequest(shop.id)}>
                            Solicitar
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm Dialog */}
      <Dialog
        open={showConfirmDialog}
        onOpenChange={(open) => {
          if (open) return;
          setShowConfirmDialog(false);
          if (!confirmResolvedRef.current) {
            confirmResolvedRef.current = true;
            confirmDialogData.onCancel?.();
          }
        }}
      >
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{confirmDialogData.title}</DialogTitle>
          </DialogHeader>
          {confirmDialogData.description && (
            <p className="text-gray-400 text-sm">{confirmDialogData.description}</p>
          )}
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              className="flex-1 border-gray-700 text-gray-200"
              onClick={() => {
                confirmResolvedRef.current = true;
                setShowConfirmDialog(false);
                confirmDialogData.onCancel?.();
              }}
            >
              Cancelar
            </Button>
            <Button
              className={`flex-1 ${confirmDialogData.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-700 text-black'}`}
              onClick={() => {
                confirmResolvedRef.current = true;
                setShowConfirmDialog(false);
                confirmDialogData.onConfirm?.();
              }}
            >
              {confirmDialogData.confirmText}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Options Dialog */}
      <Dialog
        open={showCalendarOptions}
        onOpenChange={(open) => {
          if (open) return;
          setShowCalendarOptions(false);
          setCalendarOptions(null);
        }}
      >
        <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Agregar a calendario</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 text-sm">
            Elige tu calendario preferido para agregar la reserva automáticamente.
          </p>
          <div className="flex flex-col gap-2 mt-4">
            <Button
              className="w-full bg-amber-500 hover:bg-amber-700 text-black"
              onClick={() => {
                openCalendarWithFallback(calendarOptions?.googleAppUrl, calendarOptions?.googleUrl);
              }}
            >
              Gmail
            </Button>
            <Button
              variant="outline"
              className="w-full border-blue-400 text-blue-300 hover:bg-blue-500/10"
              onClick={() => {
                openCalendarWithFallback(calendarOptions?.outlookAppUrl, calendarOptions?.outlookUrl);
              }}
            >
              Outlook
            </Button>
            <Button
              variant="outline"
              className="w-full border-gray-600 text-gray-200 hover:bg-gray-700/20"
              onClick={() => {
                openCalendarWithFallback(calendarOptions?.webcalUrl, calendarOptions?.calendarUrl);
              }}
            >
              iOS
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bandeja de entrada - Sheet desde la derecha (estilo FB/IG) */}
      <Sheet open={showInbox} onOpenChange={setShowInbox}>
        <SheetContent side="right" className="bg-gray-900 border-gray-700 w-full sm:max-w-md p-0 flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-white text-xl font-semibold">Bandeja de entrada</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {inboxLoading ? (
              <div className="p-6 text-gray-400">Cargando chats...</div>
            ) : inboxSortedByDate.length === 0 ? (
              <div className="p-6 text-gray-400">No hay conversaciones activas.</div>
            ) : (
              <div className="divide-y divide-gray-800">
                {inboxSortedByDate.map((item) => {
                  const targetKey = `${item.type}:${item.id}`;
                  const unreadCount = item.unreadCount || 0;
                  const lastMessage = item.lastMessage;
                  const lastText =
                    lastMessage?.type === 'audio'
                      ? 'Nota de audio'
                      : lastMessage?.type === 'system'
                      ? 'Mensaje del sistema'
                      : lastMessage?.message || 'Sin mensajes';
                  const peerName =
                    item.barber?.name ||
                    item.client_name ||
                    item.barbershop?.name ||
                    'Conversación';
                  const status = item.status;
                  const isClosed = status === 'completed' || status === 'cancelled' || status === 'rejected';
                  const avatarUrl = user?.user_type === 'client'
                    ? (item.barber?.id ? getUserAvatarUrl({ ...item.barber, user_type: 'barber' }) : getBarbershopAvatarUrl(item.barbershop))
                    : getUserAvatarUrl({ id: item.client_id, name: item.client_name, user_type: 'client' });
                  return (
                    <div
                      key={targetKey}
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-800/60 transition-colors active:bg-gray-800"
                      onClick={() => {
                        if (inboxLongPressTriggeredRef.current) {
                          inboxLongPressTriggeredRef.current = false;
                          return;
                        }
                        setSelectedBookingChat(item);
                        setShowInbox(false);
                      }}
                      onTouchStart={() => {
                        inboxLongPressTriggeredRef.current = false;
                        if (inboxPressTimerRef.current) clearTimeout(inboxPressTimerRef.current);
                        inboxPressTimerRef.current = setTimeout(() => {
                          inboxLongPressTriggeredRef.current = true;
                          setInboxActionTarget(item);
                          setShowInboxActionMenu(true);
                        }, 550);
                      }}
                      onTouchEnd={() => { if (inboxPressTimerRef.current) clearTimeout(inboxPressTimerRef.current); }}
                      onTouchMove={() => { if (inboxPressTimerRef.current) clearTimeout(inboxPressTimerRef.current); }}
                      onContextMenu={(e) => { e.preventDefault(); setInboxActionTarget(item); setShowInboxActionMenu(true); }}
                    >
                      <Avatar className="h-12 w-12 shrink-0 rounded-full border-2 border-gray-700">
                        <AvatarImage src={avatarUrl} alt={peerName} className="object-cover" />
                        <AvatarFallback className="bg-gray-700 text-amber-400">{peerName?.[0] || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white font-semibold truncate">{peerName}</p>
                          {unreadCount > 0 && (
                            <span className="shrink-0 text-xs bg-amber-500 text-black rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1.5">
                              {unreadCount}
                            </span>
                          )}
                          {isClosed && (
                            <span className="shrink-0 text-xs text-gray-400 border border-gray-600 rounded-full px-2 py-0.5">
                              Cerrado
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate">{item.service || item.barbershop?.name}</p>
                        <p className="text-sm text-gray-300 truncate mt-0.5">{lastText}</p>
                      </div>
                      <div className="shrink-0 text-xs text-gray-500">
                        {lastMessage?.timestamp
                          ? formatDateWithTZ(lastMessage.timestamp, { day: '2-digit', month: 'short' })
                          : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Botón flotante para abrir Bandeja de entrada (visible para cliente y barbero) */}
      {user && (user.user_type === 'client' || user.user_type === 'barber') && (
        <button
          type="button"
          onClick={() => { setShowInbox(true); loadInbox(); }}
          className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-amber-500 hover:bg-amber-600 text-black shadow-lg flex items-center justify-center transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-gray-900"
          aria-label="Abrir bandeja de entrada"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      <Dialog open={showInboxActionMenu} onOpenChange={setShowInboxActionMenu}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Acciones</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-gray-200">
            {inboxActionTarget && (
              <>
                <Button
                  variant="outline"
                  className="w-full border-gray-700 text-gray-200"
                  onClick={() => {
                    setSelectedBookingChat(inboxActionTarget);
                    setShowInboxActionMenu(false);
                    setShowInbox(false);
                  }}
                >
                  Abrir conversación
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-gray-700 text-gray-200"
                  onClick={() => {
                    markConversationUnread(inboxActionTarget);
                    setShowInboxActionMenu(false);
                  }}
                >
                  Dejar como no leída
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-red-400 text-red-300 hover:bg-red-500/20"
                  onClick={async () => {
                    const targetKey = `${inboxActionTarget.type}:${inboxActionTarget.id}`;
                    const confirmed = await requestConfirmation({
                      title: 'Eliminar conversación',
                      description: '¿Seguro que deseas eliminar esta conversación? Puedes perder el acceso al historial en la bandeja.',
                      confirmText: 'Eliminar',
                      variant: 'destructive'
                    });
                    if (!confirmed) return;
                    setDeletedInboxKeys((prev) => [...prev, targetKey]);
                    setShowInboxActionMenu(false);
                  }}
                >
                  Eliminar conversación
                </Button>
                <div className="border-t border-gray-800 my-2" />
              </>
            )}
            <Button
              variant="outline"
              className="w-full border-gray-700 text-gray-200"
              onClick={() => {
                markAllConversationsRead();
                setShowInboxActionMenu(false);
              }}
            >
              Marcar todas como leídas
            </Button>
            <Button
              variant="outline"
              className="w-full border-gray-700 text-gray-200"
              onClick={() => {
                markAllConversationsUnread();
                setShowInboxActionMenu(false);
              }}
            >
              Marcar todas como no leídas
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help Center */}
      <Dialog open={showHelpCenter} onOpenChange={setShowHelpCenter}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Centro de ayuda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {helpSections.map((section) => (
              <div key={section.title} className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                <h4 className="text-white font-semibold mb-2">{section.title}</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  {section.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4 text-sm text-gray-300">
              <p className="text-white font-semibold mb-2">Contacto directo</p>
              <p>Escríbenos por el chat interno o WhatsApp si necesitas ayuda inmediata.</p>
              <div className="mt-3">
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    const name = user?.name || 'Usuario';
                    const email = user?.email || 'sin correo';
                    const message = encodeURIComponent(`Hola Ignacio, necesito ayuda con la plataforma. Usuario: ${name} · Email: ${email}`);
                    window.open(`https://wa.me/56976488856?text=${message}`, '_blank');
                  }}
                >
                  Contactar por WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding */}
      <Dialog
        open={showOnboarding}
        onOpenChange={(open) => {
          if (!open) {
            closeOnboarding();
            return;
          }
          setShowOnboarding(true);
        }}
      >
        <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Onboarding guiado</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {onboardingSteps.length === 0 ? (
              <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4 text-sm text-gray-400">
                No hay onboarding disponible para este perfil.
              </div>
            ) : (
              <>
                <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-4">
                  <p className="text-xs uppercase text-gray-500 mb-2">
                    Paso {onboardingStep + 1} de {onboardingSteps.length}
                  </p>
                  <h4 className="text-white text-lg font-semibold">{onboardingSteps[onboardingStep]?.title}</h4>
                  <p className="text-gray-400 text-sm mt-2">{onboardingSteps[onboardingStep]?.description}</p>
                </div>
                <Progress value={Math.round(((onboardingStep + 1) / onboardingSteps.length) * 100)} />
              </>
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="border-gray-700 text-gray-200"
                onClick={closeOnboarding}
              >
                Saltar
              </Button>
              <div className="flex-1" />
              {onboardingSteps.length > 0 && onboardingStep > 0 && (
                <Button
                  variant="outline"
                  className="border-gray-700 text-gray-200"
                  onClick={() => setOnboardingStep((prev) => Math.max(0, prev - 1))}
                >
                  Atrás
                </Button>
              )}
              <Button
                className="bg-amber-500 text-black hover:bg-amber-700"
                onClick={() => {
                  if (onboardingSteps.length === 0) {
                    closeOnboarding();
                    return;
                  }
                  if (onboardingStep + 1 >= onboardingSteps.length) {
                    closeOnboarding();
                    return;
                  }
                  setOnboardingStep((prev) => prev + 1);
                }}
              >
                {onboardingSteps.length === 0
                  ? 'Cerrar'
                  : onboardingStep + 1 >= onboardingSteps.length
                    ? 'Finalizar'
                    : 'Siguiente'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat Modal for Bookings and Quick Cuts */}
      {selectedBookingChat && (
        <RealTimeChat
          isOpen={!!selectedBookingChat}
          onClose={() => setSelectedBookingChat(null)}
          serviceData={{
            id: selectedBookingChat.id,
            type: selectedBookingChat.type || 'booking',
            service: selectedBookingChat.service,
            date: selectedBookingChat.date,
            barbershop: selectedBookingChat.barbershop,
            price: selectedBookingChat.price
          }}
          currentUser={user}
          otherUser={
            selectedBookingChat.type === 'quick_cut' && user?.user_type === 'client'
              ? selectedBookingChat.barber
              : selectedBookingChat.type === 'quick_cut' && user?.user_type === 'barber'
              ? { id: selectedBookingChat.client_id, name: selectedBookingChat.client_name }
              : selectedBookingChat.barber
          }
          BACKEND_URL={BACKEND_URL}
          onOpenClientProfile={loadClientProfileForBarber}
          onMarkUnread={() => {
            markConversationUnread(selectedBookingChat);
            setSelectedBookingChat(null);
          }}
          onDeleteConversation={async () => {
            const confirmed = await requestConfirmation({
              title: 'Eliminar conversación',
              description: '¿Seguro que deseas eliminar esta conversación? Puedes perder el acceso al historial en la bandeja.',
              confirmText: 'Eliminar',
              variant: 'destructive'
            });
            if (!confirmed) return;
            setDeletedInboxKeys((prev) => [...prev, `${selectedBookingChat.type}:${selectedBookingChat.id}`]);
            setSelectedBookingChat(null);
          }}
        />
      )}

      {/* Toast Notifications System */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`
              p-4 rounded-lg shadow-lg border transition-all duration-300 transform animate-in slide-in-from-right-full
              ${toast.variant === 'success' ? 'bg-green-900 border-green-700 text-green-100' :
                toast.variant === 'destructive' ? 'bg-red-900 border-red-700 text-red-100' :
                toast.variant === 'info' ? 'bg-blue-900 border-blue-700 text-blue-100' :
                'bg-gray-900 border-gray-700 text-gray-100'}
            `}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {toast.variant === 'success' && <CheckCircle className="w-5 h-5 text-green-400" />}
                {toast.variant === 'destructive' && <XCircle className="w-5 h-5 text-red-400" />}
                {toast.variant === 'info' && <Info className="w-5 h-5 text-blue-400" />}
                {toast.variant === 'default' && <AlertCircle className="w-5 h-5 text-gray-400" />}
              </div>
              <div className="flex-1">
                {toast.title && <p className="font-medium text-sm">{toast.title}</p>}
                {toast.description && <p className="text-sm opacity-90 mt-1">{toast.description}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
